// FirebaseMarker.jsx - Clean marking interface for Firebase submissions
import React, { useState, useRef, useEffect } from 'react';
import { Maximize2, ArrowLeft, Lightbulb, MessageCircle, Save, X, Check, AlertTriangle, Flag, Clock, RotateCw, ZoomIn, ZoomOut, Table2, Download, FileText } from 'lucide-react';
import { Document, Page, pdfjs } from 'react-pdf';
import { PDF_WORKER_SRC } from './utils/pdfWorker';
import { PDFDocument, rgb, StandardFonts, degrees } from 'pdf-lib';
import { makeFontPdfSafe } from './utils/pdfSafeText';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';
import { completeMarking, flagSubmissionForReview, getSubmission, updateAiSummaryText, getAuthenticatedFileUrl } from './firebase/submissions';
import FlagForReviewModal from './components/FlagForReviewModal';
import SpreadsheetCellViewer from './components/SpreadsheetCellViewer';
import { colLetter } from './components/ExamSpreadsheetGrid';
import { addMarkedSpreadsheetPages } from './utils/markedSpreadsheetPages';
import { buildStudentSpreadsheetMarking } from './utils/studentSpreadsheetMarking';
import { storage } from './firebase/config';
import { getApp } from 'firebase/app';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { ref, getBlob } from 'firebase/storage';
import { getCommentBank, suggestComment, suggestFinalQuestionComment } from './firebase/comments';
import { getMyCommentBank, addMyComment, addMyFinalQuestionComment } from './firebase/markerComments';
import { getQuestionStructures, getAiSummaryEnabled } from './firebase/settings';
import { getHeadMarkerName } from './firebase/headMarkerNotifications';
import { DEFAULT_ORG_ID } from './firebase/orgs';

// Configure PDF.js worker with Safari compatibility
// Safari has issues with .mjs workers, so we use .min.js instead
// Using unpkg CDN to avoid caching issues
pdfjs.GlobalWorkerOptions.workerSrc = PDF_WORKER_SRC; // legacy build on older browsers - see utils/pdfWorker.js

// Percentage is always rounded UP (any fractional mark bumps to the next whole percent).
// Rounds to 4dp first to avoid floating-point noise (e.g. 48.00000001) tipping into the next percent.
function calculatePercentage(total, max) {
  if (!(max > 0)) return 0;
  return Math.ceil(Math.round((total / max) * 100 * 10000) / 10000);
}

// Collapsible header group used in the comment suggestions panel
function HeaderGroup({ header, comments, commentBtn }) {
  const [open, setOpen] = React.useState(false);
  return (
    <div style={{ border: '2px solid #667eea', borderRadius: '8px' }}>
      <div onClick={() => setOpen(!open)}
        style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 12px', background: open ? '#667eea' : '#eef0fc', cursor: 'pointer', userSelect: 'none' }}>
        <span style={{ fontWeight: '700', fontSize: '13px', color: open ? 'white' : '#667eea' }}>{header}</span>
        <span style={{ fontSize: '12px', color: open ? 'rgba(255,255,255,0.8)' : '#7f8c8d' }}>
          {comments.length} {open ? '▲' : '▼'}
        </span>
      </div>
      {open && (
        <div style={{ padding: '8px', display: 'flex', flexDirection: 'column', gap: '6px', background: '#f8f9ff' }}>
          {comments.map(c => commentBtn(c))}
        </div>
      )}
    </div>
  );
}

// Normalises a comment-bank section (plain array or { headers, comments }) into { headers, comments }
function extractSectionData(bank, sectionKey) {
  const raw = bank?.[sectionKey];
  const isNew = raw && !Array.isArray(raw);
  return {
    comments: isNew ? (raw.comments || []) : (raw || []),
    headers: isNew ? (raw.headers || []) : []
  };
}

// Renders a bank section's comments, grouped under collapsible headers when present
function renderBankSection(data, commentBtn, emptyLabel) {
  if (data.comments.length === 0) {
    return <div style={{ fontSize: '12px', color: '#95a5a6', fontStyle: 'italic', padding: '4px 2px' }}>{emptyLabel}</div>;
  }
  if (data.headers.length === 0) {
    return data.comments.map(c => commentBtn(c));
  }
  const grouped = {};
  data.headers.forEach(h => { grouped[h] = []; });
  grouped['__none__'] = [];
  data.comments.forEach(c => {
    const key = (c.header && data.headers.includes(c.header)) ? c.header : '__none__';
    grouped[key].push(c);
  });
  return (
    <>
      {data.headers.map(h => (
        <HeaderGroup key={h} header={h} comments={grouped[h] || []} commentBtn={commentBtn} />
      ))}
      {grouped['__none__'].length > 0 && (
        <HeaderGroup header="Other" comments={grouped['__none__']} commentBtn={commentBtn} />
      )}
    </>
  );
}

function FirebaseMarker({ submission, onBack, isHeadMarkerReview = false, user }) {
  // Detect Safari for conditional workarounds
  const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
  
  // PDF state
  const [pdfBlob, setPdfBlob] = useState(null);
  const [pdfBytes, setPdfBytes] = useState(null);
  const [pdfLoadError, setPdfLoadError] = useState(null);
  const [numPages, setNumPages] = useState(null);
  const [pageNumber, setPageNumber] = useState(1);
  // Manual view-rotation the marker chose per page (degrees, on top of the PDF's own
  // /Rotate flag) - for pages whose content is genuinely sideways with no rotation
  // flag to correct it automatically. Persisted with the rest of the marking data and
  // baked into the exported PDF's real page rotation on export (see createAnnotatedPDF).
  const [pageRotations, setPageRotations] = useState({});
  // The rotation react-pdf reports the current page actually has (its own /Rotate flag),
  // learned from <Page onLoadSuccess> - not persisted, just needed to compute the
  // effective rotate prop without clobbering intrinsic rotation before it's known.
  const [pageIntrinsicRotations, setPageIntrinsicRotations] = useState({});
  // Reading zoom - a transient view preference (like scroll position), not persisted and not
  // per-page. All stored mark/annotation coordinates stay in the fixed 800px-wide reference
  // frame regardless of zoom (see handleCanvasClick etc.) - only rendering and screen-pixel
  // interaction math need to convert to/from the current zoomed size.
  const [zoomLevel, setZoomLevel] = useState(1);
  const ZOOM_MIN = 0.5;
  const ZOOM_MAX = 2.5;
  const ZOOM_STEP = 0.25;
  
  // Marking state
  const [annotations, setAnnotations] = useState([]);
  const [marks, setMarks] = useState([]); // technical marks only (half-technical, full-technical)
  const [professionalMarks, setProfessionalMarks] = useState([]); // { id, x, y, page, section, reason }
  const [additionalProfessionalMarks, setAdditionalProfessionalMarks] = useState([]); // { id, x, y, page, reason }
  const [numberedFeedback, setNumberedFeedback] = useState([]);
  // Plain stamps (OFR/PAG/MAX) - nothing attached beyond the label itself:
  // no score, no reason, no sidebar entry. { id, x, y, page, label }
  const [textStamps, setTextStamps] = useState([]);
  const [spreadsheetViewGroupId, setSpreadsheetViewGroupId] = useState(null);
  // Which of that question's grids: 'spreadsheet' or 'blankSpreadsheet'
  const [spreadsheetViewGridKey, setSpreadsheetViewGridKey] = useState('spreadsheet');
  // Column widths the marker has changed to read a spreadsheet, by
  // `${groupId}:${gridKey}` (the student's own grid is never changed);
  // the marked PDF draws the spreadsheet with them too
  const [spreadsheetColWidths, setSpreadsheetColWidths] = useState({});
  const [nextFeedbackNumber, setNextFeedbackNumber] = useState(1);
  const [finalComment, setFinalComment] = useState('');
  const [questionComments, setQuestionComments] = useState({});
  // Used in FlagForReviewModal's copy ("Explain the issue for <name>")
  // instead of a hardcoded tutor name, since the platform now has more
  // than one org.
  const [headMarkerName, setHeadMarkerName] = useState('the head marker');
  useEffect(() => {
    if (!user) return;
    getHeadMarkerName(user.orgId || DEFAULT_ORG_ID).then(setHeadMarkerName);
  }, [user]);
  const [questionStructure, setQuestionStructure] = useState([]);
  const [showFinalCommentModal, setShowFinalCommentModal] = useState(false);
  const [activeQuestionTab, setActiveQuestionTab] = useState(null);
  const [selectedSection, setSelectedSection] = useState('A');
  const [highlightColor, setHighlightColor] = useState('#FFFF00');
  const [activeTool, setActiveTool] = useState('select');
  const [lockedTool, setLockedTool] = useState(null);
  const [toolMenu, setToolMenu] = useState(null);
  const [resizingAnnotation, setResizingAnnotation] = useState(null);
  const [draggingAnnotation, setDraggingAnnotation] = useState(null);
  const [selectedAnnotation, setSelectedAnnotation] = useState(null); // Track which annotation is selected
  const [showFeedbackPopup, setShowFeedbackPopup] = useState(null);
  const [tempFeedbackText, setTempFeedbackText] = useState('');
  const [activeFeedbackId, setActiveFeedbackId] = useState(null);
  const [savingToFirebase, setSavingToFirebase] = useState(false);
  const [loadingExistingMarking, setLoadingExistingMarking] = useState(false);
  const [showFlagModal, setShowFlagModal] = useState(false);
  const [flagComment, setFlagComment] = useState('');
  const [flaggingSubmission, setFlaggingSubmission] = useState(false);

  // AI-generated overall summary (head marker review only, opt-in per org - see getAiSummaryEnabled)
  const [aiSummaryFeatureEnabled, setAiSummaryFeatureEnabled] = useState(false);
  const [aiSummaryStatus, setAiSummaryStatus] = useState(undefined);
  const [aiSummaryText, setAiSummaryText] = useState('');
  const [aiSummaryError, setAiSummaryError] = useState(null);
  const [aiSummaryPolling, setAiSummaryPolling] = useState(false);
  const [includeSummaryInFinalComment, setIncludeSummaryInFinalComment] = useState(false);
  const [generatingSummaryNow, setGeneratingSummaryNow] = useState(false);
  
  // Professional mark reason picker: { type: 'section'|'additional', canvasX, canvasY, popupX, popupY, section }
  const [pendingProfessionalMark, setPendingProfessionalMark] = useState(null);
  const [nextProfessionalMarkNumber, setNextProfessionalMarkNumber] = useState(1); // Counter for P1, P2, P3...
  const [selectedProfessionalReason, setSelectedProfessionalReason] = useState(null); // { type, section, reason, reasonIndex }
  const [showSectionProfMarks, setShowSectionProfMarks] = useState(true); // Collapsible panel state
  const [showAdditionalProfMarks, setShowAdditionalProfMarks] = useState(true); // Collapsible panel state
  
  // Comment bank state
  const [commentBank, setCommentBank] = useState(null);
  const [myCommentBank, setMyCommentBank] = useState(null);
  const [showFinalCommentSuggestions, setShowFinalCommentSuggestions] = useState(false);
  
  // Custom sections
  const [sectionStructure, setSectionStructure] = useState(null);
  
  // WhatsApp notification state (only for head marker)
  const [sendWhatsAppNotification, setSendWhatsAppNotification] = useState(true);
  const [whatsappCopied, setWhatsappCopied] = useState(false);

  // Display names for homework types (used so head marker notification emails
  // show the configured name rather than the raw type key, e.g. 'RH3')
  const [homeworkNames, setHomeworkNames] = useState({});

  const overlayRef = useRef(null);
  const isLoadingPdf = useRef(false);
  const justHighlightedSelectionRef = useRef(false);

  const buildCombinedFinalComment = (comments, questions) => {
    if (!questions || questions.length === 0) return '';
    return questions
      .filter(q => (comments[q.id] || '').trim())
      .map(q => `${q.label}:\n${(comments[q.id] || '').trim()}`)
      .join('\n\n');
  };

  // Mark values
  const HALF_TECHNICAL = 0.5;
  const FULL_TECHNICAL = 1;
  const PROFESSIONAL = 1;

  const HIGHLIGHT_COLORS = [
    { name: 'Yellow', value: '#FFFF00' },
    { name: 'Green', value: '#90EE90' },
    { name: 'Blue', value: '#87CEEB' },
    { name: 'Pink', value: '#FFB6C1' }
  ];

  // Where each uploaded file starts in the joined-up PDF (page index, by
  // file name) - an uploaded workbook's "See Spreadsheet N" notes are
  // numbered by page within their own file (see prepareSpreadsheetUpload)
  const fileStartPages = useRef({});

  // Load PDF from Firebase (handles single or multiple files)
  useEffect(() => {
    const loadPDFs = async () => {
      if (isLoadingPdf.current) return;
      
      // Base files (the originally-submitted, possibly-already-marked set) - sorted
      // exactly as before, byte-for-byte unchanged, so every already-marked
      // submission renders in precisely the order it always has.
      const baseFilePaths = submission?.originalFilePaths || submission?.originalFileUrls
        || (submission?.originalFileUrl ? [submission.originalFileUrl] : []);
      const baseFileNames = submission?.fileNames || baseFilePaths.map((_, i) => `File ${i + 1}`);

      // Additions released by the head marker after marking had already started
      // (see releasePendingAdditions) - these are NEVER sorted and NEVER
      // interleaved with the base set. They always render after every existing
      // page, so marks already made on the base pages stay valid.
      const additionFilePaths = submission?.releasedAdditionFilePaths || [];
      const additionFileNames = submission?.releasedAdditionFileNames
        || additionFilePaths.map((_, i) => `Additional ${i + 1}`);

      if (baseFilePaths.length === 0 && additionFilePaths.length === 0) {
        console.log('No files to load');
        return;
      }

      const baseIndices = baseFilePaths.map((_, i) => i);
      // Natural sort (handles numbers correctly: file1, file2, file10) - identical
      // to the sort this code has always applied to the base file set.
      baseIndices.sort((a, b) => {
        const nameA = baseFileNames[a] || '';
        const nameB = baseFileNames[b] || '';
        return nameA.localeCompare(nameB, undefined, { numeric: true, sensitivity: 'base' });
      });

      const files = [...baseIndices.map(i => baseFilePaths[i]), ...additionFilePaths];
      const fileNames = [...baseIndices.map(i => baseFileNames[i]), ...additionFileNames];

      isLoadingPdf.current = true;
      setPdfLoadError(null);
      console.log(`Loading ${files.length} PDF file(s) from Firebase`);

      try {
        if (files.length === 1) {
          // SINGLE FILE - Load normally
          let storagePath = files[0];

          // If it's an old URL format, extract the path
          if (storagePath.includes('/o/')) {
            const pathMatch = storagePath.match(/\/o\/(.+?)\?/);
            if (!pathMatch) {
              console.error('Could not extract path from URL');
              setPdfLoadError('This submission\'s file reference is invalid and could not be loaded.');
              isLoadingPdf.current = false;
              return;
            }
            storagePath = decodeURIComponent(pathMatch[1]);
          }
          
          const storageRef = ref(storage, storagePath);
          
          const blob = await getBlob(storageRef);
          console.log('PDF downloaded, size:', blob.size);
          
          const arrayBuffer = await blob.arrayBuffer();
          const bytes = new Uint8Array(arrayBuffer);
          
          fileStartPages.current = { [fileNames[0]]: 0 };
          setPdfBlob(blob);
          setPdfBytes(bytes);
          setPageNumber(1);
          isLoadingPdf.current = false;
          
        } else {
          // MULTIPLE FILES - Merge them, in the order already computed above
          // (sorted base files, then any released additions appended after)
          console.log('Merging multiple PDFs...');
          console.log('File order:', fileNames);

          const mergedPdf = await PDFDocument.create();
          const startPages = {};

          for (let fileIndex = 0; fileIndex < files.length; fileIndex++) {
            let storagePath = files[fileIndex];
            const fileName = fileNames[fileIndex];

            // If it's an old URL format, extract the path
            if (storagePath.includes('/o/')) {
              const pathMatch = storagePath.match(/\/o\/(.+?)\?/);
              if (!pathMatch) {
                console.error('Could not extract path from URL:', storagePath);
                continue;
              }
              storagePath = decodeURIComponent(pathMatch[1]);
            }

            const storageRef = ref(storage, storagePath);

            console.log(`Loading file ${fileIndex + 1}/${files.length}: ${fileName}...`);
            const blob = await getBlob(storageRef);
            const arrayBuffer = await blob.arrayBuffer();

            // Load this PDF and copy its pages to merged PDF
            const sourcePdf = await PDFDocument.load(arrayBuffer);
            if (startPages[fileName] === undefined) startPages[fileName] = mergedPdf.getPageCount();
            const copiedPages = await mergedPdf.copyPages(sourcePdf, sourcePdf.getPageIndices());
            copiedPages.forEach(page => mergedPdf.addPage(page));
          }
          
          fileStartPages.current = startPages;

          // Save merged PDF
          const mergedPdfBytes = await mergedPdf.save();
          const mergedBlob = new Blob([mergedPdfBytes], { type: 'application/pdf' });
          
          console.log('PDFs merged successfully, total size:', mergedBlob.size);
          
          setPdfBlob(mergedBlob);
          setPdfBytes(mergedPdfBytes);
          setPageNumber(1);
          isLoadingPdf.current = false;
        }
        
      } catch (err) {
        console.error('Failed to load/merge PDFs:', err);
        setPdfLoadError('This PDF could not be opened — the file may be corrupted or incomplete. Use Reset Submission to ask the student to re-upload.');
        isLoadingPdf.current = false;
      }
    };
    
    loadPDFs();
  }, [submission?.originalFileUrl, submission?.originalFileUrls, submission?.originalFilePaths, submission?.releasedAdditionFilePaths]);

  // Load comment bank for this homework type
  useEffect(() => {
    const loadComments = async () => {
      if (submission?.homeworkType) {
        try {
          const bank = await getCommentBank(submission.homeworkType);
          setCommentBank(bank);
          console.log('=== Comment Bank Loaded ===');
          console.log('Homework:', submission.homeworkType);
          console.log('Bank structure:', bank);
          console.log('Section A comments:', bank.sectionA?.length || 0);
          console.log('Section B comments:', bank.sectionB?.length || 0);
          console.log('Section C comments:', bank.sectionC?.length || 0);
          console.log('Final comments:', bank.final?.length || 0);
          console.log('=========================');
        } catch (error) {
          console.error('Failed to load comment bank:', error);
        }
      }
    };
    loadComments();
  }, [submission?.homeworkType]);

  // Load this marker's own personal comment bank for this homework type
  useEffect(() => {
    const loadMyComments = async () => {
      if (submission?.homeworkType && user?.uid) {
        try {
          const bank = await getMyCommentBank(user.uid, submission.homeworkType);
          setMyCommentBank(bank);
        } catch (error) {
          console.error('Failed to load my comment bank:', error);
        }
      }
    };
    loadMyComments();
  }, [submission?.homeworkType, user?.uid]);

  useEffect(() => {
    const loadHomeworkNames = async () => {
      try {
        const { getHomeworkNames } = await import('./firebase/settings');
        setHomeworkNames(await getHomeworkNames());
      } catch (error) {
        console.error('Failed to load homework names:', error);
      }
    };
    loadHomeworkNames();
  }, []);

  // Load section structure for this homework type
  useEffect(() => {
    const loadSectionStructure = async () => {
      if (submission?.homeworkType) {
        try {
          const { getSectionStructureForHomework } = await import('./firebase/settings');
          const structure = await getSectionStructureForHomework(submission.homeworkType);
          setSectionStructure(structure);
          // Set selected section to first section ID
          if (structure?.sections?.length > 0) {
            setSelectedSection(structure.sections[0].id);
          }
          const allQStructures = await getQuestionStructures();
          setQuestionStructure(allQStructures[submission.homeworkType] || []);
          console.log('=== Section Structure Loaded ===');
          console.log('Homework:', submission.homeworkType);
          console.log('Structure:', structure);
          console.log('================================');
        } catch (error) {
          console.error('Failed to load section structure:', error);
        }
      }
    };
    loadSectionStructure();
  }, [submission?.homeworkType]);

  // Load existing marking data if in head marker review mode
  useEffect(() => {
    const loadData = async () => {
      if (submission?.id && (isHeadMarkerReview || submission.status === 'marking' || submission.status === 'flagged_for_review')) {
        setLoadingExistingMarking(true);
        try {
          const { getMarkingData } = await import('./firebase/submissions');
          const markingData = await getMarkingData(submission.id);
          
          if (markingData) {
            console.log('Loaded existing marking data:', markingData);
            setMarks(markingData.marks || []);
            setProfessionalMarks(markingData.professionalMarks || []);
            setAdditionalProfessionalMarks(markingData.additionalProfessionalMarks || []);
            setAnnotations(markingData.annotations || []);
            setNumberedFeedback(markingData.numberedFeedback || []);
            setTextStamps(markingData.textStamps || []);
            setPageRotations(markingData.pageRotations || {});
            setFinalComment(markingData.finalComment || '');
            setQuestionComments(markingData.questionComments || {});
            setSpreadsheetColWidths(markingData.spreadsheetColWidths || {});
            
            // Calculate next feedback number
            if (markingData.numberedFeedback && markingData.numberedFeedback.length > 0) {
              const maxNum = Math.max(...markingData.numberedFeedback.map(f => f.number));
              setNextFeedbackNumber(maxNum + 1);
            }
            
            // Calculate next professional mark number
            const allProfMarks = [
              ...(markingData.professionalMarks || []),
              ...(markingData.additionalProfessionalMarks || [])
            ];
            if (allProfMarks.length > 0) {
              const maxProfNum = Math.max(...allProfMarks.map(m => m.markNumber || 0));
              setNextProfessionalMarkNumber(maxProfNum + 1);
            }
          }
        } catch (error) {
          console.error('Failed to load existing marking data:', error);
        } finally {
          setLoadingExistingMarking(false);
        }
      }
    };
    
    loadData();
  }, [isHeadMarkerReview, submission]);

  // AI-generated overall summary: seed from the submission prop, then poll
  // briefly if generation is still in flight (this codebase has no
  // onSnapshot anywhere - a one-shot poll is consistent with that, rather
  // than introducing a live listener just for this one field). After the
  // poll cap, the panel falls back to a manual "Generate Summary" button.
  useEffect(() => {
    if (!isHeadMarkerReview || !submission?.id) return;
    let cancelled = false;
    let pollTimer = null;
    let pollsRemaining = 6;

    const applyStatus = (data) => {
      if (cancelled) return;
      setAiSummaryStatus(data.aiSummaryStatus);
      setAiSummaryText(data.aiSummary || '');
      setAiSummaryError(data.aiSummaryError || null);
    };

    const poll = async () => {
      if (cancelled || pollsRemaining <= 0) {
        setAiSummaryPolling(false);
        return;
      }
      pollsRemaining -= 1;
      try {
        const fresh = await getSubmission(submission.id);
        applyStatus(fresh);
        if (!cancelled && (fresh.aiSummaryStatus === 'generating' || !fresh.aiSummaryStatus)) {
          pollTimer = setTimeout(poll, 4000);
        } else {
          setAiSummaryPolling(false);
        }
      } catch (error) {
        console.error('AI summary poll error:', error);
        setAiSummaryPolling(false);
      }
    };

    (async () => {
      try {
        const enabled = await getAiSummaryEnabled();
        if (cancelled) return;
        setAiSummaryFeatureEnabled(enabled);
        if (!enabled) return;

        applyStatus(submission);
        if (!submission.aiSummaryStatus || submission.aiSummaryStatus === 'generating') {
          setAiSummaryPolling(true);
          pollTimer = setTimeout(poll, 4000);
        }
      } catch (error) {
        console.error('AI summary setup error:', error);
      }
    })();

    return () => {
      cancelled = true;
      if (pollTimer) clearTimeout(pollTimer);
    };
  }, [isHeadMarkerReview, submission?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleGenerateSummaryNow = async () => {
    if (!submission?.id || generatingSummaryNow) return;
    setGeneratingSummaryNow(true);
    setAiSummaryStatus('generating');
    try {
      const functions = getFunctions(getApp());
      const generateAiSummaryNow = httpsCallable(functions, 'generateAiSummaryNow');
      await generateAiSummaryNow({ submissionId: submission.id });
      const fresh = await getSubmission(submission.id);
      setAiSummaryStatus(fresh.aiSummaryStatus);
      setAiSummaryText(fresh.aiSummary || '');
      setAiSummaryError(fresh.aiSummaryError || null);
    } catch (error) {
      console.error('Generate summary now error:', error);
      setAiSummaryStatus('failed');
      setAiSummaryError(error?.message || 'Failed to generate summary');
    } finally {
      setGeneratingSummaryNow(false);
    }
  };

  const handleAiSummaryTextBlur = async () => {
    if (!submission?.id || aiSummaryStatus !== 'ready') return;
    if (aiSummaryText === (submission.aiSummary || '')) return;
    try {
      await updateAiSummaryText(submission.id, aiSummaryText);
    } catch (error) {
      console.error('Save AI summary edit error:', error);
    }
  };

  const handleSendSummarySeparately = () => {
    const phone = submission?.studentPhone;
    if (!phone || !aiSummaryText.trim()) return;
    const message = `Hi ${submission.studentName}, here's a summary of your homework feedback:\n\n${aiSummaryText.trim()}`;
    const cleanPhone = phone.replace(/[^0-9]/g, '');
    window.open(`https://wa.me/${cleanPhone}?text=${encodeURIComponent(message)}`, '_blank');
  };

  const pdfData = React.useMemo(() => {
    if (pdfBytes && pdfBytes.length > 0) {
      return { data: pdfBytes };
    }
    return null;
  }, [pdfBytes]);

  const onDocumentLoadSuccess = ({ numPages }) => {
    setNumPages(numPages);
  };

  const findMarkAtPosition = (x, y, threshold = 20) => {
    return marks.find(mark => {
      const distance = Math.sqrt(Math.pow(mark.x - x, 2) + Math.pow(mark.y - y, 2));
      return distance <= threshold && mark.page === pageNumber;
    });
  };

  // Fires on mouseup, before the browser's click event — this is the reliable point to read a
  // just-completed text selection (a drag that starts and ends over different text spans can
  // fail to fire a click reliably, whereas mouseup on the overlay always fires).
  const handleOverlayMouseUp = () => {
    if (activeTool !== 'highlighter' || !overlayRef.current || showingSpreadsheet) return;

    const selection = window.getSelection();
    const selectedText = selection && selection.toString().trim();
    if (!selectedText || selection.rangeCount === 0 || !overlayRef.current.contains(selection.anchorNode)) return;

    // Marker dragged over real PDF text — highlight exactly what's selected instead of a
    // guessed-size box. getClientRects() often splits a single visual line into several rects
    // (one per underlying PDF text run — different font/kerning segments), so merge any rects
    // that share a line into one continuous box per line instead of several overlapping ones.
    const overlayRect = overlayRef.current.getBoundingClientRect();
    const range = selection.getRangeAt(0);
    const rawRects = Array.from(range.getClientRects()).filter(r => r.width > 1 && r.height > 1);

    const lines = [];
    rawRects.forEach(r => {
      const rTop = r.top;
      const rBottom = r.top + r.height;
      const line = lines.find(l => Math.min(l.bottom, rBottom) - Math.max(l.top, rTop) > Math.min(l.height, r.height) * 0.5);
      if (line) {
        line.left = Math.min(line.left, r.left);
        line.right = Math.max(line.right, r.left + r.width);
        line.top = Math.min(line.top, rTop);
        line.bottom = Math.max(line.bottom, rBottom);
        line.height = line.bottom - line.top;
      } else {
        lines.push({ left: r.left, right: r.left + r.width, top: rTop, bottom: rBottom, height: r.height });
      }
    });

    if (lines.length > 0) {
      // Multi-line selections are still rendered as one box per line (a single rectangle can't
      // accurately cover a paragraph without also highlighting unselected margin text), but they
      // share a groupId so the marker can select/drag/delete the whole paragraph as one thing.
      // Screen pixel rects -> the fixed reference frame stored coordinates live in (see zoomLevel).
      const groupId = lines.length > 1 ? Date.now() : null;
      const newHighlights = lines.map((l, i) => ({
        id: Date.now() + i,
        type: 'highlight',
        x: (l.left - overlayRect.left) / zoomLevel,
        y: (l.top - overlayRect.top) / zoomLevel,
        text: null,
        width: (l.right - l.left) / zoomLevel,
        height: (l.bottom - l.top) / zoomLevel,
        color: highlightColor,
        page: pageNumber,
        groupId
      }));
      setAnnotations(prev => [...prev, ...newHighlights]);
    }
    selection.removeAllRanges();
    justHighlightedSelectionRef.current = true;
  };

  const handleCanvasClick = (e) => {
    if (!overlayRef.current || resizingAnnotation || draggingAnnotation) return;
    // The spreadsheet handles its own clicks; ones that reach here (headers,
    // formula bar, gaps) must not drop marks on the hidden PDF page
    if (showingSpreadsheet) return;

    const rect = overlayRef.current.getBoundingClientRect();
    // Screen pixels at the current zoom -> the fixed reference frame every stored mark and
    // annotation coordinate lives in, regardless of what zoom was active when it was placed.
    const x = (e.clientX - rect.left) / zoomLevel;
    const y = (e.clientY - rect.top) / zoomLevel;

    // Deselect any selected annotation when clicking on canvas
    setSelectedAnnotation(null);

    if (activeTool === 'marker') {
      const existingMark = findMarkAtPosition(x, y);
      
      if (existingMark) {
        const currentType = existingMark.type;
        let newType;
        
        // Technical marks only: half â†’ full â†’ delete
        if (currentType === 'half-technical') newType = 'full-technical';
        else if (currentType === 'full-technical') newType = null;
        
        if (newType === null) {
          setMarks(marks.filter(m => m.id !== existingMark.id));
        } else {
          setMarks(marks.map(m => m.id === existingMark.id ? { ...m, type: newType } : m));
        }
      } else {
        const newMark = {
          id: Date.now(),
          x, y,
          type: 'half-technical',
          section: selectedSection,
          page: pageNumber
        };
        setMarks([...marks, newMark]);
      }
    } else if (activeTool === 'highlighter') {
      if (justHighlightedSelectionRef.current) {
        // The mouseup handler below already turned this drag into highlight box(es) from the
        // text selection — don't also drop a fallback box at the click coordinates.
        justHighlightedSelectionRef.current = false;
      } else {
        // No text was selected (e.g. a plain click, or highlighting a diagram/table with no
        // underlying text) — fall back to a placeable, resizable box like before.
        const newAnnotation = {
          id: Date.now(),
          type: 'highlight',
          x, y,
          text: null,
          width: 80,
          height: 12,
          color: highlightColor,
          page: pageNumber
        };
        setAnnotations([...annotations, newAnnotation]);
      }
      // Don't reset to 'select' - keep highlighter active
      // setActiveTool('select');
    } else if (activeTool === 'comment') {
      const newAnnotation = {
        id: Date.now(),
        type: 'comment',
        x, y,
        text: '',
        width: 180,
        height: 120,
        color: '#fffacd',
        page: pageNumber
      };
      setAnnotations([...annotations, newAnnotation]);
      setActiveTool(lockedTool || 'select');
    } else if (activeTool === 'numbered-feedback') {
      const newFeedback = {
        id: Date.now(),
        number: nextFeedbackNumber,
        section: selectedSection, // Add current section
        x, y,
        page: pageNumber,
        text: ''
      };
      
      setShowFeedbackPopup({
        feedback: newFeedback,
        x: e.clientX,
        y: e.clientY
      });
      setTempFeedbackText('');
      
      setNumberedFeedback([...numberedFeedback, newFeedback]);
      setNextFeedbackNumber(nextFeedbackNumber + 1);
      if (!lockedTool) setActiveTool('select');
    } else if (activeTool === 'professional-mark') {
      // Check if a reason was pre-selected from the panel
      if (selectedProfessionalReason) {
        // Place mark immediately with selected reason
        const newMark = {
          id: Date.now(),
          x,
          y,
          page: pageNumber,
          section: selectedProfessionalReason.section,
          reason: selectedProfessionalReason.reason,
          markNumber: nextProfessionalMarkNumber,
          reasonIndex: selectedProfessionalReason.reasonIndex
        };

        if (selectedProfessionalReason.type === 'additional') {
          setAdditionalProfessionalMarks([...additionalProfessionalMarks, newMark]);
        } else {
          setProfessionalMarks([...professionalMarks, newMark]);
        }
        setNextProfessionalMarkNumber(nextProfessionalMarkNumber + 1);
        setSelectedProfessionalReason(null);
        if (!lockedTool) setActiveTool('select');
      } else {
        setPendingProfessionalMark({
          type: 'section',
          canvasX: x,
          canvasY: y,
          popupX: e.clientX,
          popupY: e.clientY,
          section: selectedSection,
          page: pageNumber
        });
        if (!lockedTool) setActiveTool('select');
      }
    } else if (activeTool === 'additional-professional-mark') {
      // Check if a reason was pre-selected from the panel
      if (selectedProfessionalReason) {
        const newMark = {
          id: Date.now(),
          x,
          y,
          page: pageNumber,
          section: null,
          reason: selectedProfessionalReason.reason,
          markNumber: nextProfessionalMarkNumber,
          reasonIndex: selectedProfessionalReason.reasonIndex
        };

        setAdditionalProfessionalMarks([...additionalProfessionalMarks, newMark]);
        setNextProfessionalMarkNumber(nextProfessionalMarkNumber + 1);
        setSelectedProfessionalReason(null);
        if (!lockedTool) setActiveTool('select');
      } else {
        setPendingProfessionalMark({
          type: 'additional',
          canvasX: x,
          canvasY: y,
          popupX: e.clientX,
          popupY: e.clientY,
          section: null,
          page: pageNumber
        });
        if (!lockedTool) setActiveTool('select');
      }

    } else if (activeTool === 'stamp-ofr' || activeTool === 'stamp-pag' || activeTool === 'stamp-max') {
      // Plain stamps - no score, no reason, nothing attached beyond the
      // label. Placed and deleted exactly like a professional mark, just
      // without ever opening a reason popup.
      const label = activeTool === 'stamp-ofr' ? 'OFR' : activeTool === 'stamp-pag' ? 'PAG' : 'MAX';
      const newStamp = { id: Date.now(), x, y, page: pageNumber, label };
      setTextStamps([...textStamps, newStamp]);
      if (!lockedTool) setActiveTool('select');
    }
  };

  const handleContextMenu = (e) => {
    e.preventDefault();
    if (!overlayRef.current) return;
    
    // Calculate menu dimensions (with color picker when highlighter is active)
    const menuWidth = 240;
    const menuHeight = activeTool === 'highlighter' ? 380 : 360;
    
    // Get window dimensions
    const windowWidth = window.innerWidth;
    const windowHeight = window.innerHeight;
    
    // Calculate position
    let x = e.clientX;
    let y = e.clientY;
    
    // Adjust horizontal position if menu would go off right edge
    if (x + menuWidth > windowWidth) {
      x = Math.max(10, windowWidth - menuWidth - 10);
    }
    
    // Adjust vertical position if menu would go off bottom edge
    if (y + menuHeight > windowHeight) {
      y = Math.max(10, windowHeight - menuHeight - 10);
    }
    
    // Ensure menu doesn't go off top or left edges
    x = Math.max(10, x);
    y = Math.max(10, y);
    
    setToolMenu({ x, y });
  };

  const updateAnnotationText = (id, text) => {
    setAnnotations(annotations.map(ann => ann.id === id ? { ...ann, text } : ann));
  };

  const deleteAnnotation = (id) => {
    const target = annotations.find(ann => ann.id === id);
    if (target?.groupId) {
      // Grouped highlight (a multi-line selection) — delete every line as one unit.
      setAnnotations(annotations.filter(ann => ann.groupId !== target.groupId));
    } else {
      setAnnotations(annotations.filter(ann => ann.id !== id));
    }
  };

  const updateNumberedFeedback = (id, text) => {
    setNumberedFeedback(numberedFeedback.map(f => 
      f.id === id ? { ...f, text } : f
    ));
  };

  const deleteNumberedFeedback = (id) => {
    const renumbered = numberedFeedback
      .filter(f => f.id !== id)
      .sort((a, b) => a.number - b.number)
      .map((f, index) => ({ ...f, number: index + 1 }));
    setNumberedFeedback(renumbered);
    setNextFeedbackNumber(renumbered.length + 1);
  };

  const startResize = (e, annotationId) => {
    e.stopPropagation();
    setResizingAnnotation({ id: annotationId, startX: e.clientX, startY: e.clientY });
  };

  const startDrag = (e, annotationId) => {
    if (e.target.tagName === 'TEXTAREA' || e.target.tagName === 'BUTTON') return;

    const annotation = annotations.find(a => a.id === annotationId);
    if (!annotation) return;

    // Grouped highlight (a multi-line selection) — drag every line together, preserving layout.
    const groupMembers = annotation.groupId
      ? annotations.filter(a => a.groupId === annotation.groupId)
      : [annotation];

    setDraggingAnnotation({
      id: annotationId,
      groupIds: groupMembers.map(a => a.id),
      startX: e.clientX,
      startY: e.clientY,
      initialPositions: Object.fromEntries(groupMembers.map(a => [a.id, { x: a.x, y: a.y }]))
    });
  };

  useEffect(() => {
    if (!resizingAnnotation && !draggingAnnotation) return;

    const handleMouseMove = (e) => {
      if (resizingAnnotation) {
        const annotation = annotations.find(a => a.id === resizingAnnotation.id);
        if (!annotation) return;

        // Mouse moves in real screen pixels; annotation width/height live in the fixed
        // reference frame (see zoomLevel), so the delta needs the same conversion.
        const deltaX = (e.clientX - resizingAnnotation.startX) / zoomLevel;
        const deltaY = (e.clientY - resizingAnnotation.startY) / zoomLevel;

        setAnnotations(annotations.map(ann =>
          ann.id === resizingAnnotation.id
            ? { ...ann, width: Math.max(20, ann.width + deltaX), height: Math.max(10, ann.height + deltaY) }
            : ann
        ));

        setResizingAnnotation({ ...resizingAnnotation, startX: e.clientX, startY: e.clientY });
      }

      if (draggingAnnotation) {
        const deltaX = (e.clientX - draggingAnnotation.startX) / zoomLevel;
        const deltaY = (e.clientY - draggingAnnotation.startY) / zoomLevel;

        setAnnotations(annotations.map(ann => {
          const initial = draggingAnnotation.initialPositions[ann.id];
          return initial
            ? { ...ann, x: initial.x + deltaX, y: initial.y + deltaY }
            : ann;
        }));
      }
    };

    const handleMouseUp = () => {
      setResizingAnnotation(null);
      setDraggingAnnotation(null);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [resizingAnnotation, draggingAnnotation, annotations]);

  const calculateSectionMarks = (section) => {
    const technical = getSectionTechnicalTotal(section);
    const sectionProfessional = professionalMarks.filter(m => m.section === section);

    // For grouped sections, professional counted = share of group cap
    const group = getActiveProfessionalMarkGroup(section);
    let professional = sectionProfessional.length * PROFESSIONAL;
    let professionalCounted = professional;
    if (group) {
      const groupTotal = professionalMarks.filter(m => group.sections.includes(m.section)).length;
      const groupCounted = Math.min(groupTotal, group.totalMarks);
      // Distribute counted marks across sections proportionally (or just use raw for section display)
      const sectionShare = groupTotal > 0 ? (sectionProfessional.length / groupTotal) : 0;
      professionalCounted = Math.round(groupCounted * sectionShare) * PROFESSIONAL;
    } else {
      const maxProf = sectionStructure?.sections?.find(s => s.id === section)?.maxProfessional;
      professionalCounted = maxProf != null
        ? Math.min(sectionProfessional.length, maxProf) * PROFESSIONAL
        : professional; // no explicit cap — count all placed marks
    }

    return { technical, professional, professionalCounted, total: technical + professionalCounted };
  };

  const calculateAdditionalProfessionalMarks = () => {
    return additionalProfessionalMarks.length * PROFESSIONAL;
  };

  // Build the full score object used for saving and WhatsApp template filling
  const buildScoreData = () => {
    const totals = calculateTotalMarks();
    const allSections = sectionStructure?.sections || [{ id: 'A' }, { id: 'B' }, { id: 'C' }];
    const data = {
      total: totals.total,
      technical: totals.technical,
      professional: totals.professional
    };
    allSections.forEach(s => {
      const sm = calculateSectionMarks(s.id);
      data[`section${s.id}`] = sm.total;
      data[`technical${s.id}`] = sm.technical;
      data[`professional${s.id}`] = sm.professionalCounted;
    });
    const profGroups = sectionStructure?.professionalMarkGroups || [];
    if (profGroups.length > 0) {
      data.professionalGroups = profGroups.map(g => {
        const sectionCount = professionalMarks.filter(m => g.sections.includes(m.section)).length;
        const groupCount = sectionCount + (g.includeAdditional ? additionalProfessionalMarks.length : 0);
        return {
          name: g.name || null,
          sections: g.sections,
          includeAdditional: !!g.includeAdditional,
          score: Math.min(groupCount, g.totalMarks),
          max: g.totalMarks
        };
      });
    }
    const techGroups = sectionStructure?.technicalMarkGroups || [];
    if (techGroups.length > 0) {
      data.technicalGroups = techGroups.map(g => ({
        name: g.name || null,
        sections: g.sections,
        score: calculateTechnicalGroupScore(g),
        max: g.totalMarks
      }));
    }
    return data;
  };

  const getSectionMaxTechnical = (sectionId) => {
    if (!sectionStructure?.sections) return Infinity;
    const sec = sectionStructure.sections.find(s => s.id === sectionId);
    return sec?.maxTechnical ?? sec?.maxMark ?? Infinity;
  };

  // Cap each section individually at its own maxTechnical (e.g. part (i) <= 5, part (ii) <= 4).
  // A technical mark group's totalMarks caps the combined score used for the student's total
  // (see calculateTotalMarks / buildScoreData), but each part still shows its own full count up
  // to its own max — group capping doesn't grey out individual marks.
  // A mark that only partly fits under a section's remaining headroom still counts for that
  // remaining amount (e.g. 5.5/6 + a full mark tops out at 6, it isn't discarded) — only a mark
  // placed once the section has already reached its cap is fully "surplus" (zero credit, greyed out).
  const getSectionTechnicalCapping = () => {
    const surplusIds = new Set();
    const sectionTotals = new Map();
    const sectionIds = sectionStructure?.sections?.map(s => s.id) || ['A', 'B', 'C'];

    sectionIds.forEach(sectionId => {
      const sectionMarks = marks
        .filter(m => m.section === sectionId)
        .sort((a, b) => a.id - b.id);
      const maxT = getSectionMaxTechnical(sectionId);
      let running = 0;
      sectionMarks.forEach(mark => {
        const val = mark.type === 'full-technical' ? FULL_TECHNICAL : HALF_TECHNICAL;
        if (running >= maxT) {
          surplusIds.add(mark.id);
        } else {
          running = Math.min(running + val, maxT);
        }
      });
      sectionTotals.set(sectionId, running);
    });

    return { surplusIds, sectionTotals };
  };

  const getSurplusMarkIds = () => getSectionTechnicalCapping().surplusIds;

  const getSectionTechnicalTotal = (sectionId) =>
    getSectionTechnicalCapping().sectionTotals.get(sectionId) ?? 0;

  // Sum of a technical mark group's sections' capped totals, capped again at the group's
  // totalMarks — this is what actually contributes to the student's total.
  const calculateTechnicalGroupScore = (group) => {
    const { sectionTotals } = getSectionTechnicalCapping();
    const raw = group.sections.reduce((sum, sectionId) => sum + (sectionTotals.get(sectionId) ?? 0), 0);
    return Math.min(raw, group.totalMarks);
  };

  const calculateMaxMarks = () => {
    const sections = sectionStructure?.sections || [];
    const groups = sectionStructure?.professionalMarkGroups || [];
    const groupedIds = new Set(groups.flatMap(g => g.sections));
    const technicalGroups = sectionStructure?.technicalMarkGroups || [];
    const technicalGroupedIds = new Set(technicalGroups.flatMap(g => g.sections));
    const additionalCountedInGroup = groups.some(g => g.includeAdditional);
    const maxAdditionalProf = additionalCountedInGroup ? 0 : (sectionStructure?.additionalProfessionalReasons?.length ?? 0);
    return sections.reduce((sum, s) =>
      sum + (technicalGroupedIds.has(s.id) ? 0 : (s.maxTechnical ?? s.maxMark ?? 0)) + (groupedIds.has(s.id) ? 0 : (s.maxProfessional ?? 1)), 0) +
      technicalGroups.reduce((sum, g) => sum + g.totalMarks, 0) +
      groups.reduce((sum, g) => sum + g.totalMarks, 0) +
      maxAdditionalProf;
  };

  const calculateTotalMarks = () => {
    const { sectionTotals } = getSectionTechnicalCapping();
    const techGroups = sectionStructure?.technicalMarkGroups || [];
    const techGroupedSectionIds = new Set(techGroups.flatMap(g => g.sections));
    let technical = 0;
    let professional = 0;

    // Ungrouped technical marks: each known section contributes its own capped total
    sectionTotals.forEach((total, sectionId) => {
      if (!techGroupedSectionIds.has(sectionId)) technical += total;
    });
    // Marks placed on a section outside the configured structure have no cap to apply — count in full
    marks.forEach(mark => {
      if (sectionTotals.has(mark.section)) return;
      if (mark.type === 'half-technical') technical += HALF_TECHNICAL;
      else if (mark.type === 'full-technical') technical += FULL_TECHNICAL;
    });

    // Grouped technical marks: each part counts in full up to its own cap, but the group's
    // combined contribution to the total is capped at group.totalMarks
    techGroups.forEach(group => {
      technical += calculateTechnicalGroupScore(group);
    });

    const groups = sectionStructure?.professionalMarkGroups || [];
    const groupedSectionIds = new Set(groups.flatMap(g => g.sections));
    const additionalCountedInGroup = groups.some(g => g.includeAdditional);

    // Grouped sections: cap each group's contribution at its totalMarks
    groups.forEach(group => {
      const marksInGroup = professionalMarks.filter(m => group.sections.includes(m.section)).length +
        (group.includeAdditional ? additionalProfessionalMarks.length : 0);
      professional += Math.min(marksInGroup, group.totalMarks) * PROFESSIONAL;
    });

    // Non-grouped sections: cap at per-section maxProfessional if set, otherwise count all
    const knownSectionIds = new Set((sectionStructure?.sections || []).map(s => s.id));
    (sectionStructure?.sections || []).forEach(sec => {
      if (!groupedSectionIds.has(sec.id)) {
        const secMarks = professionalMarks.filter(m => m.section === sec.id);
        const cap = sec.maxProfessional;
        professional += cap != null
          ? Math.min(secMarks.length, cap) * PROFESSIONAL
          : secMarks.length * PROFESSIONAL;
      }
    });

    // Count professional marks whose section doesn't appear in the structure at all
    // (handles missing sectionStructure or marks placed before structure was configured)
    const ungroupedUnknown = professionalMarks.filter(
      m => !groupedSectionIds.has(m.section) && !knownSectionIds.has(m.section)
    );
    professional += ungroupedUnknown.length * PROFESSIONAL;

    if (!additionalCountedInGroup) {
      professional += calculateAdditionalProfessionalMarks();
    }

    return { technical, professional, total: technical + professional };
  };

  const getMarkDisplay = (type) => {
    if (type === 'half-technical') return { color: '#445e93', label: '½', size: '20px' };
    if (type === 'full-technical') return { color: '#1b998b', label: '1', size: '20px' };
    if (type === 'professional') return { color: '#f39c12', label: '1P', size: '30px' };
    return { color: '#95a5a6', label: '?', size: '30px' };
  };

  // Get available professional mark reasons for the current section
  const getProfessionalReasons = (sectionId) => {
    if (!sectionStructure?.sections) return [];
    const sec = sectionStructure.sections.find(s => s.id === sectionId);
    return sec?.professionalReasons || [];
  };

  const getAdditionalProfessionalReasons = () => {
    return sectionStructure?.additionalProfessionalReasons || [];
  };

  // Professional mark group helpers
  const getActiveProfessionalMarkGroup = (sectionId) => {
    if (!sectionStructure?.professionalMarkGroups?.length) return null;
    return sectionStructure.professionalMarkGroups.find(g => g.sections.includes(sectionId)) || null;
  };

const getGroupIndex = (sectionId) => {
    if (!sectionStructure?.professionalMarkGroups?.length) return -1;
    return sectionStructure.professionalMarkGroups.findIndex(g => g.sections.includes(sectionId));
  };

  // --- Rotation-aware coordinate helpers for createAnnotatedPDF ---
  // A PDF page can carry a /Rotate flag (0/90/180/270) telling viewers to
  // rotate it for display, without the underlying content actually being
  // stored at those swapped dimensions - the common case being a landscape
  // spreadsheet exported to PDF as a portrait-sized page rotated 90°. The
  // marker's on-screen canvas (react-pdf) renders pages already rotated, so
  // marks/highlights are captured in that VISUAL (rotated) space - but
  // pdf-lib's draw calls operate in the page's own RAW (unrotated) content
  // coordinates. Every mark/highlight/sidebar element below has to convert
  // visual -> raw explicitly, or it lands in the wrong place on any rotated
  // page (this was the root cause of marks/feedback appearing scattered and
  // the sidebar squashed onto the wrong edge on landscape submissions).
  const normalizePdfRotation = (angle) => ((Math.round(angle / 90) * 90) % 360 + 360) % 360;

  const getPageGeometry = (page, pageIndex, rotationOverrides = {}) => {
    const { width: rawWidth, height: rawHeight } = page.getSize();
    // Add the marker's manual view-rotation (if any) to the page's own /Rotate flag -
    // this is the same value that was fed into <Page rotate={...}> while marking, so
    // marks captured in that view land back in the right raw-content spot below.
    const manualOverride = rotationOverrides[pageIndex + 1] || 0;
    const rotation = normalizePdfRotation(page.getRotation().angle + manualOverride);
    const swapped = rotation === 90 || rotation === 270;
    return {
      rawWidth, rawHeight, rotation,
      visualWidth: swapped ? rawHeight : rawWidth,
      visualHeight: swapped ? rawWidth : rawHeight
    };
  };

  // A point captured in visual space (top-left origin, y-down - as seen on
  // the marker's canvas) -> the page's raw content space (bottom-left
  // origin, y-up - what drawCircle/drawText/etc. expect).
  const visualPointToRaw = (visualX, visualY, geo) => {
    switch (geo.rotation) {
      case 90: return { x: visualY, y: visualX };
      case 180: return { x: geo.rawWidth - visualX, y: visualY };
      case 270: return { x: geo.rawWidth - visualY, y: geo.rawHeight - visualX };
      default: return { x: visualX, y: geo.rawHeight - visualY };
    }
  };

  // Same, for an axis-aligned box - transforms both corners and re-derives
  // min/max, since rotation can change which corner ends up top-left.
  // Returns {x,y,width,height} with (x,y) already the bottom-left corner
  // pdf-lib's drawRectangle expects (no further -height adjustment needed).
  const visualRectToRaw = (visualX, visualY, visualW, visualH, geo) => {
    const p1 = visualPointToRaw(visualX, visualY, geo);
    const p2 = visualPointToRaw(visualX + visualW, visualY + visualH, geo);
    return {
      x: Math.min(p1.x, p2.x),
      y: Math.min(p1.y, p2.y),
      width: Math.abs(p2.x - p1.x),
      height: Math.abs(p2.y - p1.y)
    };
  };

  // A mark circle is drawn centred on the click point with a fixed radius.
  // Markers can click right up to a page edge (or the boundary where the
  // sidebar strip begins), which with no adjustment leaves half the circle
  // cut off. Clamping in VISUAL space works for any rotation because
  // visualPointToRaw is a pure rotation/reflection (distance-preserving),
  // so keeping `radius` clear of the visual edges keeps the drawn circle
  // fully on the raw page too.
  const clampToPage = (visualX, visualY, geo, radius) => ({
    x: Math.min(Math.max(visualX, radius), geo.visualWidth - radius),
    y: Math.min(Math.max(visualY, radius), geo.visualHeight - radius)
  });

  // Converts a "sidebar-local" position - pageWidth+xOffset horizontally,
  // sidebarY vertically, exactly how the feedback sidebar always computed
  // its positions - into the target page's real raw coordinates. Growing
  // the page for a sidebar happens on whichever raw axis corresponds to
  // the VISUAL right edge for that page's rotation (see the growsWidth
  // branch in createAnnotatedPDF); this mirrors that same mapping for
  // sidebar CONTENT, not just the strip's background/divider.
  const sidebarPointToRaw = (pseudoX, pseudoY, pageGeo) => {
    const across = pseudoX - pageGeo.visualWidth;
    const along = pageGeo.visualHeight - pseudoY;
    switch (pageGeo.rotation) {
      case 90: return { x: along, y: pageGeo.rawHeight + across };
      case 180: return { x: pageGeo.rawWidth + across, y: along };
      case 270: return { x: pageGeo.rawWidth - along, y: pageGeo.rawHeight + across };
      default: return { x: pageGeo.rawWidth + across, y: pageGeo.rawHeight - along };
    }
  };

  // Drawing a large homework (many pages x many marks/annotations/feedback
  // items) is pure synchronous CPU work via pdf-lib - with no await inside
  // the per-page loop below, it can run long enough that Chrome's renderer
  // watchdog decides the tab is unresponsive, even though nothing crashed.
  // Awaiting this between pages hands control back to the browser (paint,
  // input) so the tab stays responsive without changing any PDF output.
  const yieldToMain = () => new Promise(resolve => setTimeout(resolve, 0));

  // Continuation pages exist purely to hold overflow sidebar feedback text.
  // A source "page" can be much shorter than this - e.g. a tightly cropped
  // photo snippet the student included - which otherwise left almost no
  // room for even a short comment. Giving continuation pages a sane floor
  // height keeps that overflow text readable and normal-looking instead of
  // relying on the forced-overflow guard in renderFeedbackOnSidebar.
  const MIN_CONTINUATION_SIDEBAR_HEIGHT = 700;

  // Create annotated PDF
  const createAnnotatedPDF = async (data, studentName, bytes) => {
    const pdfDoc = await PDFDocument.load(bytes);
    const pages = pdfDoc.getPages();
    const font = makeFontPdfSafe(await pdfDoc.embedFont(StandardFonts.Helvetica));
    const boldFont = makeFontPdfSafe(await pdfDoc.embedFont(StandardFonts.HelveticaBold));

    // Tracks how many continuation pages have been inserted into pdfDoc so
    // far, across the whole document. `pageIndex` only counts position
    // within the static `pages` snapshot (0..pages.length-1) - it never
    // reflects insertions. Every earlier insertion shifts the current
    // page's REAL position in pdfDoc one slot further from `pageIndex`, so
    // "insert immediately after the current page" must account for that
    // drift, or a page's own continuation page ends up landing before an
    // earlier-inserted page's content instead of right after its own.
    let insertedPagesCount = 0;

    for (let pageIndex = 0; pageIndex < pages.length; pageIndex++) {
      const page = pages[pageIndex];
      const geo = getPageGeometry(page, pageIndex, data.pageRotations || {}); // captured BEFORE growing the page below
      // Bake any manual view-rotation into the page's real /Rotate flag, so the marked
      // PDF displays upright for the student too - not just correctly positioned marks
      // for a marker who happened to rotate their own view.
      page.setRotation(degrees(geo.rotation));

      const sidebarWidth = 200;
      // Which raw axis the sidebar strip grows depends on rotation - for a
      // 90/270 page the VISUAL right edge is the RAW top edge, not the raw
      // right edge (see getPageGeometry's comment).
      const growsWidth = geo.rotation === 0 || geo.rotation === 180;
      if (growsWidth) {
        page.setSize(geo.rawWidth + sidebarWidth, geo.rawHeight);
      } else {
        page.setSize(geo.rawWidth, geo.rawHeight + sidebarWidth);
      }

      const sidebarRawRect = growsWidth
        ? { x: geo.rawWidth, y: 0, width: sidebarWidth, height: geo.rawHeight }
        : { x: 0, y: geo.rawHeight, width: geo.rawWidth, height: sidebarWidth };
      const dividerLine = growsWidth
        ? { start: { x: geo.rawWidth, y: 0 }, end: { x: geo.rawWidth, y: geo.rawHeight } }
        : { start: { x: 0, y: geo.rawHeight }, end: { x: geo.rawWidth, y: geo.rawHeight } };

      page.drawRectangle({ ...sidebarRawRect, color: rgb(1, 1, 1) });
      page.drawLine({ ...dividerLine, thickness: 2, color: rgb(0.8, 0.8, 0.8) });

      // Canvas pixels -> VISUAL PDF points. The canvas is always rendered
      // 800px wide preserving the page's own (already-rotated) aspect
      // ratio, so a single uniform scale applies to both axes regardless
      // of orientation.
      const scaleX = geo.visualWidth / 800;
      const scaleY = scaleX;

      const MARK_RADIUS = 10;

      if (data.marks) {
        data.marks.filter(m => m.page === pageIndex + 1).forEach(mark => {
          const clamped = clampToPage(mark.x * scaleX, mark.y * scaleY, geo, MARK_RADIUS);
          const { x, y } = visualPointToRaw(clamped.x, clamped.y, geo);

          let color, text;
          if (mark.type === 'half-technical') {
            color = rgb(0.267, 0.369, 0.576); // #445e93 - matches marker's live marking view
            text = '½';
          } else if (mark.type === 'full-technical') {
            color = rgb(0.106, 0.6, 0.545); // #1b998b - matches marker's live marking view
            text = '1';
          }

          if (color) {
            page.drawCircle({ x, y, size: MARK_RADIUS, color, borderWidth: 2, borderColor: rgb(1, 1, 1) });
            const textWidth = boldFont.widthOfTextAtSize(text, 9);
            page.drawText(text, { x: x - textWidth / 2, y: y - 3.5, size: 9, font: boldFont, color: rgb(1, 1, 1), rotate: degrees(geo.rotation) });
          }
        });
      }

      // Render professional marks (section) on PDF
      if (data.professionalMarks) {
        data.professionalMarks.filter(m => m.page === pageIndex + 1).forEach(mark => {
          const clamped = clampToPage(mark.x * scaleX, mark.y * scaleY, geo, MARK_RADIUS);
          const { x, y } = visualPointToRaw(clamped.x, clamped.y, geo);
          page.drawCircle({ x, y, size: MARK_RADIUS, color: rgb(0.95, 0.61, 0.07), borderWidth: 2, borderColor: rgb(1, 1, 1) });
          const label = `P${mark.markNumber || '?'}`;
          const labelWidth = boldFont.widthOfTextAtSize(label, 8);
          page.drawText(label, { x: x - labelWidth / 2, y: y - 3.5, size: 8, font: boldFont, color: rgb(1, 1, 1), rotate: degrees(geo.rotation) });
        });
      }

      // Render additional professional marks on PDF
      if (data.additionalProfessionalMarks) {
        data.additionalProfessionalMarks.filter(m => m.page === pageIndex + 1).forEach(mark => {
          const clamped = clampToPage(mark.x * scaleX, mark.y * scaleY, geo, MARK_RADIUS);
          const { x, y } = visualPointToRaw(clamped.x, clamped.y, geo);
          page.drawCircle({ x, y, size: MARK_RADIUS, color: rgb(0.8, 0.2, 0.6), borderWidth: 2, borderColor: rgb(1, 1, 1) });
          const label = `P${mark.markNumber || '?'}`;
          const labelWidth = boldFont.widthOfTextAtSize(label, 8);
          page.drawText(label, { x: x - labelWidth / 2, y: y - 3.5, size: 8, font: boldFont, color: rgb(1, 1, 1), rotate: degrees(geo.rotation) });
        });
      }

      // Plain stamps (OFR/PAG/MAX) - a tight box around the label, matching
      // the live marking view. Nothing else attached: no circle, no sidebar
      // entry, no score contribution.
      if (data.textStamps) {
        data.textStamps.filter(s => s.page === pageIndex + 1).forEach(stamp => {
          const { x, y } = visualPointToRaw(stamp.x * scaleX, stamp.y * scaleY, geo);
          const stampFontSize = 12;
          const padX = 4;
          const padY = 3;
          // Box is sized from the font's real metrics so the letters never
          // overflow it, but POSITIONED using the same rough length*4.5
          // estimate the live marking view uses - matching that estimate
          // (rather than the more "correct" measured width) is what keeps
          // this visually lined up with what the marker already saw on
          // screen, since the live view's box also auto-sizes wider than
          // that estimate and ends up shifted the same way.
          const textWidth = boldFont.widthOfTextAtSize(stamp.label, stampFontSize);
          const shiftEstimate = stamp.label.length * 4.5;
          const boxX = x - shiftEstimate - padX;
          const boxY = y - 5 - padY;
          page.drawRectangle({
            x: boxX, y: boxY,
            width: textWidth + padX * 2,
            height: stampFontSize + padY * 2,
            color: rgb(1, 1, 1),
            borderWidth: 1.5, borderColor: rgb(0.102, 0.102, 0.180)
          });
          page.drawText(stamp.label, {
            x: boxX + padX, y: y - 5,
            size: stampFontSize, font: boldFont, color: rgb(0.102, 0.102, 0.180),
            rotate: degrees(geo.rotation)
          });
        });
      }

      if (data.annotations) {
        data.annotations.filter(a => a.page === pageIndex + 1).forEach(ann => {
          const { x, y, width: w, height: h } = visualRectToRaw(
            ann.x * scaleX, ann.y * scaleY, ann.width * scaleX, ann.height * scaleY, geo
          );

          if (ann.type === 'highlight') {
            const hexColor = ann.color;
            const r = parseInt(hexColor.substr(1, 2), 16) / 255;
            const g = parseInt(hexColor.substr(3, 2), 16) / 255;
            const b = parseInt(hexColor.substr(5, 2), 16) / 255;

            page.drawRectangle({
              x, y, width: w, height: h,
              color: rgb(r, g, b), opacity: 0.3
            });
          } else if (ann.type === 'comment' && ann.text) {
            page.drawRectangle({
              x, y, width: w, height: h,
              color: rgb(1, 0.98, 0.8),
              borderWidth: 2, borderColor: rgb(0.95, 0.61, 0.07)
            });

            const lines = ann.text.split('\n');
            lines.forEach((line, i) => {
              if (line.trim()) {
                page.drawText(line, {
                  x: x + 5, y: y + h - 15 - i * 12,
                  size: 9, font, color: rgb(0, 0, 0), maxWidth: w - 10, rotate: degrees(geo.rotation)
                });
              }
            });
          }
        });
      }

      if (data.numberedFeedback) {
        const pageFeedback = data.numberedFeedback.filter(f => f.page === pageIndex + 1);

        // Helper function to render feedback items on a page sidebar.
        // pageWidth/pageHeight here are always VISUAL dimensions (the
        // sidebar's own local pseudo-coordinate space, exactly as before);
        // pageGeo carries the target page's actual rotation so drawAt can
        // convert into that page's real raw coordinates at the point of
        // drawing. For a fresh (unrotated) continuation page, pageGeo is a
        // trivial identity geometry, so this collapses to the original
        // behaviour.
        const renderFeedbackOnSidebar = (targetPage, feedbackItems, startY, pageWidth, pageHeight, pageGeo) => {
          let sidebarY = startY;
          const renderedItems = [];
          const drawAt = (pseudoX, pseudoY) => sidebarPointToRaw(pseudoX, pseudoY, pageGeo);

          // Render each item in chronological order
          for (const item of feedbackItems) {
            if (item.type === 'numbered') {
              // Render numbered feedback
              if (!item.text || !item.text.trim()) continue;

              // Calculate space needed
              const words = item.text.replace(/\n/g, ' ').split(' ');
              let lines = [];
              let currentLine = '';
              words.forEach(word => {
                const testLine = currentLine + (currentLine ? ' ' : '') + word;
                if (testLine.length * 5.5 < sidebarWidth - 50) {
                  currentLine = testLine;
                } else {
                  if (currentLine) lines.push(currentLine);
                  currentLine = word;
                }
              });
              if (currentLine) lines.push(currentLine);

              const spaceNeeded = lines.length * 12 + 35;

              // Only bail to a continuation page if something has already
              // been placed this pass. A page whose sidebar is too short
              // for even one item on its own (e.g. a small cropped-photo
              // "page" from the original scan) would otherwise never make
              // progress: every continuation page inherits that same page's
              // height (see currentHeight below), so it'd hit this same
              // "doesn't fit" check forever and never reach pdfDoc.save().
              // Forcing at least one item through - even if it overflows
              // the sidebar's usual boundary - guarantees the item list
              // shrinks every pass, so this always terminates.
              if (sidebarY - spaceNeeded < 50 && renderedItems.length > 0) {
                break; // Out of space
              }

              // Render the feedback number
              const numberText = item.number.toString();
              const numberWidth = boldFont.widthOfTextAtSize(numberText, 14);
              // Double-digit numbers can be wider than the standard 15pt gap
              // before the text column - push the first text line clear of
              // the number rather than letting them overlap.
              const numberedFirstLineX = Math.max(pageWidth + 30, pageWidth + 15 + numberWidth + 6);
              targetPage.drawText(numberText, {
                ...drawAt(pageWidth + 15, sidebarY),
                size: 14,
                font: boldFont,
                color: rgb(0.91, 0.30, 0.24),
                rotate: degrees(pageGeo.rotation)
              });

              // Render the feedback text - first line clears the number,
              // wrapped lines return to the standard left indent.
              lines.forEach((line, i) => {
                const lineX = i === 0 ? numberedFirstLineX : pageWidth + 30;
                targetPage.drawText(line, {
                  ...drawAt(lineX, sidebarY - (i * 12)),
                  size: 9,
                  font,
                  color: rgb(0, 0, 0),
                  maxWidth: sidebarWidth - (lineX - pageWidth) - 10,
                  rotate: degrees(pageGeo.rotation)
                });
              });

              sidebarY -= (lines.length * 12 + 25);

              // Draw separator line
              if (sidebarY > 50) {
                targetPage.drawLine({
                  start: drawAt(pageWidth + 10, sidebarY + 10),
                  end: drawAt(pageWidth + sidebarWidth - 10, sidebarY + 10),
                  thickness: 1,
                  color: rgb(0.9, 0.9, 0.9)
                });
              }

              renderedItems.push(item);

            } else if (item.type === 'professional') {
              // Render professional mark
              if (!item.reason || !item.reason.trim()) continue;

              // Calculate space needed
              const reasonWords = item.reason.split(' ');
              let rLines = [];
              let rLine = '';
              reasonWords.forEach(word => {
                const test = rLine + (rLine ? ' ' : '') + word;
                if (test.length * 4 < sidebarWidth - 50) { rLine = test; }
                else { if (rLine) rLines.push(rLine); rLine = word; }
              });
              if (rLine) rLines.push(rLine);

              const spaceNeeded = rLines.length * 11 + 30;

              // Same forced-progress guard as the numbered-feedback branch
              // above - see the comment there.
              if (sidebarY - spaceNeeded < 50 && renderedItems.length > 0) {
                break; // Out of space
              }

              // Render the professional mark - label first (e.g. "P12:" can
              // be wider than the standard 15pt gap for double-digit marks),
              // so the first reason line starts clear of it rather than
              // overlapping; wrapped lines return to the standard indent.
              const labelText = `${item.label}:`;
              const labelWidth = boldFont.widthOfTextAtSize(labelText, 10);
              const professionalFirstLineX = Math.max(pageWidth + 30, pageWidth + 15 + labelWidth + 6);
              targetPage.drawText(labelText, { ...drawAt(pageWidth + 15, sidebarY), size: 10, font: boldFont, color: item.color, rotate: degrees(pageGeo.rotation) });
              rLines.forEach((line, i) => {
                const lineX = i === 0 ? professionalFirstLineX : pageWidth + 30;
                targetPage.drawText(line, { ...drawAt(lineX, sidebarY - (i * 11)), size: 8, font, color: rgb(0.3, 0.3, 0.3), maxWidth: sidebarWidth - (lineX - pageWidth) - 10, rotate: degrees(pageGeo.rotation) });
              });
              sidebarY -= (rLines.length * 11 + 20);

              renderedItems.push(item);
            }
          }

          return { renderedItems, finalY: sidebarY };
        };

        // Sort page feedback by original number and assign sequential numbers
        const sortedPageFeedback = [...pageFeedback].sort((a, b) => a.number - b.number);
        const renumberMap = {};
        let seqNum = 1;
        sortedPageFeedback.forEach(f => {
          if (f.text && f.text.trim()) {
            renumberMap[f.id] = seqNum++;
          }
        });

        // Render numbered feedback markers on PDF (the numbers themselves)
        pageFeedback.forEach(feedback => {
          const { x, y } = visualPointToRaw(feedback.x * scaleX, feedback.y * scaleY, geo);
          const displayNumber = renumberMap[feedback.id];
          if (!displayNumber) return;
          page.drawText(displayNumber.toString(), {
            x: x - 6,
            y: y - 8,
            size: 16,
            font: boldFont,
            color: rgb(0.91, 0.30, 0.24),
            rotate: degrees(geo.rotation)
          });
        });

        // Collect all feedback items for this page and merge into chronological order
        const numberedFeedback = sortedPageFeedback.filter(f => f.text && f.text.trim()).map(f => ({
          type: 'numbered',
          sortKey: f.number,
          number: renumberMap[f.id],
          text: f.text
        }));

        const pageProfMarks = (data.professionalMarks || []).filter(m => m.page === pageIndex + 1);
        const pageAddlProfMarks = (data.additionalProfessionalMarks || []).filter(m => m.page === pageIndex + 1);
        const professionalFeedback = [
          ...pageProfMarks.map(m => ({
            type: 'professional',
            sortKey: m.markNumber || 999, // Professional marks use their P number for sorting
            label: `P${m.markNumber || '?'}`,
            reason: m.reason,
            color: rgb(0.95, 0.61, 0.07)
          })),
          ...pageAddlProfMarks.map(m => ({
            type: 'professional',
            sortKey: m.markNumber || 999,
            label: `P${m.markNumber || '?'}`,
            reason: m.reason,
            color: rgb(0.8, 0.2, 0.6)
          }))
        ];

        // Merge and sort all feedback items chronologically
        const allFeedbackItems = [...numberedFeedback, ...professionalFeedback].sort((a, b) => a.sortKey - b.sortKey);

        // Render feedback on this page's sidebar with continuation pages if needed
        let feedbackToRender = [...allFeedbackItems];
        let currentPage = page;
        let currentGeo = geo;
        const currentWidth = geo.visualWidth;
        const currentHeight = geo.visualHeight;
        // The page actually being rendered onto right now - starts as the
        // source page's real (possibly tiny) height, then grows to the
        // continuation floor height from the first continuation page on.
        let sidebarPageHeight = currentHeight;

        while (feedbackToRender.length > 0) {
          const result = renderFeedbackOnSidebar(
            currentPage,
            feedbackToRender,
            sidebarPageHeight - 30,
            currentWidth,
            sidebarPageHeight,
            currentGeo
          );

          // Remove rendered items
          feedbackToRender = feedbackToRender.slice(result.renderedItems.length);

          // If there's overflow, insert a continuation page. `pages` (used by
          // the outer for-loop) is a static snapshot taken before this loop
          // ever runs, holding direct page-object references - an inserted
          // page is never one of its elements, and every remaining `pages[i]`
          // reference stays valid regardless of where pdf-lib now physically
          // slots it in the document. So there is nothing in `pages` to
          // "skip": manually bumping `pageIndex` here used to double up with
          // the outer for-loop's own `pageIndex++`, silently dropping the
          // last source page for every continuation page inserted before it
          // (marks, sidebar, everything) once the loop ran past `pages.length`.
          if (feedbackToRender.length > 0) {
            const continuationPage = pdfDoc.insertPage(pageIndex + 1 + insertedPagesCount);
            insertedPagesCount++;

            sidebarPageHeight = Math.max(currentHeight, MIN_CONTINUATION_SIDEBAR_HEIGHT);

            // Set up the continuation page with sidebar - always unrotated
            // (it's a fresh blank page, not part of the student's original
            // scanned content), sized directly to the desired visual
            // footprint, so it needs no rotation handling of its own.
            continuationPage.setSize(currentWidth + sidebarWidth, sidebarPageHeight);
            currentGeo = { rotation: 0, rawWidth: currentWidth, rawHeight: sidebarPageHeight, visualWidth: currentWidth, visualHeight: sidebarPageHeight };

            // Draw blank white background
            continuationPage.drawRectangle({
              x: 0,
              y: 0,
              width: currentWidth + sidebarWidth,
              height: sidebarPageHeight,
              color: rgb(1, 1, 1)
            });

            // Draw sidebar area
            continuationPage.drawRectangle({
              x: currentWidth,
              y: 0,
              width: sidebarWidth,
              height: sidebarPageHeight,
              color: rgb(1, 1, 1)
            });

            // Draw sidebar divider line
            continuationPage.drawLine({
              start: { x: currentWidth, y: 0 },
              end: { x: currentWidth, y: sidebarPageHeight },
              thickness: 2,
              color: rgb(0.8, 0.8, 0.8)
            });

            // Add "Continued from previous page" text
            continuationPage.drawText('(Feedback continued)', {
              x: 50,
              y: sidebarPageHeight - 50,
              size: 12,
              font: boldFont,
              color: rgb(0.5, 0.5, 0.5)
            });

            currentPage = continuationPage;
            await yieldToMain(); // a page needing multiple continuation pages is itself a lot of drawing
          } else {
            break; // All rendered
          }
        }
      }

      await yieldToMain(); // keep the tab responsive across a large page count
    }

// Anything marked on the interactive spreadsheet gets its own pages here
    // Note pages are counted within their own file - make them pages of
    // the joined-up PDF (or, if that file can't be found, drop them so the
    // spreadsheet goes after the student's pages instead)
    const placedSpreadsheetAnswers = submission?.spreadsheetAnswers && Object.fromEntries(
      Object.entries(submission.spreadsheetAnswers).map(([groupId, entry]) => {
        const start = entry.pdfFileName ? fileStartPages.current[entry.pdfFileName] : 0;
        const shift = (page) => (page && start !== undefined ? page + start : undefined);
        return [groupId, { ...entry, pdfPage: shift(entry.pdfPage), blankPdfPage: shift(entry.blankPdfPage) }];
      })
    );
    await addMarkedSpreadsheetPages({ pdfDoc, data, spreadsheetAnswers: placedSpreadsheetAnswers, mode: submission?.spreadsheetMarkingMode, originalPages: pages, font, boldFont });

    let summaryPage = pdfDoc.addPage();
    const { width, height } = summaryPage.getSize();

    // The marks breakdown + final comment below can run past one page for a
    // long comment or a homework with lots of sections - draw() checks
    // before every row and starts a fresh page rather than letting text
    // silently render below the visible page (pdf-lib doesn't paginate on
    // its own, so without this the extra content was cut off, not just
    // hard to see).
    const SUMMARY_PAGE_BOTTOM_MARGIN = 70;
    function draw(text, opts = {}) {
      if (yPos < SUMMARY_PAGE_BOTTOM_MARGIN) {
        summaryPage = pdfDoc.addPage();
        yPos = height - 50;
      }
      summaryPage.drawText(text, { x: 50, y: yPos, font, ...opts });
    }

    const totals = calculateTotalMarks();
    
    // Use actual section structure or default to A/B/C
    const sections = sectionStructure?.sections || [
      { id: 'A', name: 'Section A', maxTechnical: 5, maxProfessional: 1 },
      { id: 'B', name: 'Section B', maxTechnical: 6, maxProfessional: 1 },
      { id: 'C', name: 'Section C', maxTechnical: 6, maxProfessional: 1 }
    ];

    const pdfGroups = sectionStructure?.professionalMarkGroups || [];
    const pdfGroupedIds = new Set(pdfGroups.flatMap(g => g.sections));
    const pdfAdditionalGroupIdx = pdfGroups.findIndex(g => g.includeAdditional);
    const pdfAdditionalGrouped = pdfAdditionalGroupIdx !== -1;

    const pdfTechnicalGroups = sectionStructure?.technicalMarkGroups || [];
    const pdfTechnicalGroupedIds = new Set(pdfTechnicalGroups.flatMap(g => g.sections));

    // Calculate total max marks — use group totalMarks once per group, not per-section maxTechnical/maxProfessional
    const maxAdditionalProfPDF = pdfAdditionalGrouped ? 0 : (sectionStructure?.additionalProfessionalReasons?.length ?? 0);
    const totalMaxMarks =
      sections.reduce((sum, s) =>
        sum + (pdfTechnicalGroupedIds.has(s.id) ? 0 : (s.maxTechnical ?? s.maxMark ?? 0)) + (pdfGroupedIds.has(s.id) ? 0 : (s.maxProfessional ?? 1)), 0) +
      pdfTechnicalGroups.reduce((sum, g) => sum + g.totalMarks, 0) +
      pdfGroups.reduce((sum, g) => sum + g.totalMarks, 0) +
      maxAdditionalProfPDF;

    summaryPage.drawText('Marking Summary', { x: 50, y: height - 50, size: 24, font: boldFont, color: rgb(0.17, 0.24, 0.31) });
    summaryPage.drawText(`Student: ${studentName}`, { x: 50, y: height - 90, size: 14, font });

    let yPos = height - 130;

    // Technical Marks heading + per-section rows
    draw('Technical Marks', { size: 14, font: boldFont, color: rgb(0.27, 0.37, 0.58) });
    yPos -= 22;
    sections.forEach((section) => {
      const sectionMarks = calculateSectionMarks(section.id);
      const maxT = section.maxTechnical ?? section.maxMark ?? 0;
      draw(`  ${section.name}: ${sectionMarks.technical.toFixed(1)}/${maxT}`, { size: 12 });
      yPos -= 18;
    });
    pdfTechnicalGroups.forEach((group, gIdx) => {
      const counted = calculateTechnicalGroupScore(group);
      const label = group.name || `Group ${gIdx + 1}`;
      draw(`  ${label}: ${counted.toFixed(1)}/${group.totalMarks}`, { size: 12 });
      yPos -= 18;
    });
    yPos -= 10;

    // Professional Marks heading + non-grouped sections, groups, additional
    const nonGroupedWithP = sections.filter(s => !pdfGroupedIds.has(s.id) && (s.maxProfessional ?? 1) > 0);
    const addlProf = calculateAdditionalProfessionalMarks();
    const hasAdditionalReasons = (sectionStructure?.additionalProfessionalReasons?.length ?? 0) > 0;
    if (nonGroupedWithP.length > 0 || pdfGroups.length > 0 || maxAdditionalProfPDF > 0) {
      draw('Professional Marks', { size: 14, font: boldFont, color: rgb(0.95, 0.61, 0.07) });
      yPos -= 22;
      nonGroupedWithP.forEach(section => {
        const sectionMarks = calculateSectionMarks(section.id);
        const maxP = section.maxProfessional ?? 1;
        const placed = Math.round(sectionMarks.professional / PROFESSIONAL);
        const counted = Math.min(placed, maxP);
        const excessNote = placed > maxP ? ` (+${placed - maxP} not counted)` : '';
        draw(`  ${section.name}: ${counted}/${maxP}${excessNote}`, { size: 12 });
        yPos -= 18;
      });
      pdfGroups.forEach((group, gIdx) => {
        const placed = professionalMarks.filter(m => group.sections.includes(m.section)).length +
          (group.includeAdditional ? additionalProfessionalMarks.length : 0);
        const counted = Math.min(placed, group.totalMarks);
        const label = group.name || `Group ${gIdx + 1}`;
        const excessNote = placed > group.totalMarks ? ` (+${placed - group.totalMarks} not counted)` : '';
        draw(`  ${label}${group.includeAdditional ? ' (incl. additional)' : ''}: ${counted}/${group.totalMarks}${excessNote}`, { size: 12 });
        yPos -= 18;
      });
      if (maxAdditionalProfPDF > 0) {
        draw(`  Additional Professional Marks: ${addlProf.toFixed(1)}/${maxAdditionalProfPDF}`, { size: 12 });
        yPos -= 18;
      } else if (pdfAdditionalGrouped && hasAdditionalReasons) {
        const groupLabel = pdfGroups[pdfAdditionalGroupIdx].name || `Group ${pdfAdditionalGroupIdx + 1}`;
        draw(`  Additional Professional Marks: ${addlProf.toFixed(1)} (counted in ${groupLabel})`, { size: 12 });
        yPos -= 18;
      }
      yPos -= 10;
    }

    draw(`Total: ${totals.total.toFixed(1)}/${totalMaxMarks} marks`, { size: 16, font: boldFont, color: rgb(0.91, 0.30, 0.24) });

if (data.finalComment) {
      yPos -= 40; // Add space before final comment
      draw('Final Comment:', { size: 14, font: boldFont });
      yPos -= 25; // Move down for the comment text

      const commentLines = data.finalComment.split('\n');
      commentLines.forEach((line) => {
        // Wrap long lines to fit within the page width
        const words = line.split(' ');
        let wrappedLines = [];
        let currentLine = '';

        words.forEach(word => {
          const testLine = currentLine + (currentLine ? ' ' : '') + word;
          // Approximate character width - adjust multiplier based on font size
          if (testLine.length * 6.5 < width - 100) {
            currentLine = testLine;
          } else {
            if (currentLine) wrappedLines.push(currentLine);
            currentLine = word;
          }
        });
        if (currentLine) wrappedLines.push(currentLine);

        // Render each wrapped line
        wrappedLines.forEach((wrappedLine) => {
          draw(wrappedLine, { size: 11, maxWidth: width - 100 });
          yPos -= 15; // Move down for next line
        });
      });
    }

    return await pdfDoc.save();
  };

  const handleCopyWhatsAppMessage = async () => {
    try {
      const { getHomeworkNames, getMaxMarks, getWhatsAppTemplates, fillWhatsAppTemplate } = await import('./firebase/settings');
      const [homeworkNames, maxMarks, whatsappTemplates] = await Promise.all([
        getHomeworkNames(), getMaxMarks(), getWhatsAppTemplates()
      ]);
      const scoreData = buildScoreData();
      const effectiveFinalComment = questionStructure.length > 0
        ? buildCombinedFinalComment(questionComments, questionStructure)
        : finalComment;
      const finalCommentWithSummary = (includeSummaryInFinalComment && aiSummaryText.trim())
        ? `${effectiveFinalComment}\n\n${aiSummaryText.trim()}`.trim()
        : effectiveFinalComment;
      const homeworkName = homeworkNames[submission.homeworkType] || submission.homeworkType;
      const template = whatsappTemplates[submission.homeworkType];
      const hwSections = sectionStructure?.sections || null;
      const sectionScores = hwSections
        ? Object.fromEntries(hwSections.map(s => [s.id, scoreData[`section${s.id}`] ?? 0]))
        : null;
      const technicalScores = hwSections
        ? Object.fromEntries(hwSections.map(s => [s.id, scoreData[`technical${s.id}`] ?? 0]))
        : null;
      const professionalScores = hwSections
        ? Object.fromEntries(hwSections.map(s => [s.id, scoreData[`professional${s.id}`] ?? 0]))
        : null;
      // Also extract per-section scores directly so fillWhatsAppTemplate can find them even if hwSections is null
      const directSectionScores = Object.fromEntries(
        Object.entries(scoreData).filter(([k]) => /^(technical|professional)[A-Z]/.test(k) && typeof scoreData[k] === 'number')
      );
      const maxMark = maxMarks[submission.homeworkType] || 20;
      const percentage = calculatePercentage(scoreData.total, maxMark);
      let message;
      if (template) {
        message = fillWhatsAppTemplate(template, {
          studentName: submission.studentName,
          sectionA: scoreData.sectionA,
          sectionB: scoreData.sectionB,
          sectionC: scoreData.sectionC,
          sections: hwSections,
          sectionScores,
          technicalScores,
          professionalScores,
          professionalGroups: scoreData.professionalGroups || null,
          technicalGroups: scoreData.technicalGroups || null,
          total: scoreData.total,
          maxMark,
          percentage,
          technical: scoreData.technical,
          professional: scoreData.professional,
          finalComment: finalCommentWithSummary || '',
          summary: aiSummaryText || '',
          homeworkType: submission.homeworkType,
          homeworkName,
          ...directSectionScores
        });
      } else {
        const sectionLines = hwSections
          ? hwSections.map((s, i) => {
              const legacyKey = i === 0 ? 'sectionA' : i === 1 ? 'sectionB' : 'sectionC';
              return `${s.name}: ${(scoreData[legacyKey] ?? 0).toFixed(1)}`;
            }).join('\n')
          : `Section A: ${scoreData.sectionA?.toFixed(1) || '0.0'}\nSection B: ${scoreData.sectionB?.toFixed(1) || '0.0'}\nSection C: ${scoreData.sectionC?.toFixed(1) || '0.0'}`;
        message = `Hi ${submission.studentName}, your homework has been marked.\n\n${sectionLines}\n\nTotal: ${scoreData.total?.toFixed(1) || '0.0'}/${maxMark} (${percentage}%)\n\n${finalCommentWithSummary || ''}`;
      }
      await navigator.clipboard.writeText(message);
      setWhatsappCopied(true);
      setTimeout(() => setWhatsappCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy WhatsApp message:', err);
    }
  };

  // Interactive spreadsheet marking - the spreadsheet is marked in the page
  // area with the same tools as a PDF page (SpreadsheetCellViewer.jsx).
  // Whatever's placed on a cell goes into the same arrays as the PDF's, with
  // `cell` instead of a page position (page: null), so section totals, caps
  // and comment/P numbering all include it. The marked PDF draws them on
  // separate pages (markedSpreadsheetPages.js).
  const handleSpreadsheetColWidthsChange = React.useCallback((key, widths) => {
    setSpreadsheetColWidths(prev => ({ ...prev, [key]: widths }));
  }, []);

  const showingSpreadsheet = !!(spreadsheetViewGroupId && submission?.spreadsheetAnswers?.[spreadsheetViewGroupId]);

  const sameCell = (a, b) => !!a && !!b && a.groupId === b.groupId && a.gridKey === b.gridKey && a.row === b.row && a.col === b.col;

  const cellPlaceLabel = (cell) => {
    const name = submission?.spreadsheetAnswers?.[cell.groupId]?.name || 'Spreadsheet';
    return `${name} ${colLetter(cell.col)}${cell.row + 1}`;
  };

  // A click on a cell, for whichever tool is active - mirrors handleCanvasClick
  const handleCellClick = (cell, e) => {
    setSelectedAnnotation(null);

    if (activeTool === 'marker') {
      // Same as on the PDF: half -> full -> removed
      const existing = [...marks].reverse().find(m => sameCell(m.cell, cell));
      if (!existing) {
        setMarks([...marks, { id: Date.now(), x: 0, y: 0, type: 'half-technical', section: selectedSection, page: null, cell }]);
      } else if (existing.type === 'half-technical') {
        setMarks(marks.map(m => m.id === existing.id ? { ...m, type: 'full-technical' } : m));
      } else {
        setMarks(marks.filter(m => m.id !== existing.id));
      }
    } else if (activeTool === 'numbered-feedback') {
      const newFeedback = { id: Date.now(), number: nextFeedbackNumber, section: selectedSection, x: 0, y: 0, page: null, cell, text: '' };
      setShowFeedbackPopup({ feedback: newFeedback, x: e.clientX, y: e.clientY });
      setTempFeedbackText('');
      setNumberedFeedback([...numberedFeedback, newFeedback]);
      setNextFeedbackNumber(nextFeedbackNumber + 1);
      if (!lockedTool) setActiveTool('select');
    } else if (activeTool === 'professional-mark' || activeTool === 'additional-professional-mark') {
      const additionalTool = activeTool === 'additional-professional-mark';
      if (selectedProfessionalReason) {
        const additional = additionalTool || selectedProfessionalReason.type === 'additional';
        const newMark = {
          id: Date.now(), x: 0, y: 0, page: null, cell,
          section: additionalTool ? null : selectedProfessionalReason.section,
          reason: selectedProfessionalReason.reason,
          markNumber: nextProfessionalMarkNumber,
          reasonIndex: selectedProfessionalReason.reasonIndex
        };
        if (additional) setAdditionalProfessionalMarks([...additionalProfessionalMarks, newMark]);
        else setProfessionalMarks([...professionalMarks, newMark]);
        setNextProfessionalMarkNumber(nextProfessionalMarkNumber + 1);
        setSelectedProfessionalReason(null);
      } else {
        setPendingProfessionalMark({
          type: additionalTool ? 'additional' : 'section',
          canvasX: 0, canvasY: 0,
          popupX: e.clientX, popupY: e.clientY,
          section: additionalTool ? null : selectedSection,
          page: null,
          cell
        });
      }
      if (!lockedTool) setActiveTool('select');
    } else if (activeTool === 'stamp-ofr' || activeTool === 'stamp-pag' || activeTool === 'stamp-max') {
      // Clicking the same stamp on the same cell again takes it off
      const label = activeTool === 'stamp-ofr' ? 'OFR' : activeTool === 'stamp-pag' ? 'PAG' : 'MAX';
      const existing = textStamps.find(s => s.label === label && sameCell(s.cell, cell));
      if (existing) setTextStamps(textStamps.filter(s => s.id !== existing.id));
      else setTextStamps([...textStamps, { id: Date.now(), x: 0, y: 0, page: null, label, cell }]);
      if (!lockedTool) setActiveTool('select');
    }
  };

  // Highlighter click or drag over cells: highlights them in the current
  // colour, or takes it off if they all already have that colour
  const handleCellHighlightRange = (cells) => {
    const has = (cell) => annotations.some(a => a.type === 'highlight' && a.color === highlightColor && sameCell(a.cell, cell));
    const removeOnly = cells.every(has);
    const now = Date.now();
    setAnnotations([
      ...annotations.filter(a => !(a.type === 'highlight' && a.cell && cells.some(c => sameCell(a.cell, c)))),
      ...(removeOnly ? [] : cells.map((cell, i) => ({ id: now + i, type: 'highlight', x: 0, y: 0, width: 0, height: 0, text: null, color: highlightColor, page: null, cell })))
    ]);
  };

  const deleteCellProfessionalMark = (mark, additional) => {
    const remainingSection = additional ? professionalMarks : professionalMarks.filter(m => m.id !== mark.id);
    const remainingAdditional = additional ? additionalProfessionalMarks.filter(m => m.id !== mark.id) : additionalProfessionalMarks;
    if (additional) setAdditionalProfessionalMarks(remainingAdditional);
    else setProfessionalMarks(remainingSection);
    const allRemaining = [...remainingSection, ...remainingAdditional];
    setNextProfessionalMarkNumber(allRemaining.length > 0 ? Math.max(...allRemaining.map(m => m.markNumber || 0)) + 1 : 1);
  };

  // Uploaded-spreadsheet submissions (see parseUploadedSpreadsheet.js /
  // generateSpreadsheetUploadPdf.js) keep the student's original .xlsx in
  // Storage alongside the generated PDF, for a marker who wants to open it
  // in real Excel.
  const downloadOriginalSpreadsheet = async () => {
    try {
      const url = await getAuthenticatedFileUrl(submission.spreadsheetSourceFilePath);
      if (url) window.open(url, '_blank');
    } catch (error) {
      console.error('Download original spreadsheet error:', error);
    }
  };

  // Save progress without completing
  const saveProgress = async () => {
    try {
      setSavingToFirebase(true);
      
      const scoreData = buildScoreData();
      
      const effectiveFinalComment = questionStructure.length > 0
        ? buildCombinedFinalComment(questionComments, questionStructure)
        : finalComment;
      const markingData = {
        marks: [...marks],
        professionalMarks: [...professionalMarks],
        additionalProfessionalMarks: [...additionalProfessionalMarks],
        annotations: [...annotations],
        numberedFeedback: [...numberedFeedback],
        textStamps: [...textStamps],
        pageRotations: { ...pageRotations },
        finalComment: effectiveFinalComment,
        questionComments,
        score: scoreData,
        spreadsheetColWidths: { ...spreadsheetColWidths }
      };

      // Save marking data without changing submission status
      const { saveMarkingData } = await import('./firebase/submissions');
      await saveMarkingData(submission.id, markingData);

      // Persist any edits to the AI summary too, so nothing typed is lost
      if (isHeadMarkerReview && aiSummaryStatus === 'ready') {
        try { await updateAiSummaryText(submission.id, aiSummaryText); } catch (e) { console.error('Save AI summary edit error:', e); }
      }

      alert('Progress saved! You can come back to finish marking this submission later.');
      
    } catch (error) {
      console.error('Failed to save progress:', error);
      alert('Failed to save progress. Please try again.');
    } finally {
      setSavingToFirebase(false);
    }
  };

  const handleFlagForReview = async () => {
    if (!flagComment.trim()) return;
    setFlaggingSubmission(true);
    try {
      // Save whatever marking has been done so far before handing off to Aileen
      const scoreData = buildScoreData();
      const effectiveFinalComment = questionStructure.length > 0
        ? buildCombinedFinalComment(questionComments, questionStructure)
        : finalComment;
      const { saveMarkingData } = await import('./firebase/submissions');
      await saveMarkingData(submission.id, {
        marks: [...marks],
        professionalMarks: [...professionalMarks],
        additionalProfessionalMarks: [...additionalProfessionalMarks],
        annotations: [...annotations],
        numberedFeedback: [...numberedFeedback],
        textStamps: [...textStamps],
        pageRotations: { ...pageRotations },
        finalComment: effectiveFinalComment,
        questionComments,
        score: scoreData,
        spreadsheetColWidths: { ...spreadsheetColWidths }
      });

      // Persist any edits to the AI summary too, so nothing typed is lost
      if (isHeadMarkerReview && aiSummaryStatus === 'ready') {
        try { await updateAiSummaryText(submission.id, aiSummaryText); } catch (e) { console.error('Save AI summary edit error:', e); }
      }

      await flagSubmissionForReview(submission.id, user.uid, user.name, flagComment);

      // Notify head marker (non-blocking)
      try {
        const { sendHeadMarkerNotification, NOTIFICATION_TYPES } = await import('./firebase/headMarkerNotifications');
        sendHeadMarkerNotification(NOTIFICATION_TYPES.SUBMISSION_FLAGGED_FOR_REVIEW, {
          studentName: submission?.studentName,
          homeworkType: homeworkNames[submission?.homeworkType] || submission?.homeworkType,
          markerName: user.name,
          flagComment
        });
      } catch (e) { console.error('Notification error:', e); }

      setShowFlagModal(false);
      setFlagComment('');
      if (onBack) onBack();
    } catch (error) {
      console.error('Failed to flag submission:', error);
      alert('Failed to flag submission. Please try again.');
    } finally {
      setFlaggingSubmission(false);
    }
  };

  // Complete marking
  const completeFirebaseMarking = async () => {
    const confirmMessage = isHeadMarkerReview 
      ? `Approve and send to ${submission.studentName}?\n\nThis will create the final marked PDF and send it to the student.`
      : `Complete marking for ${submission.studentName}?\n\nThis will save your marking.`;
      
    if (!window.confirm(confirmMessage)) {
      return;
    }
    
    setSavingToFirebase(true);
    
    try {
      const scoreData = buildScoreData();

      const effectiveFinalComment = questionStructure.length > 0
        ? buildCombinedFinalComment(questionComments, questionStructure)
        : finalComment;
      // Only merged for the head marker's actual approve-and-send action -
      // saveProgress/handleFlagForReview leave finalComment as the marker's
      // own unmerged text, since those are intermediate handoffs, not the
      // final deliverable.
      const finalCommentWithSummary = (isHeadMarkerReview && includeSummaryInFinalComment && aiSummaryText.trim())
        ? `${effectiveFinalComment}\n\n${aiSummaryText.trim()}`.trim()
        : effectiveFinalComment;
      const markingData = {
        marks: [...marks],
        professionalMarks: [...professionalMarks],
        additionalProfessionalMarks: [...additionalProfessionalMarks],
        annotations: [...annotations],
        numberedFeedback: [...numberedFeedback],
        textStamps: [...textStamps],
        pageRotations: { ...pageRotations },
        finalComment: finalCommentWithSummary,
        questionComments,
        score: scoreData,
        spreadsheetColWidths: { ...spreadsheetColWidths }
      };

      if (isHeadMarkerReview) {
        // Head marker is approving - create PDF and send to student

        // Persist any edits to the AI summary itself before it's merged/sent
        if (aiSummaryStatus === 'ready' && aiSummaryText !== (submission.aiSummary || '')) {
          try { await updateAiSummaryText(submission.id, aiSummaryText); } catch (e) { console.error('Save AI summary edit error:', e); }
        }

        // Save final marking data (with any edits)
        const { saveMarkingData } = await import('./firebase/submissions');
        await saveMarkingData(submission.id, markingData);
        
        // Create the marked PDF
        const arrayBuffer = await pdfBlob.arrayBuffer();
        const freshBytes = new Uint8Array(arrayBuffer);
        
        const markedPdfBytes = await createAnnotatedPDF(markingData, submission.studentName, freshBytes);
        const markedPdfFile = new File([markedPdfBytes], `${submission.homeworkType}_marked.pdf`, { type: 'application/pdf' });
        
        // Upload marked PDF
        const { uploadMarkedPDF } = await import('./firebase/submissions');
        await uploadMarkedPDF(submission.id, markedPdfFile, submission?.spreadsheetAnswers ? buildStudentSpreadsheetMarking(markingData) : null);
        
        // Approve submission and update score (sets status to 'marked')
        const { approveSubmission } = await import('./firebase/submissions');
        await approveSubmission(submission.id, scoreData);
        
        // Send WhatsApp notification if checkbox is checked and student has phone
        if (sendWhatsAppNotification && submission.studentPhone) {
          try {
            
            // Get homework settings
            const { getHomeworkNames, getMaxMarks, getWhatsAppTemplates, fillWhatsAppTemplate } = await import('./firebase/settings');
            const homeworkNames = await getHomeworkNames();
            const maxMarks = await getMaxMarks();
            const whatsappTemplates = await getWhatsAppTemplates();
            
            const homeworkName = homeworkNames[submission.homeworkType] || submission.homeworkType;
            const template = whatsappTemplates[submission.homeworkType];
            
            const hwSections = sectionStructure?.sections || null;
            const sectionScores = hwSections
              ? Object.fromEntries(hwSections.map(s => [s.id, scoreData[`section${s.id}`] ?? 0]))
              : null;
            const technicalScores = hwSections
              ? Object.fromEntries(hwSections.map(s => [s.id, scoreData[`technical${s.id}`] ?? 0]))
              : null;
            const professionalScores = hwSections
              ? Object.fromEntries(hwSections.map(s => [s.id, scoreData[`professional${s.id}`] ?? 0]))
              : null;
            const directSectionScores = Object.fromEntries(
              Object.entries(scoreData).filter(([k]) => /^(technical|professional)[A-Z]/.test(k) && typeof scoreData[k] === 'number')
            );

            const maxMark = maxMarks[submission.homeworkType] || 20;
            const percentage = calculatePercentage(scoreData.total, maxMark);
            let message;
            if (template) {
              message = fillWhatsAppTemplate(template, {
                studentName: submission.studentName,
                sectionA: scoreData.sectionA,
                sectionB: scoreData.sectionB,
                sectionC: scoreData.sectionC,
                sections: hwSections,
                sectionScores,
                technicalScores,
                professionalScores,
                professionalGroups: scoreData.professionalGroups || null,
                technicalGroups: scoreData.technicalGroups || null,
                total: scoreData.total,
                maxMark,
                percentage,
                technical: scoreData.technical,
                professional: scoreData.professional,
                finalComment: finalCommentWithSummary || '',
                summary: aiSummaryText || '',
                homeworkType: submission.homeworkType,
                homeworkName: homeworkName,
                ...directSectionScores
              });
            } else {
              // Fallback using actual section names
              const sectionLines = hwSections
                ? hwSections.map((s, i) => {
                    const legacyKey = i === 0 ? 'sectionA' : i === 1 ? 'sectionB' : 'sectionC';
                    return `${s.name}: ${(scoreData[legacyKey] ?? 0).toFixed(1)}`;
                  }).join('\n')
                : `Section A: ${scoreData.sectionA?.toFixed(1) || '0.0'}\nSection B: ${scoreData.sectionB?.toFixed(1) || '0.0'}\nSection C: ${scoreData.sectionC?.toFixed(1) || '0.0'}`;
              message = `Hi ${submission.studentName}, your homework has been marked.\n\n${sectionLines}\n\nTotal: ${scoreData.total?.toFixed(1) || '0.0'}/${maxMark} (${percentage}%)\n\n${finalCommentWithSummary || ''}`;
            }
            
            const phone = submission.studentPhone.replace(/[^0-9]/g, '');
            window.open(`https://wa.me/${phone}?text=${encodeURIComponent(message)}`, '_blank');
          } catch (whatsappError) {
            console.error('Failed to send WhatsApp notification:', whatsappError);
            // Don't fail the whole operation if WhatsApp fails
          }
        }
        
        alert('Approved! The student can now view their marked homework.');
        
      } else {
        // Regular marker completing marking
        await completeMarking(submission.id, markingData);

        // Head marker "ready for review" notification for this now fires
        // server-side, from onSubmissionAwaitingReview in functions/index.js
        // - not from here. That also fixes it only firing when review is
        // actually required (this client call used to fire unconditionally,
        // even for homeworks that skip review and go straight to 'marked').

        // Check if this homework requires review
        const { requiresReview } = await import('./firebase/settings');
        const needsReview = await requiresReview(submission.homeworkType);
        
        if (needsReview) {
          // Goes to head marker for review
          alert('SUCCESS: Marking saved!\n\nThis submission will be reviewed by the head marker before being sent to the student.');
        } else {
          // No review needed - create and upload PDF now
          const arrayBuffer = await pdfBlob.arrayBuffer();
          const freshBytes = new Uint8Array(arrayBuffer);
          
          const markedPdfBytes = await createAnnotatedPDF(markingData, submission.studentName, freshBytes);
          const markedPdfFile = new File([markedPdfBytes], `${submission.homeworkType}_marked.pdf`, { type: 'application/pdf' });
          
          const { uploadMarkedPDF } = await import('./firebase/submissions');
          await uploadMarkedPDF(submission.id, markedPdfFile, submission?.spreadsheetAnswers ? buildStudentSpreadsheetMarking(markingData) : null);
          
          alert('SUCCESS: Marking completed!\n\nThe student can now view their score and download the marked PDF.');
        }
      }
      
      if (onBack) onBack();
      
    } catch (error) {
      console.error('Failed to complete marking:', error);
      alert('Failed to save marking. Please try again.');
    } finally {
      setSavingToFirebase(false);
    }
  };

  const totals = calculateTotalMarks();

  return (
    <div style={{
      fontFamily: "'Crimson Pro', 'Georgia', serif",
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #f5f1e8 0%, #e8e0d5 100%)',
      padding: '20px'
    }}>
      {/* Header */}
      <div style={{
        background: '#2c3e50',
        color: '#ecf0f1',
        padding: '24px 32px',
        borderRadius: '12px',
        marginBottom: '24px',
        boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        <div>
          <h1 style={{ margin: 0, fontSize: '32px', fontWeight: '700' }}>
            Homework Marker
          </h1>
          <p style={{ margin: '8px 0 0 0', opacity: 0.9, fontSize: '16px' }}>
            Marking: {submission?.studentName} | Type: {submission?.homeworkType || 'N/A'}
          </p>
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            {submission?.examExtraTimeApplied && (
              <div style={{
                display: 'inline-flex', alignItems: 'center', gap: '6px',
                marginTop: '10px', padding: '4px 10px', borderRadius: '6px',
                background: 'rgba(129,140,248,0.25)', color: '#c7d2fe',
                fontSize: '13px', fontWeight: '600'
              }}>
                <Clock size={13} />
                Extra time applied: {submission.examExtraTimeApplied.type === 'percentage'
                  ? `+${submission.examExtraTimeApplied.value}%`
                  : `+${submission.examExtraTimeApplied.value} min`}
                {' '}({submission.examExtraTimeApplied.baseDurationMinutes} min base)
              </div>
            )}
            {submission?.recoveredFromAbandonedSession && (
              <div style={{
                display: 'inline-flex', alignItems: 'center', gap: '6px',
                marginTop: '10px', padding: '4px 10px', borderRadius: '6px',
                background: 'rgba(231,76,60,0.25)', color: '#fca5a5',
                fontSize: '13px', fontWeight: '600'
              }}>
                <AlertTriangle size={13} />
                Auto-recovered: student did not manually submit this exam
              </div>
            )}
          </div>
        </div>

        {onBack && (
          <button
            onClick={onBack}
            style={{
              padding: '12px 24px',
              background: '#95a5a6',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              fontWeight: '600',
              fontSize: '14px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}
          >
            <ArrowLeft size={18} /> Back to Inbox
          </button>
        )}
      </div>

      {/* Interactive Spreadsheet Marking - only present when the head marker
          has turned it on for this homework type AND this submission actually
          captured a spreadsheet answer (see ExamWindowApp.jsx's handleSubmit
          and submissions.js's createSubmission). Fully additive: renders
          nothing at all otherwise, so every other submission's screen is
          unaffected. */}
      {submission?.hasInteractiveSpreadsheet && submission?.spreadsheetAnswers && (
        <div style={{
          background: 'white',
          borderRadius: '12px',
          padding: '16px 24px',
          marginBottom: '24px',
          boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          flexWrap: 'wrap'
        }}>
          <span style={{ fontSize: '13px', fontWeight: '600', color: '#2c3e50' }}>Mark:</span>
          <button
            onClick={() => setSpreadsheetViewGroupId(null)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              padding: '8px 14px',
              background: !spreadsheetViewGroupId ? '#c33329' : 'white',
              color: !spreadsheetViewGroupId ? 'white' : '#c33329',
              border: '1px solid #c33329',
              borderRadius: '6px',
              fontSize: '13px',
              fontWeight: '600',
              cursor: 'pointer'
            }}
          >
            <FileText size={14} />
            PDF pages
          </button>
          {/* One button per spreadsheet, in Spreadsheet 1, 2... order */}
          {Object.entries(submission.spreadsheetAnswers)
            .flatMap(([groupId, groupData]) => [
              groupData.gridJson && { groupId, gridKey: 'spreadsheet', number: groupData.number, kind: groupData.blankGridJson ? 'prepopulated' : null, name: groupData.name || groupId },
              groupData.blankGridJson && { groupId, gridKey: 'blankSpreadsheet', number: groupData.blankNumber, kind: 'blank', name: groupData.name || groupId }
            ].filter(Boolean))
            .sort((a, b) => (a.number || Infinity) - (b.number || Infinity))
            .map(sheet => {
              const active = spreadsheetViewGroupId === sheet.groupId && spreadsheetViewGridKey === sheet.gridKey;
              const detail = [sheet.name, sheet.kind].filter(Boolean).join(', ');
              return (
                <button
                  key={`${sheet.groupId}:${sheet.gridKey}`}
                  onClick={() => { setSpreadsheetViewGroupId(sheet.groupId); setSpreadsheetViewGridKey(sheet.gridKey); setSelectedAnnotation(null); if (activeTool === 'comment') { setActiveTool('select'); setLockedTool(null); } }}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    padding: '8px 14px',
                    background: active ? '#c33329' : 'white',
                    color: active ? 'white' : '#c33329',
                    border: '1px solid #c33329',
                    borderRadius: '6px',
                    fontSize: '13px',
                    fontWeight: '600',
                    cursor: 'pointer'
                  }}
                >
                  <Table2 size={14} />
                  {sheet.number ? `Spreadsheet ${sheet.number} (${detail})` : detail}
                </button>
              );
            })}
          {submission?.spreadsheetSourceFilePath && (
            <button
              onClick={downloadOriginalSpreadsheet}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                padding: '8px 14px',
                background: 'white',
                color: '#c33329',
                border: '1px solid #c33329',
                borderRadius: '6px',
                fontSize: '13px',
                fontWeight: '600',
                cursor: 'pointer'
              }}
            >
              <Download size={14} />
              Download Original Spreadsheet
            </button>
          )}
        </div>
      )}

      <div style={{ display: 'flex', gap: '16px' }}>
        {/* Section Selector Strip - sticky left column */}
        <div style={{
          width: '96px',
          flexShrink: 0,
          position: 'sticky',
          top: '20px',
          alignSelf: 'flex-start'
        }}>
          <div style={{
            background: 'white',
            borderRadius: '12px',
            padding: '12px 8px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
            display: 'flex',
            flexDirection: 'column',
            gap: '8px',
            alignItems: 'stretch'
          }}>
            <span style={{ fontWeight: '700', color: '#2c3e50', fontSize: '12px', textAlign: 'center', letterSpacing: '0.5px', textTransform: 'uppercase' }}>Section</span>
            {(sectionStructure?.sections || [{ id: 'A', name: 'Section A' }, { id: 'B', name: 'Section B' }, { id: 'C', name: 'Section C' }]).map(section => (
              <button
                key={section.id}
                onClick={() => setSelectedSection(section.id)}
                style={{
                  padding: '10px 4px',
                  background: selectedSection === section.id ? '#445e93' : '#ecf0f1',
                  color: selectedSection === section.id ? 'white' : '#2c3e50',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontWeight: '600',
                  fontSize: '13px',
                  textAlign: 'center'
                }}
                title={section.name}
              >
                {section.name}
              </button>
            ))}
            {numPages && (
              <div style={{ marginTop: '4px', color: '#7f8c8d', fontSize: '11px', textAlign: 'center' }}>
                {pageNumber}/{numPages}
              </div>
            )}
          </div>
        </div>

        {/* Main PDF Area */}
        <div style={{ flex: 1 }}>

          {/* Marking Tools Toolbar */}
          <div style={{
            position: 'sticky',
            top: 0,
            zIndex: 100,
            background: 'white',
            borderRadius: '12px',
            padding: '10px 16px',
            marginBottom: '16px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            display: 'flex',
            gap: '6px',
            alignItems: 'center',
            flexWrap: 'wrap'
          }}>
            
            {[
              { id: 'select', icon: '↖', name: 'Select' },
              { id: 'marker', icon: 'T', name: 'Technical Mark' },
              { id: 'highlighter', icon: '🖍', name: 'Highlighter' },
              { id: 'numbered-feedback', icon: '#', name: 'Numbered Feedback' },
              { id: 'comment', icon: '💬', name: 'Comment' },
              { id: 'professional-mark', icon: 'P', name: 'Professional Mark' },
              { id: 'additional-professional-mark', icon: 'AP', name: 'Additional Prof Mark' },
              { id: 'stamp-ofr', icon: 'OFR', name: 'OFR Stamp' },
              { id: 'stamp-pag', icon: 'PAG', name: 'PAG Stamp' },
              { id: 'stamp-max', icon: 'MAX', name: 'MAX Stamp' }
            ].filter(tool => !(showingSpreadsheet && tool.id === 'comment')).map((tool) => {
              const isActive = activeTool === tool.id;
              const isLocked = lockedTool === tool.id;
              return (
                <div key={tool.id} style={{ position: 'relative' }}>
                  <button
                    onClick={() => {
                      if (isLocked) {
                        setLockedTool(null);
                      } else {
                        setActiveTool(tool.id);
                        setLockedTool(null);
                      }
                    }}
                    onDoubleClick={(e) => {
                      e.preventDefault();
                      setActiveTool(tool.id);
                      setLockedTool(tool.id);
                    }}
                    style={{
                      padding: '0',
                      background: isActive ? '#667eea' : '#f8f9fa',
                      color: isActive ? 'white' : '#2c3e50',
                      border: isLocked ? '2px solid #e67e22' : `2px solid ${isActive ? '#667eea' : '#e2e8f0'}`,
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontSize: tool.icon.length > 2 ? '11px' : '18px',
                      fontWeight: 'bold',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: '44px',
                      height: '44px',
                      transition: 'all 0.15s'
                    }}
                    onMouseEnter={e => {
                      if (!isActive) {
                        e.currentTarget.style.background = '#e8eaf6';
                        e.currentTarget.style.borderColor = isLocked ? '#e67e22' : '#667eea';
                      }
                    }}
                    onMouseLeave={e => {
                      if (!isActive) {
                        e.currentTarget.style.background = '#f8f9fa';
                        e.currentTarget.style.borderColor = isLocked ? '#e67e22' : '#e2e8f0';
                      }
                    }}
                    title={isLocked ? `${tool.name} (locked — click to unlock)` : `${tool.name} (double-click to lock)`}
                  >
                    {tool.icon}
                  </button>
                  {isLocked && (
                    <div style={{
                      position: 'absolute', top: '-4px', right: '-4px',
                      width: '10px', height: '10px',
                      background: '#e67e22',
                      borderRadius: '50%',
                      border: '1px solid white'
                    }} />
                  )}
                </div>
              );
            })}

            {/* Rotate view - a per-page reading aid, not a placement tool: fixes pages
                whose content displays sideways with no /Rotate flag to auto-correct it.
                Persisted, and baked into the exported PDF's own rotation on completion. */}
            <div style={{ width: '1px', height: '28px', background: '#e2e8f0', margin: '0 4px' }} />
            <button
              onClick={() => setPageRotations(prev => ({ ...prev, [pageNumber]: ((prev[pageNumber] || 0) + 90) % 360 }))}
              style={{
                padding: '0',
                background: (pageRotations[pageNumber] || 0) !== 0 ? '#667eea' : '#f8f9fa',
                color: (pageRotations[pageNumber] || 0) !== 0 ? 'white' : '#2c3e50',
                border: `2px solid ${(pageRotations[pageNumber] || 0) !== 0 ? '#667eea' : '#e2e8f0'}`,
                borderRadius: '6px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '44px',
                height: '44px'
              }}
              title={
                (pageRotations[pageNumber] || 0) !== 0
                  ? `Rotate view (currently +${pageRotations[pageNumber]}°, this page only)`
                  : 'Rotate view — for pages that display sideways (this page only)'
              }
            >
              <RotateCw size={18} />
            </button>
            {(pageRotations[pageNumber] || 0) !== 0 && (
              <button
                onClick={() => setPageRotations(prev => { const next = { ...prev }; delete next[pageNumber]; return next; })}
                style={{
                  fontSize: '11px', color: '#667eea', background: 'none', border: 'none',
                  cursor: 'pointer', textDecoration: 'underline', padding: '0 4px', whiteSpace: 'nowrap'
                }}
                title="Reset this page's view rotation"
              >
                reset
              </button>
            )}

            {/* Zoom - a reading aid like rotate, not persisted (view-only, applies to all
                pages). Marks/annotations stay stored in a fixed reference frame regardless of
                zoom - see zoomLevel's declaration for how that's kept consistent. */}
            <div style={{ width: '1px', height: '28px', background: '#e2e8f0', margin: '0 4px' }} />
            <button
              onClick={() => setZoomLevel(z => Math.max(ZOOM_MIN, +(z - ZOOM_STEP).toFixed(2)))}
              disabled={zoomLevel <= ZOOM_MIN}
              style={{
                padding: '0',
                background: '#f8f9fa',
                color: zoomLevel <= ZOOM_MIN ? '#bfc9d4' : '#2c3e50',
                border: '2px solid #e2e8f0',
                borderRadius: '6px',
                cursor: zoomLevel <= ZOOM_MIN ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '36px',
                height: '44px'
              }}
              title="Zoom out"
            >
              <ZoomOut size={16} />
            </button>
            <button
              onClick={() => setZoomLevel(1)}
              style={{
                fontSize: '12px', fontWeight: '600', color: zoomLevel !== 1 ? '#667eea' : '#7f8c8d',
                background: 'none', border: 'none', cursor: 'pointer', padding: '0 2px',
                minWidth: '38px', whiteSpace: 'nowrap'
              }}
              title="Reset zoom to 100%"
            >
              {Math.round(zoomLevel * 100)}%
            </button>
            <button
              onClick={() => setZoomLevel(z => Math.min(ZOOM_MAX, +(z + ZOOM_STEP).toFixed(2)))}
              disabled={zoomLevel >= ZOOM_MAX}
              style={{
                padding: '0',
                background: '#f8f9fa',
                color: zoomLevel >= ZOOM_MAX ? '#bfc9d4' : '#2c3e50',
                border: '2px solid #e2e8f0',
                borderRadius: '6px',
                cursor: zoomLevel >= ZOOM_MAX ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '36px',
                height: '44px'
              }}
              title="Zoom in"
            >
              <ZoomIn size={16} />
            </button>

            <span style={{ fontSize: '11px', color: '#95a5a6', marginLeft: '4px', whiteSpace: 'nowrap' }}>
              dbl-click to lock
            </span>

            {/* Highlight Color Picker - only show when highlighter is active */}
            {activeTool === 'highlighter' && (
              <div style={{
                marginLeft: '8px',
                padding: '4px 8px',
                background: '#f0f2ff',
                borderRadius: '6px',
                display: 'flex',
                alignItems: 'center',
                gap: '6px'
              }}>
                {HIGHLIGHT_COLORS.map(color => (
                  <button
                    key={color.value}
                    onClick={(e) => {
                      e.stopPropagation();
                      setHighlightColor(color.value);
                    }}
                    style={{
                      width: '24px',
                      height: '24px',
                      background: color.value,
                      border: highlightColor === color.value ? '3px solid #2c3e50' : '2px solid #bdc3c7',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      boxShadow: highlightColor === color.value ? '0 2px 4px rgba(0,0,0,0.3)' : 'none'
                    }}
                    title={color.name}
                  />
                ))}
                <span style={{ fontSize: '11px', color: '#7f8c8d', marginLeft: '4px', whiteSpace: 'nowrap' }}>
                  drag over text to highlight it, or click for a box
                </span>
              </div>
            )}
          </div>

          {/* PDF Viewer */}
          {pdfData ? (
            <div style={{
              background: 'white',
              borderRadius: '12px',
              padding: '24px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
              position: 'relative'
            }}>
              <div
                ref={overlayRef}
                onClick={handleCanvasClick}
                onMouseUp={handleOverlayMouseUp}
                onContextMenu={handleContextMenu}
                style={{
                  position: 'relative',
                  cursor: activeTool === 'select' ? 'default' : activeTool === 'marker' ? 'crosshair' : 'cell',
                  display: 'inline-block'
                }}
              >
                {showingSpreadsheet ? (
                  <SpreadsheetCellViewer
                    groupId={spreadsheetViewGroupId}
                    gridKey={spreadsheetViewGridKey}
                    answer={submission.spreadsheetAnswers[spreadsheetViewGroupId]}
                    zoomLevel={zoomLevel}
                    items={{ marks, numberedFeedback, professionalMarks, additionalProfessionalMarks, annotations, textStamps }}
                    activeTool={activeTool}
                    surplusIds={getSurplusMarkIds()}
                    onCellClick={handleCellClick}
                    onHighlightRange={handleCellHighlightRange}
                    onDeleteProfessionalMark={deleteCellProfessionalMark}
                    colWidthOverrides={spreadsheetColWidths}
                    onColWidthsChange={handleSpreadsheetColWidthsChange}
                  />
                ) : null}
                {/* Hidden, not removed, while the spreadsheet shows: the PDF
                    reader uses up the file when it loads, so a second load
                    would fail */}
                <div style={{ display: showingSpreadsheet ? 'none' : 'contents' }}>
                <Document
                  file={pdfData}
                  onLoadSuccess={onDocumentLoadSuccess}
                  onLoadError={(error) => {
                    console.error('PDF Load Error Details:', error);
                    console.error('Error message:', error.message);
                    console.error('Error stack:', error.stack);
                    setPdfLoadError('This PDF could not be opened — the file may be corrupted or incomplete. Use Reset Submission to ask the student to re-upload.');
                  }}
                  loading={<div style={{ padding: '40px', textAlign: 'center' }}>Loading PDF...</div>}
                >
                  <Page
                    pageNumber={pageNumber}
                    width={800 * zoomLevel}
                    rotate={
                      pageIntrinsicRotations[pageNumber] !== undefined
                        ? (pageIntrinsicRotations[pageNumber] + (pageRotations[pageNumber] || 0)) % 360
                        : undefined
                    }
                    onLoadSuccess={(page) => {
                      // Only needed to combine with a manual override below - if it's
                      // already known for this page, no need to re-set it on every load.
                      setPageIntrinsicRotations(prev =>
                        prev[pageNumber] !== undefined ? prev : { ...prev, [pageNumber]: page.rotate }
                      );
                    }}
                    renderTextLayer={!isSafari}
                    renderAnnotationLayer={true}
                  />
                </Document>

                {/* Marks */}
                {marks.filter(m => m.page === pageNumber).map(mark => {
                  const surplusIds = getSurplusMarkIds();
                  const display = getMarkDisplay(mark.type);
                  const markSize = parseInt(display.size);
                  const halfSize = markSize / 2;
                  const isSurplus = surplusIds.has(mark.id);
                  return (
                    <div
                      key={mark.id}
                      title={isSurplus ? 'Not counted (over max)' : undefined}
                      style={{
                        position: 'absolute',
                        left: mark.x * zoomLevel - halfSize,
                        top: mark.y * zoomLevel - halfSize,
                        width: `${markSize}px`,
                        height: `${markSize}px`,
                        background: isSurplus ? '#95a5a6' : display.color,
                        color: 'white',
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontWeight: 'bold',
                        fontSize: mark.type === 'half-technical' || mark.type === 'full-technical' ? '12px' : '14px',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                        pointerEvents: 'none',
                        border: isSurplus ? '2px dashed #7f8c8d' : '2px solid white',
                        opacity: isSurplus ? 0.6 : 1
                      }}
                    >
                      {display.label}
                    </div>
                  );
                })}

                {/* Annotations */}
                {annotations.filter(a => a.page === pageNumber).map(ann => (
                  <div
                    key={ann.id}
                    onClick={(e) => {
                      e.stopPropagation();
                      // Select this annotation when clicked (for both highlights and comments)
                      setSelectedAnnotation(ann.id);
                    }}
                    onMouseDown={(e) => startDrag(e, ann.id)}
                    style={{
                      position: 'absolute',
                      left: ann.x * zoomLevel,
                      top: ann.y * zoomLevel,
                      width: ann.width * zoomLevel,
                      height: ann.height * zoomLevel,
                      background: ann.type === 'highlight' ? `${ann.color}4D` : ann.color,
                      border: ann.type === 'comment' ? '2px solid #f39c12' : 'none',
                      borderRadius: ann.type === 'comment' ? '8px' : '4px',
                      padding: ann.type === 'comment' ? '12px' : '4px',
                      boxShadow: ann.type === 'comment' ? '0 4px 8px rgba(0,0,0,0.15)' : 'none',
                      pointerEvents: 'auto',
                      zIndex: ann.type === 'comment' ? 100 : 50,
                      cursor: draggingAnnotation?.groupIds?.includes(ann.id) ? 'grabbing' : 'grab'
                    }}
                  >
                    {ann.type === 'comment' && (
                      <div style={{ position: 'relative', height: '100%' }}>
                        <textarea
                          value={ann.text}
                          onChange={(e) => updateAnnotationText(ann.id, e.target.value)}
                          onClick={(e) => e.stopPropagation()}
                          onMouseDown={(e) => e.stopPropagation()}
                          placeholder="Enter feedback..."
                          style={{
                            width: '100%',
                            height: 'calc(100% - 24px)',
                            border: 'none',
                            background: 'transparent',
                            resize: 'none',
                            fontFamily: 'inherit',
                            fontSize: '13px',
                            outline: 'none',
                            pointerEvents: 'auto',
                            cursor: 'text'
                          }}
                        />
                        {/* Only show controls when this annotation is selected */}
                        {selectedAnnotation === ann.id && (
                          <>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                deleteAnnotation(ann.id);
                              }}
                              onMouseDown={(e) => e.stopPropagation()}
                              style={{
                                position: 'absolute',
                                top: '-10px',
                                right: '-10px',
                                background: '#e74c3c',
                                color: 'white',
                                border: 'none',
                                borderRadius: '50%',
                                width: '24px',
                                height: '24px',
                                cursor: 'pointer',
                                fontSize: '14px',
                                fontWeight: 'bold',
                                zIndex: 10,
                                pointerEvents: 'auto'
                              }}
                            >
                              <X size={14} />
                            </button>
                            
                            <div
                              onMouseDown={(e) => {
                                e.stopPropagation();
                                startResize(e, ann.id);
                              }}
                              style={{
                                position: 'absolute',
                                bottom: '-6px',
                                right: '-6px',
                                width: '20px',
                                height: '20px',
                                cursor: 'nwse-resize',
                                background: '#f39c12',
                                borderRadius: '0 0 6px 0',
                                border: '2px solid white',
                                boxShadow: '0 2px 4px rgba(0,0,0,0.3)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                pointerEvents: 'auto',
                                zIndex: 20
                              }}
                            >
                              <Maximize2 size={14} color="white" />
                            </div>
                          </>
                        )}
                      </div>
                    )}
                    
                    {ann.type === 'highlight' && (
                      <>
                        {/* Only show controls when this annotation is selected */}
                        {selectedAnnotation === ann.id && (
                          <>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                deleteAnnotation(ann.id);
                              }}
                              onMouseDown={(e) => e.stopPropagation()}
                              style={{
                                position: 'absolute',
                                top: '-10px',
                                right: '-10px',
                                background: '#e74c3c',
                                color: 'white',
                                border: 'none',
                                borderRadius: '50%',
                                width: '24px',
                                height: '24px',
                                cursor: 'pointer',
                                fontSize: '14px',
                                fontWeight: 'bold',
                                zIndex: 10,
                                boxShadow: '0 2px 4px rgba(0,0,0,0.3)',
                                pointerEvents: 'auto'
                              }}
                            >
                              <X size={14} />
                            </button>

                            {/* Resizing a single line of a grouped (multi-line) highlight doesn't
                                make sense — only offer it for a standalone highlight box. */}
                            {!ann.groupId && (
                              <div
                                onMouseDown={(e) => {
                                  e.stopPropagation();
                                  startResize(e, ann.id);
                                }}
                                style={{
                                  position: 'absolute',
                                  bottom: '-6px',
                                  right: '-6px',
                                  width: '20px',
                                  height: '20px',
                                  cursor: 'nwse-resize',
                                  background: ann.color,
                                  border: '3px solid #2c3e50',
                                  borderRadius: '50%',
                                  boxShadow: '0 2px 6px rgba(0,0,0,0.4)',
                                  pointerEvents: 'auto',
                                  zIndex: 20
                                }}
                              />
                            )}
                          </>
                        )}
                      </>
                    )}
                  </div>
                ))}

                {/* Numbered Feedback Markers */}
                {numberedFeedback.filter(f => f.page === pageNumber).map(feedback => (
                  <div
                    key={feedback.id}
                    style={{
                      position: 'absolute',
                      left: feedback.x * zoomLevel - 12,
                      top: feedback.y * zoomLevel - 12,
                      width: '24px',
                      height: '24px',
                      color: '#e74c3c',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontWeight: 'bold',
                      fontSize: '18px',
                      textShadow: '0 0 3px white, 0 0 3px white, 0 0 3px white',
                      pointerEvents: 'none',
                      zIndex: 200
                    }}
                  >
                    {feedback.number}
                  </div>
                ))}

                {/* Professional Mark Indicators on PDF */}
                {professionalMarks.filter(m => m.page === pageNumber).map(mark => (
                  <div
                    key={mark.id}
                    title={mark.reason || 'Professional mark'}
                    onClick={(e) => { e.stopPropagation(); setAdditionalProfessionalMarks(prev => prev); /* click to delete handled below */ }}
                    onDoubleClick={(e) => { 
                      e.stopPropagation(); 
                      setProfessionalMarks(professionalMarks.filter(m2 => m2.id !== mark.id));
                      // Recalculate next number based on remaining marks
                      const remainingMarks = professionalMarks.filter(m2 => m2.id !== mark.id);
                      const allRemaining = [...remainingMarks, ...additionalProfessionalMarks];
                      if (allRemaining.length > 0) {
                        const maxNum = Math.max(...allRemaining.map(m => m.markNumber || 0));
                        setNextProfessionalMarkNumber(maxNum + 1);
                      } else {
                        setNextProfessionalMarkNumber(1);
                      }
                    }}
                    style={{
                      position: 'absolute',
                      left: mark.x * zoomLevel - 10,
                      top: mark.y * zoomLevel - 10,
                      width: '20px',
                      height: '20px',
                      background: '#f39c12',
                      color: 'white',
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontWeight: 'bold',
                      fontSize: '10px',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                      border: '2px solid white',
                      cursor: 'pointer',
                      zIndex: 150,
                      userSelect: 'none'
                    }}
                  >
                    P{mark.markNumber || '?'}
                  </div>
                ))}

                {/* Additional Professional Mark Indicators on PDF */}
                {additionalProfessionalMarks.filter(m => m.page === pageNumber).map(mark => (
                  <div
                    key={mark.id}
                    title={mark.reason || 'Additional professional mark'}
                    onDoubleClick={(e) => { 
                      e.stopPropagation(); 
                      setAdditionalProfessionalMarks(additionalProfessionalMarks.filter(m2 => m2.id !== mark.id));
                      // Recalculate next number based on remaining marks
                      const remainingMarks = additionalProfessionalMarks.filter(m2 => m2.id !== mark.id);
                      const allRemaining = [...professionalMarks, ...remainingMarks];
                      if (allRemaining.length > 0) {
                        const maxNum = Math.max(...allRemaining.map(m => m.markNumber || 0));
                        setNextProfessionalMarkNumber(maxNum + 1);
                      } else {
                        setNextProfessionalMarkNumber(1);
                      }
                    }}
                    style={{
                      position: 'absolute',
                      left: mark.x * zoomLevel - 10,
                      top: mark.y * zoomLevel - 10,
                      width: '20px',
                      height: '20px',
                      background: '#7b2d8b',
                      color: 'white',
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontWeight: 'bold',
                      fontSize: '10px',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                      border: '2px solid white',
                      cursor: 'pointer',
                      zIndex: 150,
                      userSelect: 'none'
                    }}
                  >
                    P{mark.markNumber || '?'}
                  </div>
                ))}

                {/* Text Stamps (OFR/PAG/MAX) - plain labels, nothing attached */}
                {textStamps.filter(s => s.page === pageNumber).map(stamp => (
                  <div
                    key={stamp.id}
                    title={`${stamp.label} - double-click to delete`}
                    onDoubleClick={(e) => {
                      e.stopPropagation();
                      setTextStamps(textStamps.filter(s2 => s2.id !== stamp.id));
                    }}
                    style={{
                      position: 'absolute',
                      left: stamp.x * zoomLevel - (stamp.label.length * 4.5) - 7,
                      top: stamp.y * zoomLevel - 13,
                      color: '#1a1a2e',
                      fontWeight: 'bold',
                      fontSize: '13px',
                      lineHeight: 1,
                      background: 'white',
                      border: '2px solid #1a1a2e',
                      borderRadius: '4px',
                      padding: '2px 6px',
                      boxShadow: '0 1px 3px rgba(0,0,0,0.15)',
                      cursor: 'pointer',
                      zIndex: 150,
                      userSelect: 'none'
                    }}
                  >
                    {stamp.label}
                  </div>
                ))}

                </div>

                {/* Feedback Popup */}
                {showFeedbackPopup && (
                  <>
                    <div
                      style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        background: 'rgba(0,0,0,0.3)',
                        zIndex: 1999
                      }}
                      onClick={() => {
                        setNumberedFeedback(numberedFeedback.filter(f => f.id !== showFeedbackPopup.feedback.id));
                        setNextFeedbackNumber(nextFeedbackNumber - 1);
                        setShowFeedbackPopup(null);
                        setTempFeedbackText('');
                      }}
                    />
                    
                    <div
                      style={{
                        position: 'fixed',
                        left: '50%',
                        top: '50%',
                        transform: 'translate(-50%, -50%)',
                        display: 'flex',
                        flexWrap: 'wrap',
                        justifyContent: 'center',
                        gap: '16px',
                        zIndex: 2000,
                        maxWidth: '90vw',
                        maxHeight: '90vh',
                        overflow: 'auto'
                      }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      {/* Main Feedback Form */}
                      <div style={{
                        background: 'white',
                        border: '3px solid #e74c3c',
                        borderRadius: '12px',
                        padding: '16px',
                        boxShadow: '0 8px 24px rgba(0,0,0,0.3)',
                        width: '460px'
                      }}>
                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          marginBottom: '12px',
                          gap: '8px'
                        }}>
                          <div style={{
                            width: '28px',
                            height: '28px',
                            background: '#e74c3c',
                            color: 'white',
                            borderRadius: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontWeight: 'bold',
                            fontSize: '14px'
                          }}>
                            {showFeedbackPopup.feedback.number}
                          </div>
                          <h4 style={{ margin: 0, color: '#2c3e50' }}>
                            Enter Feedback
                          </h4>
                        </div>

                        <textarea
                          autoFocus
                          value={tempFeedbackText}
                          onChange={(e) => setTempFeedbackText(e.target.value)}
                          onMouseDown={(e) => e.stopPropagation()}
                          onKeyDown={(e) => {
                            if (e.key === 'Escape') {
                              setNumberedFeedback(numberedFeedback.filter(f => f.id !== showFeedbackPopup.feedback.id));
                              setNextFeedbackNumber(nextFeedbackNumber - 1);
                              setShowFeedbackPopup(null);
                              setTempFeedbackText('');
                            } else if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
                              updateNumberedFeedback(showFeedbackPopup.feedback.id, tempFeedbackText);
                              setShowFeedbackPopup(null);
                              setTempFeedbackText('');
                            }
                          }}
                          placeholder="Type feedback... (Ctrl+Enter to save, Esc to cancel)"
                          style={{
                            width: '100%',
                            minHeight: '180px',
                            padding: '10px',
                            border: '2px solid #ecf0f1',
                            borderRadius: '6px',
                            fontSize: '14px',
                            fontFamily: 'inherit',
                            resize: 'vertical',
                            marginBottom: '12px',
                            boxSizing: 'border-box'
                          }}
                        />
                        
                        <div style={{
                          display: 'flex',
                          gap: '8px',
                          justifyContent: 'flex-end'
                        }}>
                          <button
                            onClick={() => {
                              setNumberedFeedback(numberedFeedback.filter(f => f.id !== showFeedbackPopup.feedback.id));
                              setNextFeedbackNumber(nextFeedbackNumber - 1);
                              setShowFeedbackPopup(null);
                              setTempFeedbackText('');
                            }}
                            onMouseDown={(e) => e.stopPropagation()}
                            style={{
                              padding: '8px 16px',
                              background: '#ecf0f1',
                              color: '#2c3e50',
                              border: 'none',
                              borderRadius: '6px',
                              cursor: 'pointer',
                              fontWeight: '600',
                              fontSize: '14px'
                            }}
                          >
                            Cancel
                          </button>
                          
                          {tempFeedbackText.trim() && (
                            <button
                              onClick={async () => {
                                if (!tempFeedbackText.trim()) {
                                  alert('Please enter comment text first');
                                  return;
                                }
                                
                                try {
                                  await suggestComment(
                                    submission.homeworkType,
                                    showFeedbackPopup.feedback.section,
                                    tempFeedbackText,
                                    user?.uid || 'unknown',
                                    user?.name || 'Unknown Marker'
                                  );
                                  
                                  // Notify head marker (non-blocking)
                                  try {
                                    const { sendHeadMarkerNotification, NOTIFICATION_TYPES } = await import('./firebase/headMarkerNotifications');
                                    sendHeadMarkerNotification(NOTIFICATION_TYPES.COMMENT_SUGGESTION, {
                                      homeworkType: homeworkNames[submission.homeworkType] || submission.homeworkType,
                                      section: showFeedbackPopup.feedback.section,
                                      commentText: tempFeedbackText,
                                      markerName: user?.name || 'Unknown Marker'
                                    });
                                  } catch (e) { console.error('Notification error:', e); }
                                  
                                  alert('SUCCESS: Comment suggested to head marker for approval!');
                                } catch (error) {
                                  console.error('Suggest comment error:', error);
                                  alert('Failed to suggest comment');
                                }
                              }}
                              onMouseDown={(e) => e.stopPropagation()}
                              style={{
                                padding: '8px 16px',
                                background: '#f39c12',
                                color: 'white',
                                border: 'none',
                                borderRadius: '6px',
                                cursor: 'pointer',
                                fontWeight: '600',
                                fontSize: '14px'
                              }}
                              title="Suggest this comment to be added to the comment bank"
                            >
                              <Lightbulb size={14} style={{ marginRight: '4px' }} /> Suggest
                            </button>
                          )}

                          {tempFeedbackText.trim() && (
                            <button
                              onClick={async () => {
                                try {
                                  const saved = await addMyComment(
                                    user.uid,
                                    submission.homeworkType,
                                    showFeedbackPopup.feedback.section,
                                    tempFeedbackText
                                  );
                                  const section = showFeedbackPopup.feedback.section;
                                  const sectionKey = section === 'final' ? 'final' : `section${section}`;
                                  setMyCommentBank(prev => {
                                    const base = prev || {};
                                    const existing = base[sectionKey];
                                    const isNew = existing && !Array.isArray(existing);
                                    return {
                                      ...base,
                                      [sectionKey]: isNew
                                        ? { ...existing, comments: [...(existing.comments || []), saved] }
                                        : [...(existing || []), saved]
                                    };
                                  });
                                } catch (error) {
                                  console.error('Save my comment error:', error);
                                  alert('Failed to save comment');
                                }
                              }}
                              onMouseDown={(e) => e.stopPropagation()}
                              style={{
                                padding: '8px 16px',
                                background: '#667eea',
                                color: 'white',
                                border: 'none',
                                borderRadius: '6px',
                                cursor: 'pointer',
                                fontWeight: '600',
                                fontSize: '14px'
                              }}
                              title="Save to your own personal comment list - available instantly, only to you"
                            >
                              + My Comments
                            </button>
                          )}

                          <button
                            onClick={() => {
                              updateNumberedFeedback(showFeedbackPopup.feedback.id, tempFeedbackText);
                              setShowFeedbackPopup(null);
                              setTempFeedbackText('');
                            }}
                            onMouseDown={(e) => e.stopPropagation()}
                            style={{
                              padding: '8px 16px',
                              background: '#1b998b',
                              color: 'white',
                              border: 'none',
                              borderRadius: '6px',
                              cursor: 'pointer',
                              fontWeight: '600',
                              fontSize: '14px'
                            }}
                          >
                            Save
                          </button>
                        </div>
                      </div>

                      {/* Comment Suggestions - Right Side Panel */}
                      {(commentBank || myCommentBank) && (() => {
                        const section = showFeedbackPopup.feedback.section;
                        const sectionKey = section === 'final' ? 'final' : `section${section}`;
                        const central = extractSectionData(commentBank, sectionKey);
                        const mine = extractSectionData(myCommentBank, sectionKey);

                        if (central.comments.length === 0 && mine.comments.length === 0) return null;

                        const commentBtn = (comment) => (
                          <div
                            key={comment.id}
                            onClick={() => setTempFeedbackText(prev => prev.trim() ? `${prev}\n${comment.text}` : comment.text)}
                            style={{ padding: '10px', background: '#f8f9fa', borderRadius: '8px', fontSize: '13px', cursor: 'pointer', border: '2px solid #e2e8f0', lineHeight: '1.5', transition: 'all 0.15s' }}
                            onMouseEnter={e => { e.currentTarget.style.background = '#667eea'; e.currentTarget.style.color = 'white'; e.currentTarget.style.borderColor = '#667eea'; }}
                            onMouseLeave={e => { e.currentTarget.style.background = '#f8f9fa'; e.currentTarget.style.color = '#2c3e50'; e.currentTarget.style.borderColor = '#e2e8f0'; }}
                          >
                            {comment.text}
                          </div>
                        );

                        return (
                          <div style={{ background: 'white', border: '3px solid #667eea', borderRadius: '12px', padding: '16px', boxShadow: '0 8px 24px rgba(0,0,0,0.3)', width: '350px', maxHeight: '560px', display: 'flex', flexDirection: 'column', overflowY: 'auto' }}>
                            <div style={{ fontSize: '11px', color: '#7f8c8d', marginBottom: '12px', fontStyle: 'italic' }}>
                              Click to insert - click more to add them together, then edit if needed
                            </div>

                            <div style={{ fontSize: '13px', fontWeight: '700', color: '#2c3e50', marginBottom: '8px' }}>
                              My Comments ({mine.comments.length})
                            </div>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '16px' }}>
                              {renderBankSection(mine, commentBtn, 'None saved yet - use "+ My Comments" above')}
                            </div>

                            <div style={{ fontSize: '13px', fontWeight: '700', color: '#667eea', marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                              <MessageCircle size={14} />
                              Central Bank ({central.comments.length})
                            </div>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                              {renderBankSection(central, commentBtn, 'No comments in the central bank yet')}
                            </div>
                          </div>
                        );
                      })()}
                    </div>
                  </>
                )}
              </div>

              {/* Page Navigation */}
              {numPages > 1 && (
                <div style={{
                  marginTop: '16px',
                  display: 'flex',
                  justifyContent: 'center',
                  gap: '12px'
                }}>
                  <button
                    onClick={() => { setPageNumber(Math.max(1, pageNumber - 1)); setSpreadsheetViewGroupId(null); }}
                    disabled={pageNumber <= 1}
                    style={{
                      padding: '8px 16px',
                      background: pageNumber <= 1 ? '#ecf0f1' : '#445e93',
                      color: pageNumber <= 1 ? '#95a5a6' : 'white',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: pageNumber <= 1 ? 'not-allowed' : 'pointer',
                      fontWeight: '600'
                    }}
                  >
                    Previous
                  </button>
                  <button
                    onClick={() => { setPageNumber(Math.min(numPages, pageNumber + 1)); setSpreadsheetViewGroupId(null); }}
                    disabled={pageNumber >= numPages}
                    style={{
                      padding: '8px 16px',
                      background: pageNumber >= numPages ? '#ecf0f1' : '#445e93',
                      color: pageNumber >= numPages ? '#95a5a6' : 'white',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: pageNumber >= numPages ? 'not-allowed' : 'pointer',
                      fontWeight: '600'
                    }}
                  >
                    Next
                  </button>
                </div>
              )}
            </div>
          ) : (
            <div style={{
              background: 'white',
              borderRadius: '12px',
              padding: '60px',
              textAlign: 'center',
              boxShadow: '0 2px 8px rgba(0,0,0,0.05)'
            }}>
              {pdfLoadError ? (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px', color: '#e74c3c' }}>
                  <AlertTriangle size={32} />
                  <p style={{ margin: 0, fontWeight: 600 }}>Unable to open this PDF</p>
                  <p style={{ margin: 0, color: '#7f8c8d', maxWidth: '420px' }}>{pdfLoadError}</p>
                </div>
              ) : (
                <p>Loading PDF...</p>
              )}
            </div>
          )}
        </div>

        {/* Sidebar */}
        <div style={{ width: '320px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
          {/* Technical Marks */}
          {(() => {
            const secs = sectionStructure?.sections || [
              { id: 'A', name: 'Section A', maxTechnical: 5, maxProfessional: 1 },
              { id: 'B', name: 'Section B', maxTechnical: 6, maxProfessional: 1 },
              { id: 'C', name: 'Section C', maxTechnical: 6, maxProfessional: 1 }
            ];
            const techGroups = sectionStructure?.technicalMarkGroups || [];
            // A group's parts routinely add up to more than the group's capped total — that's
            // expected, not an error, so it doesn't turn this panel red. Only an individual
            // section exceeding its own max would (kept for parity with other panels, though the
            // per-section cap above already prevents that from ever actually happening).
            const anyOver = secs.some(s => calculateSectionMarks(s.id).technical > (s.maxTechnical ?? s.maxMark ?? 6));
            const renderedGroupIndices = new Set();
            return (
              <div style={{
                background: 'white', borderRadius: '12px', padding: '16px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                border: `2px solid ${anyOver ? '#e74c3c' : '#445e93'}`
              }}>
                <h3 style={{ margin: '0 0 10px 0', fontSize: '13px', fontWeight: '700', color: '#445e93', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  Technical Marks
                </h3>
                <div style={{ fontSize: '13px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {secs.map(section => {
                    const gIdx = techGroups.findIndex(g => g.sections.includes(section.id));

                    // Grouped section: render the group's header + all its parts once, at the position
                    // of the first part encountered — skip subsequent parts of an already-rendered group.
                    if (gIdx !== -1) {
                      if (renderedGroupIndices.has(gIdx)) return null;
                      renderedGroupIndices.add(gIdx);
                      const group = techGroups[gIdx];
                      const groupSections = secs.filter(s => group.sections.includes(s.id));
                      const counted = calculateTechnicalGroupScore(group);
                      const label = group.name || `Group ${gIdx + 1}`;
                      return (
                        <div key={`tech-group-${gIdx}`}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <span style={{ color: '#2c3e50' }}>{label}</span>
                            <span style={{ fontWeight: '600', color: '#445e93' }}>
                              {counted.toFixed(1)}/{group.totalMarks} (Max {group.totalMarks})
                            </span>
                          </div>
                          <ul style={{ margin: '4px 0 0 0', padding: '0 0 0 18px', listStyleType: 'disc', display: 'flex', flexDirection: 'column', gap: '4px' }}>
                            {groupSections.map(gs => {
                              const st = calculateSectionMarks(gs.id);
                              const maxT = gs.maxTechnical ?? gs.maxMark ?? 0;
                              return (
                                <li key={gs.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                  <span style={{ color: '#2c3e50' }}>{gs.name}</span>
                                  <span style={{ fontWeight: '600', color: '#445e93' }}>
                                    {st.technical.toFixed(1)}/{maxT}
                                  </span>
                                </li>
                              );
                            })}
                          </ul>
                        </div>
                      );
                    }

                    // Ungrouped section: plain row, same as before.
                    const st = calculateSectionMarks(section.id);
                    const maxT = section.maxTechnical ?? section.maxMark ?? 6;
                    const over = st.technical > maxT;
                    return (
                      <div key={section.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span style={{ color: '#2c3e50' }}>{section.name}</span>
                        <span style={{ fontWeight: '600', color: over ? '#e74c3c' : '#445e93' }}>
                          {st.technical.toFixed(1)}/{maxT}{over && ' OVER!'}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })()}

          {/* Professional Marks */}
          {(() => {
            const secs = sectionStructure?.sections || [
              { id: 'A', name: 'Section A', maxTechnical: 5, maxProfessional: 1 },
              { id: 'B', name: 'Section B', maxTechnical: 6, maxProfessional: 1 },
              { id: 'C', name: 'Section C', maxTechnical: 6, maxProfessional: 1 }
            ];
            const groups = sectionStructure?.professionalMarkGroups || [];
            const groupedIds = new Set(groups.flatMap(g => g.sections));
            const nonGroupedWithP = secs.filter(s => !groupedIds.has(s.id) && (s.maxProfessional == null || s.maxProfessional > 0));
            const additionalMax = getAdditionalProfessionalReasons().length;
            const additionalGroupIdx = groups.findIndex(g => g.includeAdditional);
            const additionalGrouped = additionalGroupIdx !== -1;
            if (nonGroupedWithP.length === 0 && groups.length === 0 && additionalMax === 0) return null;

            const anyProfOver =
              nonGroupedWithP.some(s => {
                const st = calculateSectionMarks(s.id);
                return s.maxProfessional != null && Math.round(st.professional / PROFESSIONAL) > s.maxProfessional;
              }) ||
              groups.some(g => {
                const used = professionalMarks.filter(m => g.sections.includes(m.section)).length +
                  (g.includeAdditional ? additionalProfessionalMarks.length : 0);
                return used > g.totalMarks;
              }) ||
              (!additionalGrouped && additionalProfessionalMarks.length > additionalMax);

            return (
              <div style={{
                background: 'white', borderRadius: '12px', padding: '16px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                border: `2px solid ${anyProfOver ? '#e74c3c' : '#f39c12'}`
              }}>
                <h3 style={{ margin: '0 0 10px 0', fontSize: '13px', fontWeight: '700', color: '#f39c12', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  Professional Marks
                </h3>
                <div style={{ fontSize: '13px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  {nonGroupedWithP.map(section => {
                    const st = calculateSectionMarks(section.id);
                    const maxP = section.maxProfessional;
                    const placed = Math.round(st.professional / PROFESSIONAL);
                    const over = maxP != null && placed > maxP;
                    return (
                      <div key={section.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span style={{ color: '#2c3e50' }}>{section.name}</span>
                        <span style={{ fontWeight: '600', color: over ? '#e74c3c' : '#f39c12' }}>
                          {maxP != null ? `${Math.min(placed, maxP)}/${maxP}` : placed}
                          {over && ` (+${placed - maxP} not counted)`}
                        </span>
                      </div>
                    );
                  })}
                  {groups.map((group, gIdx) => {
                    const sectionUsed = professionalMarks.filter(m => group.sections.includes(m.section)).length;
                    const used = sectionUsed + (group.includeAdditional ? additionalProfessionalMarks.length : 0);
                    const over = used > group.totalMarks;
                    const label = group.name || `Group ${gIdx + 1}`;
                    return (
                      <div key={gIdx} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span style={{ color: '#2c3e50' }}>{label}{group.includeAdditional && ' (incl. additional)'}</span>
                        <span style={{ fontWeight: '600', color: over ? '#e74c3c' : '#f39c12' }}>
                          {Math.min(used, group.totalMarks)}/{group.totalMarks}{over && ` (+${used - group.totalMarks} not counted)`}
                        </span>
                      </div>
                    );
                  })}
                  {additionalMax > 0 && !additionalGrouped && (
                    <>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span style={{ color: '#7b2d8b' }}>Additional Professional</span>
                        <span style={{ fontWeight: '600', color: additionalProfessionalMarks.length > additionalMax ? '#e74c3c' : '#7b2d8b' }}>
                          {additionalProfessionalMarks.length}/{additionalMax}{additionalProfessionalMarks.length > additionalMax ? ' OVER!' : ''}
                        </span>
                      </div>
                      {additionalProfessionalMarks.length > 0 && (
                        <div style={{ fontSize: '12px', color: '#7f8c8d', marginTop: '2px' }}>
                          {additionalProfessionalMarks.map((m) => (
                            <div key={m.id} style={{ marginBottom: '4px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                              <span style={{ flex: 1, marginRight: '8px' }}>P{m.markNumber}. {m.reason || 'No reason given'}</span>
                              <button onClick={() => {
                                setAdditionalProfessionalMarks(additionalProfessionalMarks.filter(x => x.id !== m.id));
                                const remainingMarks = additionalProfessionalMarks.filter(x => x.id !== m.id);
                                const allRemaining = [...professionalMarks, ...remainingMarks];
                                if (allRemaining.length > 0) {
                                  const maxNum = Math.max(...allRemaining.map(mark => mark.markNumber || 0));
                                  setNextProfessionalMarkNumber(maxNum + 1);
                                } else {
                                  setNextProfessionalMarkNumber(1);
                                }
                              }}
                                style={{ background: 'none', border: 'none', color: '#e74c3c', cursor: 'pointer', fontSize: '14px', padding: '0', lineHeight: 1 }}>
                                <X size={12} />
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            );
          })()}


          {/* Available Professional Marks - Collapsible Panels */}
          {(() => {
            const sectionReasons = getProfessionalReasons(selectedSection);
            const additionalReasons = getAdditionalProfessionalReasons();
            
            // Filter out already used reasons
            const usedSectionReasons = professionalMarks
              .filter(m => m.section === selectedSection)
              .map(m => m.reasonIndex);
            const availableSectionReasons = sectionReasons.filter((_, i) => !usedSectionReasons.includes(i));

            const usedAdditionalReasons = additionalProfessionalMarks.map(m => m.reasonIndex);
            const availableAdditionalReasons = additionalReasons.filter((_, i) => !usedAdditionalReasons.includes(i));

            const sectionGIdx = getGroupIndex(selectedSection);
            const selectedSectionName = sectionStructure?.sections?.find(s => s.id === selectedSection)?.name || `Section ${selectedSection}`;
            const sectionGroupLabel = sectionGIdx !== -1
              ? `Group ${sectionGIdx + 1} Professional Marks`
              : `${selectedSectionName} Professional Marks`;

            const showSectionPanel = availableSectionReasons.length > 0;
            const showAdditionalPanel = availableAdditionalReasons.length > 0;
            
            if (!showSectionPanel && !showAdditionalPanel) return null;
            
            return (
              <>
                {/* Section Professional Marks Panel */}
                {showSectionPanel && (
                  <div style={{
                    background: 'white',
                    borderRadius: '12px',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                    border: '2px solid #f39c12',
                    overflow: 'hidden'
                  }}>
                    <div
                      onClick={() => setShowSectionProfMarks(!showSectionProfMarks)}
                      style={{
                        padding: '16px 20px',
                        background: showSectionProfMarks ? '#f39c12' : '#fef9f0',
                        cursor: 'pointer',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        userSelect: 'none'
                      }}
                    >
                      <h3 style={{
                        margin: 0,
                        color: showSectionProfMarks ? 'white' : '#f39c12',
                        fontSize: '15px',
                        fontWeight: '700'
                      }}>
                        {sectionGroupLabel}
                      </h3>
                      <span style={{ color: showSectionProfMarks ? 'white' : '#f39c12', fontSize: '18px' }}>
                        {showSectionProfMarks ? '▼' : '▶'}
                      </span>
                    </div>
                    
                    {showSectionProfMarks && (
                      <div style={{ padding: '16px 20px', background: 'white' }}>
                        <div style={{ fontSize: '12px', color: '#7f8c8d', marginBottom: '12px', fontStyle: 'italic' }}>
                          Click reason, then click PDF to place mark
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                          {availableSectionReasons.map((reason, idx) => {
                            const actualIndex = sectionReasons.indexOf(reason);
                            const isSelected = selectedProfessionalReason?.type === 'section' && 
                                             selectedProfessionalReason?.reasonIndex === actualIndex;
                            return (
                              <button
                                key={actualIndex}
                                onClick={() => {
                                  setSelectedProfessionalReason({
                                    type: 'section',
                                    section: selectedSection,
                                    reason,
                                    reasonIndex: actualIndex
                                  });
                                  setActiveTool('professional-mark');
                                }}
                                style={{
                                  padding: '10px 14px',
                                  background: isSelected ? '#f39c12' : '#fef9f0',
                                  color: isSelected ? 'white' : '#2c3e50',
                                  border: `2px solid ${isSelected ? '#f39c12' : '#e2e8f0'}`,
                                  borderRadius: '8px',
                                  cursor: 'pointer',
                                  textAlign: 'left',
                                  fontSize: '13px',
                                  fontFamily: 'inherit',
                                  lineHeight: '1.4',
                                  fontWeight: isSelected ? '600' : '400',
                                  transition: 'all 0.15s'
                                }}
                                onMouseEnter={e => {
                                  if (!isSelected) {
                                    e.target.style.background = '#fef0d0';
                                    e.target.style.borderColor = '#f39c12';
                                  }
                                }}
                                onMouseLeave={e => {
                                  if (!isSelected) {
                                    e.target.style.background = '#fef9f0';
                                    e.target.style.borderColor = '#e2e8f0';
                                  }
                                }}
                              >
                                P{nextProfessionalMarkNumber + (isSelected ? 0 : idx)}. {reason}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                )}
                
                {/* Additional Professional Marks Panel */}
                {showAdditionalPanel && (
                  <div style={{
                    background: 'white',
                    borderRadius: '12px',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                    border: '2px solid #7b2d8b',
                    overflow: 'hidden'
                  }}>
                    <div
                      onClick={() => setShowAdditionalProfMarks(!showAdditionalProfMarks)}
                      style={{
                        padding: '16px 20px',
                        background: showAdditionalProfMarks ? '#7b2d8b' : '#f9f0fc',
                        cursor: 'pointer',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        userSelect: 'none'
                      }}
                    >
                      <h3 style={{
                        margin: 0,
                        color: showAdditionalProfMarks ? 'white' : '#7b2d8b',
                        fontSize: '15px',
                        fontWeight: '700'
                      }}>
                        Additional Professional Marks
                      </h3>
                      <span style={{ color: showAdditionalProfMarks ? 'white' : '#7b2d8b', fontSize: '18px' }}>
                        {showAdditionalProfMarks ? '▼' : '▶'}
                      </span>
                    </div>
                    
                    {showAdditionalProfMarks && (
                      <div style={{ padding: '16px 20px', background: 'white' }}>
                        <div style={{ fontSize: '12px', color: '#7f8c8d', marginBottom: '12px', fontStyle: 'italic' }}>
                          Click reason, then click PDF to place mark
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                          {availableAdditionalReasons.map((reason, idx) => {
                            const actualIndex = additionalReasons.indexOf(reason);
                            const isSelected = selectedProfessionalReason?.type === 'additional' && 
                                             selectedProfessionalReason?.reasonIndex === actualIndex;
                            return (
                              <button
                                key={actualIndex}
                                onClick={() => {
                                  setSelectedProfessionalReason({
                                    type: 'additional',
                                    section: null,
                                    reason,
                                    reasonIndex: actualIndex
                                  });
                                  setActiveTool('professional-mark');
                                }}
                                style={{
                                  padding: '10px 14px',
                                  background: isSelected ? '#7b2d8b' : '#f9f0fc',
                                  color: isSelected ? 'white' : '#2c3e50',
                                  border: `2px solid ${isSelected ? '#7b2d8b' : '#e2e8f0'}`,
                                  borderRadius: '8px',
                                  cursor: 'pointer',
                                  textAlign: 'left',
                                  fontSize: '13px',
                                  fontFamily: 'inherit',
                                  lineHeight: '1.4',
                                  fontWeight: isSelected ? '600' : '400',
                                  transition: 'all 0.15s'
                                }}
                                onMouseEnter={e => {
                                  if (!isSelected) {
                                    e.target.style.background = '#f5e8f9';
                                    e.target.style.borderColor = '#7b2d8b';
                                  }
                                }}
                                onMouseLeave={e => {
                                  if (!isSelected) {
                                    e.target.style.background = '#f9f0fc';
                                    e.target.style.borderColor = '#e2e8f0';
                                  }
                                }}
                              >
                                P{nextProfessionalMarkNumber + (isSelected ? 0 : idx)}. {reason}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </>
            );
          })()}



          {/* Total */}
          <div style={{
            background: 'white',
            borderRadius: '12px',
            padding: '20px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
            border: '3px solid #e74c3c'
          }}>
            <div style={{ fontSize: '24px', fontWeight: 'bold', marginBottom: '12px' }}>
              TOTAL: {totals.total.toFixed(1)}
            </div>
            {(() => {
              const maxAvail = calculateMaxMarks();
              const pct = calculatePercentage(totals.total, maxAvail);
              return (
                <div style={{ fontSize: '18px', fontWeight: '600', color: '#c33329', marginBottom: '8px' }}>
                  {pct}% ({totals.total.toFixed(1)}/{maxAvail})
                </div>
              );
            })()}
            {(() => {
              const rawAdditional = calculateAdditionalProfessionalMarks();
              return (
                <>
                  <div>Technical: {totals.technical.toFixed(1)}</div>
                  <div>
                    Professional: {totals.professional.toFixed(1)}
                    {rawAdditional > 0 && ` (incl. ${rawAdditional.toFixed(1)} additional)`}
                  </div>
                </>
              );
            })()}
          </div>

          {/* Professional Marks List */}
          {(professionalMarks.length > 0 || additionalProfessionalMarks.length > 0) && (
            <div style={{
              background: 'white',
              borderRadius: '12px',
              padding: '20px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
              maxHeight: '400px',
              overflow: 'auto'
            }}>
              <h3 style={{ margin: '0 0 16px 0', color: '#f39c12' }}>
                Professional Marks ({professionalMarks.length + additionalProfessionalMarks.length})
              </h3>
              {professionalMarks.length > 0 && (
                <>
                  <div style={{ fontSize: '11px', fontWeight: '700', color: '#7f8c8d', textTransform: 'uppercase', marginBottom: '8px', letterSpacing: '0.5px' }}>
                    Section Marks
                  </div>
                  {professionalMarks.map(mark => (
                    <div key={mark.id} style={{
                      marginBottom: '10px', padding: '10px 12px',
                      background: '#fef9f0', borderRadius: '8px',
                      border: '1px solid #f39c12',
                      display: 'flex', alignItems: 'flex-start', gap: '10px'
                    }}>
                      <div style={{
                        width: '28px', height: '28px', background: '#f39c12',
                        borderRadius: '50%', color: 'white', display: 'flex',
                        alignItems: 'center', justifyContent: 'center',
                        fontWeight: 'bold', fontSize: '10px', flexShrink: 0
                      }}>P{mark.markNumber || '?'}</div>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: '11px', color: '#95a5a6', marginBottom: '3px' }}>
                          {sectionStructure?.sections?.find(s => s.id === mark.section)?.name || `Section ${mark.section}`} — {mark.cell ? cellPlaceLabel(mark.cell) : `p.${mark.page}`}
                        </div>
                        <div style={{ fontSize: '13px', color: '#2c3e50', lineHeight: '1.4' }}>
                          {mark.reason}
                        </div>
                      </div>
                      <button onClick={() => {
                        setProfessionalMarks(professionalMarks.filter(m => m.id !== mark.id));
                        // Recalculate next number based on remaining marks
                        const remainingMarks = professionalMarks.filter(m => m.id !== mark.id);
                        const allRemaining = [...remainingMarks, ...additionalProfessionalMarks];
                        if (allRemaining.length > 0) {
                          const maxNum = Math.max(...allRemaining.map(m => m.markNumber || 0));
                          setNextProfessionalMarkNumber(maxNum + 1);
                        } else {
                          setNextProfessionalMarkNumber(1);
                        }
                      }}
                        style={{ background: 'none', border: 'none', color: '#e74c3c', cursor: 'pointer', padding: '2px', flexShrink: 0 }}>
                        <X size={14} />
                      </button>
                    </div>
                  ))}
                </>
              )}
              {additionalProfessionalMarks.length > 0 && (
                <>
                  <div style={{ fontSize: '11px', fontWeight: '700', color: '#7f8c8d', textTransform: 'uppercase', marginBottom: '8px', marginTop: professionalMarks.length > 0 ? '12px' : '0', letterSpacing: '0.5px' }}>
                    Additional Marks
                  </div>
                  {additionalProfessionalMarks.map(mark => (
                    <div key={mark.id} style={{
                      marginBottom: '10px', padding: '10px 12px',
                      background: '#f9f0fc', borderRadius: '8px',
                      border: '1px solid #7b2d8b',
                      display: 'flex', alignItems: 'flex-start', gap: '10px'
                    }}>
                      <div style={{
                        width: '28px', height: '28px', background: '#7b2d8b',
                        borderRadius: '50%', color: 'white', display: 'flex',
                        alignItems: 'center', justifyContent: 'center',
                        fontWeight: 'bold', fontSize: '9px', flexShrink: 0
                      }}>P{mark.markNumber || '?'}</div>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: '11px', color: '#95a5a6', marginBottom: '3px' }}>
                          Additional — {mark.cell ? cellPlaceLabel(mark.cell) : `p.${mark.page}`}
                        </div>
                        <div style={{ fontSize: '13px', color: '#2c3e50', lineHeight: '1.4' }}>
                          {mark.reason}
                        </div>
                      </div>
                      <button onClick={() => {
                        setAdditionalProfessionalMarks(additionalProfessionalMarks.filter(m => m.id !== mark.id));
                        // Recalculate next number based on remaining marks
                        const remainingMarks = additionalProfessionalMarks.filter(m => m.id !== mark.id);
                        const allRemaining = [...professionalMarks, ...remainingMarks];
                        if (allRemaining.length > 0) {
                          const maxNum = Math.max(...allRemaining.map(m => m.markNumber || 0));
                          setNextProfessionalMarkNumber(maxNum + 1);
                        } else {
                          setNextProfessionalMarkNumber(1);
                        }
                      }}
                        style={{ background: 'none', border: 'none', color: '#e74c3c', cursor: 'pointer', padding: '2px', flexShrink: 0 }}>
                        <X size={14} />
                      </button>
                    </div>
                  ))}
                </>
              )}
            </div>
          )}

          {/* Final Comment */}
          <div style={{
            background: 'white',
            borderRadius: '12px',
            padding: '20px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.05)'
          }}>
            <h3 style={{ margin: '0 0 16px 0', fontSize: '16px', color: '#2c3e50' }}>
              Final Comment
            </h3>

            {questionStructure.length > 0 ? (
              <div>
                <button
                  onClick={() => {
                    setActiveQuestionTab(questionStructure[0]?.id || null);
                    setShowFinalCommentModal(true);
                  }}
                  style={{
                    width: '100%',
                    padding: '10px 16px',
                    background: '#667eea',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontWeight: '600',
                    fontSize: '14px',
                    marginBottom: '12px'
                  }}
                >
                  Edit Final Comments
                </button>
                {buildCombinedFinalComment(questionComments, questionStructure).trim() ? (
                  <div style={{
                    padding: '10px 12px',
                    background: '#f8f9fa',
                    border: '2px solid #e2e8f0',
                    borderRadius: '6px',
                    fontSize: '12px',
                    color: '#2c3e50',
                    lineHeight: '1.6',
                    whiteSpace: 'pre-wrap'
                  }}>
                    {buildCombinedFinalComment(questionComments, questionStructure)}
                  </div>
                ) : (
                  <div style={{ fontSize: '13px', color: '#7f8c8d' }}>
                    No final comments written yet
                  </div>
                )}
              </div>
            ) : (
              <>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
                  {(() => {
                    const central = extractSectionData(commentBank, 'final');
                    const mine = extractSectionData(myCommentBank, 'final');
                    const count = central.comments.length + mine.comments.length;
                    return count > 0 && (
                      <span
                        onClick={() => setShowFinalCommentSuggestions(true)}
                        style={{ fontSize: '12px', color: '#667eea', cursor: 'pointer', fontWeight: '500' }}
                      >
                        {mine.comments.length} mine, {central.comments.length} central - click to view
                      </span>
                    );
                  })()}
                </div>
                <textarea
                  value={finalComment}
                  onChange={(e) => setFinalComment(e.target.value)}
                  placeholder="Overall feedback..."
                  style={{
                    width: '100%',
                    minHeight: '100px',
                    padding: '10px',
                    border: '2px solid #ecf0f1',
                    borderRadius: '6px',
                    fontSize: '14px',
                    fontFamily: 'inherit',
                    resize: 'vertical'
                  }}
                />
                {finalComment.trim() && !user?.suspended && (
                  <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                    <button
                      onClick={async () => {
                        try {
                          await suggestComment(
                            submission.homeworkType,
                            'final',
                            finalComment,
                            user?.uid || 'unknown',
                            user?.name || 'Unknown Marker'
                          );
                          try {
                            const { sendHeadMarkerNotification, NOTIFICATION_TYPES } = await import('./firebase/headMarkerNotifications');
                            sendHeadMarkerNotification(NOTIFICATION_TYPES.COMMENT_SUGGESTION, {
                              homeworkType: homeworkNames[submission.homeworkType] || submission.homeworkType,
                              section: 'final',
                              commentText: finalComment,
                              markerName: user?.name || 'Unknown Marker'
                            });
                          } catch (e) { console.error('Notification error:', e); }
                          alert('Comment suggested to head marker for approval!');
                        } catch (error) {
                          console.error('Suggest comment error:', error);
                          alert('Failed to suggest comment');
                        }
                      }}
                      style={{
                        marginTop: '8px',
                        padding: '8px 16px',
                        background: '#f39c12',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontWeight: '600',
                        fontSize: '14px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px'
                      }}
                      title="Suggest this comment to be added to the final comment bank"
                    >
                      <Lightbulb size={14} /> Suggest
                    </button>

                    <button
                      onClick={async () => {
                        try {
                          const saved = await addMyComment(
                            user.uid,
                            submission.homeworkType,
                            'final',
                            finalComment
                          );
                          setMyCommentBank(prev => {
                            const base = prev || {};
                            const existing = base.final;
                            const isNew = existing && !Array.isArray(existing);
                            return {
                              ...base,
                              final: isNew
                                ? { ...existing, comments: [...(existing.comments || []), saved] }
                                : [...(existing || []), saved]
                            };
                          });
                        } catch (error) {
                          console.error('Save my comment error:', error);
                          alert('Failed to save comment');
                        }
                      }}
                      style={{
                        marginTop: '8px',
                        padding: '8px 16px',
                        background: '#667eea',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontWeight: '600',
                        fontSize: '14px'
                      }}
                      title="Save to your own personal comment list - available instantly, only to you"
                    >
                      + My Comments
                    </button>
                  </div>
                )}
              </>
            )}
          </div>

          {/* Numbered Feedback */}
          {numberedFeedback.length > 0 && (
            <div style={{
              background: 'white',
              borderRadius: '12px',
              padding: '20px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
              maxHeight: '400px',
              overflow: 'auto'
            }}>
              <h3 style={{ margin: '0 0 16px 0' }}>
                Numbered Feedback ({numberedFeedback.length})
              </h3>
              {numberedFeedback
                .sort((a, b) => a.number - b.number)
                .map(feedback => (
                  <div
                    key={feedback.id}
                    style={{
                      marginBottom: '16px',
                      padding: '12px',
                      background: activeFeedbackId === feedback.id ? '#fff3cd' : '#f8f9fa',
                      border: '2px solid #e74c3c',
                      borderRadius: '8px'
                    }}
                  >
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      marginBottom: '8px',
                      justifyContent: 'space-between'
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <div style={{
                          width: '28px',
                          height: '28px',
                          background: '#e74c3c',
                          color: 'white',
                          borderRadius: '50%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontWeight: 'bold',
                          fontSize: '14px'
                        }}>
                          {feedback.number}
                        </div>
                        <span style={{ fontSize: '12px', color: '#7f8c8d' }}>
                          {feedback.cell ? cellPlaceLabel(feedback.cell) : `Page ${feedback.page}`}
                        </span>
                      </div>
                      
                      <button
                        onClick={() => deleteNumberedFeedback(feedback.id)}
                        style={{
                          background: '#e74c3c',
                          color: 'white',
                          border: 'none',
                          borderRadius: '4px',
                          width: '24px',
                          height: '24px',
                          cursor: 'pointer',
                          fontSize: '14px',
                          fontWeight: 'bold'
                        }}
                      >
                        <X size={14} />
                      </button>
                    </div>
                    
                    <textarea
                      value={feedback.text}
                      onChange={(e) => updateNumberedFeedback(feedback.id, e.target.value)}
                      onFocus={() => setActiveFeedbackId(feedback.id)}
                      onBlur={() => setActiveFeedbackId(null)}
                      placeholder={`Feedback for marker ${feedback.number}...`}
                      style={{
                        width: '100%',
                        minHeight: '60px',
                        padding: '8px',
                        border: '1px solid #ecf0f1',
                        borderRadius: '4px',
                        fontSize: '13px',
                        fontFamily: 'inherit',
                        resize: 'vertical'
                      }}
                    />
                  </div>
                ))}
            </div>
          )}

          {/* AI-Generated Overall Summary - head marker review only, opt-in per org */}
          {isHeadMarkerReview && aiSummaryFeatureEnabled && (
            <div style={{
              background: 'white',
              borderRadius: '12px',
              padding: '20px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
              border: '2px solid #667eea',
              marginBottom: '16px'
            }}>
              <h3 style={{ margin: '0 0 8px 0', color: '#667eea' }}>AI-Generated Summary</h3>

              {aiSummaryStatus === 'generating' && (
                <div style={{ fontSize: '13px', color: '#7f8c8d', marginBottom: '10px' }}>
                  {(aiSummaryPolling || generatingSummaryNow) ? 'Generating…' : 'Still generating - taking longer than expected.'}
                </div>
              )}
              {aiSummaryStatus === 'skipped' && (
                <div style={{ fontSize: '13px', color: '#7f8c8d', marginBottom: '10px' }}>
                  No marker comments to summarize yet.
                </div>
              )}
              {aiSummaryStatus === 'failed' && (
                <div style={{ fontSize: '13px', color: '#c0392b', marginBottom: '10px' }}>
                  Failed to generate{aiSummaryError ? `: ${aiSummaryError}` : '.'}
                </div>
              )}
              {!aiSummaryStatus && !aiSummaryPolling && (
                <div style={{ fontSize: '13px', color: '#7f8c8d', marginBottom: '10px' }}>
                  Not generated yet.
                </div>
              )}

              {aiSummaryStatus !== 'generating' && (
                <button
                  onClick={handleGenerateSummaryNow}
                  disabled={generatingSummaryNow}
                  style={{
                    padding: '8px 16px',
                    background: generatingSummaryNow ? '#bdc3c7' : '#667eea',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: generatingSummaryNow ? 'not-allowed' : 'pointer',
                    fontWeight: '600',
                    fontSize: '13px',
                    marginBottom: '12px'
                  }}
                >
                  {generatingSummaryNow ? 'Generating…' : (aiSummaryStatus === 'ready' ? 'Regenerate Summary' : 'Generate Summary')}
                </button>
              )}

              {(aiSummaryStatus === 'ready' || aiSummaryText) && (
                <>
                  <textarea
                    value={aiSummaryText}
                    onChange={(e) => setAiSummaryText(e.target.value)}
                    onBlur={handleAiSummaryTextBlur}
                    placeholder="AI summary..."
                    style={{
                      width: '100%',
                      minHeight: '100px',
                      padding: '10px',
                      border: '2px solid #ecf0f1',
                      borderRadius: '6px',
                      fontSize: '14px',
                      fontFamily: 'inherit',
                      resize: 'vertical',
                      marginBottom: '12px'
                    }}
                  />

                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', color: '#2c3e50', marginBottom: '12px', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={includeSummaryInFinalComment}
                      onChange={(e) => setIncludeSummaryInFinalComment(e.target.checked)}
                    />
                    Include in Final Comment / PDF feedback
                  </label>

                  <button
                    onClick={handleSendSummarySeparately}
                    disabled={!submission?.studentPhone || !aiSummaryText.trim()}
                    title={!submission?.studentPhone ? 'No phone number on file for this student' : undefined}
                    style={{
                      padding: '8px 16px',
                      background: 'white',
                      color: '#25D366',
                      border: '2px solid #25D366',
                      borderRadius: '6px',
                      cursor: (!submission?.studentPhone || !aiSummaryText.trim()) ? 'not-allowed' : 'pointer',
                      opacity: (!submission?.studentPhone || !aiSummaryText.trim()) ? 0.5 : 1,
                      fontWeight: '600',
                      fontSize: '13px'
                    }}
                  >
                    Send Summary Separately via WhatsApp
                  </button>
                </>
              )}
            </div>
          )}

          {/* Complete Button */}
          <div style={{
            background: 'white',
            borderRadius: '12px',
            padding: '20px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
            border: `3px solid ${isHeadMarkerReview ? '#c33329' : '#1b998b'}`
          }}>
            <h3 style={{ margin: '0 0 12px 0', color: isHeadMarkerReview ? '#c33329' : '#1b998b' }}>
              {isHeadMarkerReview ? 'Review & Approve' : 'Complete Marking'}
            </h3>
            {loadingExistingMarking && (
              <div style={{
                padding: '12px',
                background: '#fff3cd',
                borderRadius: '8px',
                marginBottom: '12px',
                fontSize: '14px',
                color: '#856404'
              }}>
                Loading existing marking data...
              </div>
            )}
            
            {/* Save Progress Button */}
            <button
              onClick={saveProgress}
              disabled={savingToFirebase || loadingExistingMarking}
              style={{
                width: '100%',
                padding: '14px',
                background: (!savingToFirebase && !loadingExistingMarking) ? '#f39c12' : '#95a5a6',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                cursor: (!savingToFirebase && !loadingExistingMarking) ? 'pointer' : 'not-allowed',
                fontWeight: '600',
                fontSize: '15px',
                marginBottom: '12px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px'
              }}
            >
              <Save size={18} />
              {savingToFirebase ? 'Saving...' : 'Save Progress & Come Back Later'}
            </button>

            {/* Flag for Review Button - available to markers and the head marker alike */}
            <button
              onClick={() => { setShowFlagModal(true); setFlagComment(''); }}
              disabled={savingToFirebase || loadingExistingMarking}
              style={{
                width: '100%',
                padding: '12px',
                background: 'white',
                color: '#f39c12',
                border: '2px solid #f39c12',
                borderRadius: '8px',
                cursor: (!savingToFirebase && !loadingExistingMarking) ? 'pointer' : 'not-allowed',
                fontWeight: '600',
                fontSize: '14px',
                marginBottom: '12px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px'
              }}
            >
              <Flag size={16} />
              Flag for Review
            </button>

            {/* WhatsApp Notification Checkbox (only for head marker) */}
            {isHeadMarkerReview && (
              <>
              <div
                onClick={() => setSendWhatsAppNotification(!sendWhatsAppNotification)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  padding: '12px',
                  background: '#f8f9fa',
                  border: '2px solid #e2e8f0',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  marginBottom: '12px',
                  userSelect: 'none'
                }}
              >
                <div style={{
                  width: '20px',
                  height: '20px',
                  borderRadius: '4px',
                  border: '2px solid',
                  borderColor: sendWhatsAppNotification ? '#25D366' : '#bdc3c7',
                  background: sendWhatsAppNotification ? '#25D366' : 'white',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'white',
                  fontWeight: 'bold',
                  fontSize: '12px',
                  flexShrink: 0
                }}>
                  {sendWhatsAppNotification && <Check size={14} />}
                </div>
                <span style={{ fontSize: '14px', color: '#4a5568', fontWeight: '500' }}>
                  Tick to send final comment to student by WhatsApp
                </span>
              </div>
              <button
                onClick={handleCopyWhatsAppMessage}
                style={{
                  width: '100%',
                  padding: '10px',
                  background: whatsappCopied ? '#27ae60' : '#ecf0f1',
                  color: whatsappCopied ? 'white' : '#2c3e50',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontWeight: '600',
                  fontSize: '13px',
                  marginBottom: '4px',
                  transition: 'all 0.2s'
                }}
              >
                {whatsappCopied ? 'Copied!' : 'Copy WhatsApp message'}
              </button>
              </>
            )}
            
            {/* Complete Marking Button */}
            <button
              onClick={completeFirebaseMarking}
              disabled={savingToFirebase || loadingExistingMarking}
              style={{
                width: '100%',
                padding: '16px',
                background: (!savingToFirebase && !loadingExistingMarking) 
                  ? (isHeadMarkerReview ? '#c33329' : '#1b998b') 
                  : '#95a5a6',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                cursor: (!savingToFirebase && !loadingExistingMarking) ? 'pointer' : 'not-allowed',
                fontWeight: '700',
                fontSize: '16px'
              }}
            >
              {savingToFirebase 
                ? 'Saving...' 
                : isHeadMarkerReview 
                  ? 'Approve & Send to Student' 
                  : 'Complete & Submit'}
            </button>
          </div>
        </div>
      </div>

      {/* Professional Mark Reason Picker Popup */}
      {pendingProfessionalMark && (
        <>
          <div onClick={() => setPendingProfessionalMark(null)}
            style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 1998 }} />
          <div style={{
            position: 'fixed',
            left: Math.min(pendingProfessionalMark.popupX, window.innerWidth - 340),
            top: Math.min(pendingProfessionalMark.popupY, window.innerHeight - 360),
            background: 'white',
            border: `3px solid ${pendingProfessionalMark.type === 'section' ? '#f39c12' : '#7b2d8b'}`,
            borderRadius: '12px',
            boxShadow: '0 8px 24px rgba(0,0,0,0.25)',
            zIndex: 1999,
            width: '320px',
            padding: '20px'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px' }}>
              <div style={{
                width: '32px', height: '32px',
                background: pendingProfessionalMark.type === 'section' ? '#f39c12' : '#7b2d8b',
                borderRadius: '50%', color: 'white', display: 'flex',
                alignItems: 'center', justifyContent: 'center', fontWeight: 'bold', fontSize: '11px', flexShrink: 0
              }}>
                {pendingProfessionalMark.type === 'section' ? '1P' : 'AP'}
              </div>
              <h4 style={{ margin: 0, color: '#2c3e50', fontSize: '15px' }}>
                {pendingProfessionalMark.type === 'section'
                  ? `Professional Mark — ${sectionStructure?.sections?.find(s => s.id === pendingProfessionalMark.section)?.name || `Section ${pendingProfessionalMark.section}`}`
                  : 'Additional Professional Mark'}
              </h4>
            </div>

            <div style={{ fontSize: '13px', color: '#7f8c8d', marginBottom: '12px' }}>
              Select the reason for this professional mark:
            </div>

            {(() => {
              const fullReasons = pendingProfessionalMark.type === 'section'
                ? getProfessionalReasons(pendingProfessionalMark.section)
                : getAdditionalProfessionalReasons();
              const usedReasonIndices = pendingProfessionalMark.type === 'section'
                ? professionalMarks.filter(m => m.section === pendingProfessionalMark.section).map(m => m.reasonIndex)
                : additionalProfessionalMarks.map(m => m.reasonIndex);
              const reasons = fullReasons
                .map((reason, i) => ({ reason, originalIndex: i }))
                .filter(({ originalIndex }) => !usedReasonIndices.includes(originalIndex));

              if (fullReasons.length === 0) {
                return (
                  <div style={{ fontSize: '13px', color: '#e74c3c', padding: '12px', background: '#fef9f9', borderRadius: '8px', marginBottom: '12px' }}>
                    No reasons configured for this section yet. Ask the head marker to add reasons in Settings.
                  </div>
                );
              }
              if (reasons.length === 0) {
                return (
                  <div style={{ fontSize: '13px', color: '#27ae60', padding: '12px', background: '#f0faf4', borderRadius: '8px', marginBottom: '12px' }}>
                    All professional marks for this section have been awarded.
                  </div>
                );
              }

              return (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '16px', maxHeight: '220px', overflowY: 'auto' }}>
                  {reasons.map(({ reason, originalIndex }) => (
                    <button
                      key={originalIndex}
                      onClick={() => {
                        const newMark = {
                          id: Date.now(),
                          x: pendingProfessionalMark.canvasX,
                          y: pendingProfessionalMark.canvasY,
                          page: pendingProfessionalMark.page,
                          section: pendingProfessionalMark.section,
                          reason,
                          markNumber: nextProfessionalMarkNumber,
                          reasonIndex: originalIndex,
                          ...(pendingProfessionalMark.cell ? { cell: pendingProfessionalMark.cell } : {})
                        };
                        if (pendingProfessionalMark.type === 'section') {
                          setProfessionalMarks([...professionalMarks, newMark]);
                        } else {
                          setAdditionalProfessionalMarks([...additionalProfessionalMarks, newMark]);
                        }
                        setNextProfessionalMarkNumber(nextProfessionalMarkNumber + 1);
                        setPendingProfessionalMark(null);
                      }}
                      style={{
                        padding: '10px 14px',
                        background: '#fef9f0',
                        border: `2px solid ${pendingProfessionalMark.type === 'section' ? '#f39c12' : '#7b2d8b'}`,
                        borderRadius: '8px',
                        cursor: 'pointer',
                        textAlign: 'left',
                        fontSize: '13px',
                        color: '#2c3e50',
                        fontFamily: 'inherit',
                        lineHeight: '1.4',
                        transition: 'background 0.15s'
                      }}
                      onMouseEnter={e => e.target.style.background = pendingProfessionalMark.type === 'section' ? '#fef0d0' : '#f5e8f9'}
                      onMouseLeave={e => e.target.style.background = '#fef9f0'}
                    >
                      {reason}
                    </button>
                  ))}
                </div>
              );
            })()}

            <button
              onClick={() => setPendingProfessionalMark(null)}
              style={{
                width: '100%', padding: '8px', background: '#ecf0f1',
                color: '#7f8c8d', border: 'none', borderRadius: '6px',
                cursor: 'pointer', fontWeight: '600', fontSize: '13px'
              }}
            >
              Cancel
            </button>
          </div>
        </>
      )}

      {/* Tool Menu */}
      {toolMenu && (
        <>
          <div
            onClick={() => setToolMenu(null)}
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 999
            }}
          />
          <div style={{
            position: 'fixed',
            left: toolMenu.x,
            top: toolMenu.y,
            background: 'white',
            border: '2px solid #2c3e50',
            borderRadius: '8px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
            zIndex: 1000,
            minWidth: '220px',
            maxWidth: '260px',
            maxHeight: `${Math.min(380, window.innerHeight - toolMenu.y - 20)}px`,
            overflowY: 'auto'
          }}>
            {[
              { id: 'select', icon: 'Arrow', name: 'Select / Arrow' },
              { id: 'marker', icon: 'Target', name: 'Marker (Technical)' },
              { id: 'highlighter', icon: 'Pen', name: 'Highlighter' },
              { id: 'numbered-feedback', icon: '#', name: 'Numbered Feedback' },
              { id: 'comment', icon: 'Comment', name: 'Comment' },
              { id: 'divider', icon: null, name: null },
              { id: 'professional-mark', icon: 'P', name: 'Professional Mark' },
              { id: 'additional-professional-mark', icon: 'AP', name: 'Additional Prof Mark' },
              { id: 'divider2', icon: null, name: null },
              { id: 'stamp-ofr', icon: 'OFR', name: 'OFR Stamp' },
              { id: 'stamp-pag', icon: 'PAG', name: 'PAG Stamp' },
              { id: 'stamp-max', icon: 'MAX', name: 'MAX Stamp' }
            ].filter(tool => !(showingSpreadsheet && tool.id === 'comment')).map((tool) => {
              if (tool.id.startsWith('divider')) {
                return <div key={tool.id} style={{ height: '1px', background: '#e2e8f0', margin: '4px 0' }} />;
              }
              return (
                <div
                  key={tool.id}
                  onClick={() => {
                    setActiveTool(tool.id);
                    // Don't close the menu for highlighter - let it stay open
                    if (tool.id !== 'highlighter') {
                      setToolMenu(null);
                    }
                  }}
                  style={{
                    padding: '12px 16px',
                    cursor: 'pointer',
                    background: activeTool === tool.id ? '#f8f9fa' : 'white',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px'
                  }}
                >
                  <span>{tool.name}</span>
                </div>
              );
            })}

            {/* Highlight Colors */}
            {(activeTool === 'highlighter' || toolMenu) && (
              <div style={{ padding: '12px 16px', borderTop: '1px solid #ecf0f1' }}>
                <div style={{ fontSize: '11px', color: '#7f8c8d', marginBottom: '8px', fontWeight: '600' }}>
                  Highlight Color:
                </div>
                <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
                  {HIGHLIGHT_COLORS.map(color => (
                    <button
                      key={color.value}
                      onClick={(e) => {
                        e.stopPropagation();
                        setHighlightColor(color.value);
                      }}
                      style={{
                        width: '32px',
                        height: '32px',
                        background: color.value,
                        border: highlightColor === color.value ? '3px solid #2c3e50' : '2px solid #bdc3c7',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        boxShadow: highlightColor === color.value ? '0 2px 6px rgba(0,0,0,0.3)' : 'none'
                      }}
                      title={color.name}
                    />
                  ))}
                </div>
                <div style={{ fontSize: '10px', color: '#95a5a6', marginTop: '8px', textAlign: 'center' }}>
                  drag over text to highlight it, or click for a box
                </div>
              </div>
            )}
          </div>
        </>
      )}

      {/* Final Comment Suggestions Modal */}
      {showFinalCommentSuggestions && (commentBank?.final || myCommentBank?.final) && (() => {
        const raw = commentBank?.final;
        const isNew = raw && !Array.isArray(raw);
        const finalComments = isNew ? (raw.comments || []) : (raw || []);
        const finalHeaders  = isNew ? (raw.headers  || []) : [];
        const mine = extractSectionData(myCommentBank, 'final');
        if (finalComments.length === 0 && mine.comments.length === 0) return null;
        const grouped = {};
        finalHeaders.forEach(h => { grouped[h] = []; });
        grouped['__none__'] = [];
        finalComments.forEach(c => {
          const key = (c.header && finalHeaders.includes(c.header)) ? c.header : '__none__';
          grouped[key].push(c);
        });
        const insertComment = (text) => {
          setFinalComment(prev => prev ? prev + ' ' + text : text);
        };
        return (
          <>
          {/* Backdrop */}
          <div
            onClick={() => setShowFinalCommentSuggestions(false)}
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(0,0,0,0.5)',
              zIndex: 2999,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          />
          
          {/* Modal */}
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              position: 'fixed',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              background: 'white',
              border: '3px solid #667eea',
              borderRadius: '16px',
              padding: '24px',
              boxShadow: '0 20px 60px rgba(0,0,0,0.4)',
              zIndex: 3000,
              width: '90%',
              maxWidth: '800px',
              maxHeight: '80vh',
              display: 'flex',
              flexDirection: 'column'
            }}
          >
            {/* Header */}
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '20px',
              paddingBottom: '16px',
              borderBottom: '2px solid #e2e8f0'
            }}>
              <div>
                <h3 style={{ margin: '0 0 8px 0', color: '#667eea', fontSize: '24px' }}>
                  Final Comment Suggestions
                </h3>
                <div style={{ fontSize: '14px', color: '#7f8c8d' }}>
                  Click comments to add them to your final feedback
                </div>
              </div>
              <button
                onClick={() => setShowFinalCommentSuggestions(false)}
                style={{
                  background: '#e74c3c',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  padding: '10px 20px',
                  fontSize: '16px',
                  fontWeight: '600',
                  cursor: 'pointer'
                }}
              >
                Close
              </button>
            </div>

            {/* Current Final Comment Preview */}
            {finalComment && (
              <div style={{
                marginBottom: '20px',
                padding: '16px',
                background: '#f0fdf4',
                border: '2px solid #1b998b',
                borderRadius: '12px'
              }}>
                <div style={{
                  fontSize: '12px',
                  fontWeight: '600',
                  color: '#1b998b',
                  marginBottom: '8px'
                }}>
                  Current Final Comment:
                </div>
                <div style={{
                  fontSize: '14px',
                  color: '#2c3e50',
                  lineHeight: '1.6',
                  whiteSpace: 'pre-wrap'
                }}>
                  {finalComment}
                </div>
              </div>
            )}

            {/* Scroll region: My Comments + Central Bank */}
            <div style={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '20px', padding: '4px' }}>

              {/* My Comments */}
              {mine.comments.length > 0 && (
                <div>
                  <div style={{ fontSize: '13px', fontWeight: '700', color: '#2c3e50', marginBottom: '8px' }}>
                    My Comments ({mine.comments.length})
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))', gap: '12px' }}>
                    {mine.comments.map((comment) => (
                      <div key={comment.id} onClick={() => insertComment(comment.text)}
                        style={{ padding: '16px', borderRadius: '10px', border: '2px solid #e2e8f0', background: '#f8f9fa', cursor: 'pointer', fontSize: '14px', lineHeight: '1.6', transition: 'all 0.15s' }}
                        onMouseEnter={e => { e.currentTarget.style.background = '#667eea'; e.currentTarget.style.color = 'white'; e.currentTarget.style.borderColor = '#667eea'; }}
                        onMouseLeave={e => { e.currentTarget.style.background = '#f8f9fa'; e.currentTarget.style.color = '#2c3e50'; e.currentTarget.style.borderColor = '#e2e8f0'; }}>
                        {comment.text}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Central Bank */}
              {finalComments.length > 0 && (
                <div>
                  <div style={{ fontSize: '13px', fontWeight: '700', color: '#667eea', marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <MessageCircle size={14} />
                    Central Bank ({finalComments.length})
                  </div>
                  {finalHeaders.length === 0 ? (
                    // Flat grid — original behaviour
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))', gap: '12px' }}>
                      {finalComments.map((comment) => (
                        <div key={comment.id} onClick={() => insertComment(comment.text)}
                          style={{ padding: '16px', borderRadius: '10px', border: '2px solid #e2e8f0', background: '#f8f9fa', cursor: 'pointer', fontSize: '14px', lineHeight: '1.6', transition: 'all 0.15s' }}
                          onMouseEnter={e => { e.currentTarget.style.background = '#667eea'; e.currentTarget.style.color = 'white'; e.currentTarget.style.borderColor = '#667eea'; }}
                          onMouseLeave={e => { e.currentTarget.style.background = '#f8f9fa'; e.currentTarget.style.color = '#2c3e50'; e.currentTarget.style.borderColor = '#e2e8f0'; }}>
                          {comment.text}
                        </div>
                      ))}
                    </div>
                  ) : (
                    // Grouped by header
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      {finalHeaders.map(header => (
                        <div key={header}>
                          <div style={{ fontWeight: '700', color: 'white', background: '#667eea', padding: '8px 14px', borderRadius: '8px 8px 0 0', fontSize: '14px' }}>{header}</div>
                          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '8px', padding: '10px', background: '#f0f2ff', borderRadius: '0 0 8px 8px', border: '2px solid #667eea', borderTop: 'none' }}>
                            {(grouped[header] || []).map(comment => (
                              <div key={comment.id} onClick={() => insertComment(comment.text)}
                                style={{ padding: '12px', borderRadius: '8px', border: '2px solid #e2e8f0', background: 'white', cursor: 'pointer', fontSize: '14px', lineHeight: '1.6', transition: 'all 0.15s' }}
                                onMouseEnter={e => { e.currentTarget.style.background = '#667eea'; e.currentTarget.style.color = 'white'; e.currentTarget.style.borderColor = '#667eea'; }}
                                onMouseLeave={e => { e.currentTarget.style.background = 'white'; e.currentTarget.style.color = '#2c3e50'; e.currentTarget.style.borderColor = '#e2e8f0'; }}>
                                {comment.text}
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                      {grouped['__none__'].length > 0 && (
                        <div>
                          <div style={{ fontWeight: '700', color: 'white', background: '#95a5a6', padding: '8px 14px', borderRadius: '8px 8px 0 0', fontSize: '14px' }}>Other</div>
                          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '8px', padding: '10px', background: '#f8f9fa', borderRadius: '0 0 8px 8px', border: '2px solid #95a5a6', borderTop: 'none' }}>
                            {grouped['__none__'].map(comment => (
                              <div key={comment.id} onClick={() => insertComment(comment.text)}
                                style={{ padding: '12px', borderRadius: '8px', border: '2px solid #e2e8f0', background: 'white', cursor: 'pointer', fontSize: '14px', lineHeight: '1.6', transition: 'all 0.15s' }}
                                onMouseEnter={e => { e.currentTarget.style.background = '#667eea'; e.currentTarget.style.color = 'white'; e.currentTarget.style.borderColor = '#667eea'; }}
                                onMouseLeave={e => { e.currentTarget.style.background = 'white'; e.currentTarget.style.color = '#2c3e50'; e.currentTarget.style.borderColor = '#e2e8f0'; }}>
                                {comment.text}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Footer */}
            <div style={{
              marginTop: '20px',
              paddingTop: '16px',
              borderTop: '2px solid #e2e8f0',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <div style={{ fontSize: '13px', color: '#7f8c8d', fontStyle: 'italic' }}>
                Tip: Click multiple comments to combine them
              </div>
              <div style={{ display: 'flex', gap: '12px' }}>
                <button
                  onClick={() => setFinalComment('')}
                  style={{
                    padding: '10px 20px',
                    background: '#e74c3c',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontWeight: '600'
                  }}
                >
                  Clear All
                </button>
                <button
                  onClick={() => setShowFinalCommentSuggestions(false)}
                  style={{
                    padding: '10px 20px',
                    background: '#1b998b',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontWeight: '600'
                  }}
                >
                  Done
                </button>
              </div>
            </div>
          </div>
          </>
        );
      })()}

      {/* Final Comment Modal (per-question mode) */}
      {showFinalCommentModal && questionStructure.length > 0 && (() => {
        const activeQuestion = questionStructure.find(q => q.id === activeQuestionTab) || questionStructure[0];
        const qBankComments = (commentBank?.finalQuestions || {})[activeQuestion?.id] || [];
        const myQComments = (myCommentBank?.finalQuestions || {})[activeQuestion?.id] || [];
        const combined = buildCombinedFinalComment(questionComments, questionStructure);
        return (
          <>
            <div
              onClick={() => setShowFinalCommentModal(false)}
              style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', zIndex: 2999 }}
            />
            <div
              onClick={(e) => e.stopPropagation()}
              style={{
                position: 'fixed',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                background: 'white',
                border: '3px solid #667eea',
                borderRadius: '16px',
                padding: '24px',
                boxShadow: '0 20px 60px rgba(0,0,0,0.4)',
                zIndex: 3000,
                width: '90%',
                maxWidth: '820px',
                maxHeight: '85vh',
                display: 'flex',
                flexDirection: 'column'
              }}
            >
              {/* Header */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', paddingBottom: '16px', borderBottom: '2px solid #e2e8f0' }}>
                <h3 style={{ margin: 0, color: '#667eea', fontSize: '22px' }}>Final Comment</h3>
                <button
                  onClick={() => setShowFinalCommentModal(false)}
                  style={{ background: '#e74c3c', color: 'white', border: 'none', borderRadius: '8px', padding: '10px 20px', fontWeight: '600', fontSize: '15px', cursor: 'pointer' }}
                >
                  Done
                </button>
              </div>

              {/* Question tabs */}
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '20px' }}>
                {questionStructure.map((q, idx) => {
                  const hasComment = (questionComments[q.id] || '').trim();
                  const isActive = activeQuestionTab === q.id;
                  return (
                    <button
                      key={q.id}
                      onClick={() => setActiveQuestionTab(q.id)}
                      style={{
                        padding: '8px 16px',
                        background: isActive ? '#667eea' : '#f8f9fa',
                        color: isActive ? 'white' : '#2c3e50',
                        border: `2px solid ${isActive ? '#667eea' : '#e2e8f0'}`,
                        borderRadius: '8px',
                        cursor: 'pointer',
                        fontWeight: '600',
                        fontSize: '13px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px'
                      }}
                    >
                      {q.label || `Question ${idx + 1}`}
                      {hasComment && (
                        <span style={{
                          width: '8px',
                          height: '8px',
                          background: isActive ? 'white' : '#1b998b',
                          borderRadius: '50%',
                          display: 'inline-block',
                          flexShrink: 0
                        }} />
                      )}
                    </button>
                  );
                })}
              </div>

              {/* Scrollable content */}
              <div style={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '16px' }}>
                {activeQuestion && (
                  <>
                    {/* Textarea */}
                    <div>
                      <label style={{ display: 'block', fontWeight: '600', fontSize: '14px', color: '#2c3e50', marginBottom: '8px' }}>
                        {activeQuestion.label}
                      </label>
                      <textarea
                        value={questionComments[activeQuestion.id] || ''}
                        onChange={(e) => {
                          const updated = { ...questionComments, [activeQuestion.id]: e.target.value };
                          setQuestionComments(updated);
                          setFinalComment(buildCombinedFinalComment(updated, questionStructure));
                        }}
                        placeholder={`Comments for ${activeQuestion.label}...`}
                        rows={4}
                        style={{
                          width: '100%',
                          padding: '10px',
                          border: '2px solid #e2e8f0',
                          borderRadius: '8px',
                          fontSize: '14px',
                          fontFamily: 'inherit',
                          resize: 'vertical'
                        }}
                        autoFocus
                      />
                      {(questionComments[activeQuestion.id] || '').trim() && !user?.suspended && (
                        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                          <button
                            onClick={async () => {
                              try {
                                await suggestFinalQuestionComment(
                                  submission.homeworkType,
                                  activeQuestion.id,
                                  activeQuestion.label,
                                  questionComments[activeQuestion.id],
                                  user?.uid || 'unknown',
                                  user?.name || 'Unknown Marker'
                                );
                                try {
                                  const { sendHeadMarkerNotification, NOTIFICATION_TYPES } = await import('./firebase/headMarkerNotifications');
                                  sendHeadMarkerNotification(NOTIFICATION_TYPES.COMMENT_SUGGESTION, {
                                    homeworkType: homeworkNames[submission.homeworkType] || submission.homeworkType,
                                    section: activeQuestion.label,
                                    commentText: questionComments[activeQuestion.id],
                                    markerName: user?.name || 'Unknown Marker'
                                  });
                                } catch (e) { console.error('Notification error:', e); }
                                alert('Comment suggested to head marker for approval!');
                              } catch (error) {
                                alert('Failed to suggest comment');
                              }
                            }}
                            style={{
                              marginTop: '8px',
                              padding: '8px 16px',
                              background: '#f39c12',
                              color: 'white',
                              border: 'none',
                              borderRadius: '6px',
                              cursor: 'pointer',
                              fontWeight: '600',
                              fontSize: '13px',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '6px'
                            }}
                          >
                            <Lightbulb size={14} /> Suggest this comment
                          </button>

                          <button
                            onClick={async () => {
                              try {
                                const saved = await addMyFinalQuestionComment(
                                  user.uid,
                                  submission.homeworkType,
                                  activeQuestion.id,
                                  questionComments[activeQuestion.id]
                                );
                                setMyCommentBank(prev => {
                                  const base = prev || {};
                                  const existing = base.finalQuestions || {};
                                  return {
                                    ...base,
                                    finalQuestions: {
                                      ...existing,
                                      [activeQuestion.id]: [...(existing[activeQuestion.id] || []), saved]
                                    }
                                  };
                                });
                              } catch (error) {
                                console.error('Save my comment error:', error);
                                alert('Failed to save comment');
                              }
                            }}
                            style={{
                              marginTop: '8px',
                              padding: '8px 16px',
                              background: '#667eea',
                              color: 'white',
                              border: 'none',
                              borderRadius: '6px',
                              cursor: 'pointer',
                              fontWeight: '600',
                              fontSize: '13px'
                            }}
                            title="Save to your own personal comment list - available instantly, only to you"
                          >
                            + My Comments
                          </button>
                        </div>
                      )}
                    </div>

                    {/* My stored comments */}
                    {myQComments.length > 0 && (
                      <div>
                        <div style={{ fontWeight: '600', fontSize: '13px', color: '#2c3e50', marginBottom: '10px' }}>
                          My comments — click to insert:
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '8px' }}>
                          {myQComments.map(c => (
                            <div
                              key={c.id}
                              onClick={() => {
                                const current = questionComments[activeQuestion.id] || '';
                                const updated = { ...questionComments, [activeQuestion.id]: current ? current + ' ' + c.text : c.text };
                                setQuestionComments(updated);
                                setFinalComment(buildCombinedFinalComment(updated, questionStructure));
                              }}
                              style={{ padding: '12px', border: '2px solid #e2e8f0', borderRadius: '8px', background: '#f8f9fa', cursor: 'pointer', fontSize: '13px', lineHeight: '1.5', transition: 'all 0.15s' }}
                              onMouseEnter={e => { e.currentTarget.style.background = '#667eea'; e.currentTarget.style.color = 'white'; e.currentTarget.style.borderColor = '#667eea'; }}
                              onMouseLeave={e => { e.currentTarget.style.background = '#f8f9fa'; e.currentTarget.style.color = '#2c3e50'; e.currentTarget.style.borderColor = '#e2e8f0'; }}
                            >
                              {c.text}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Stored comments */}
                    {qBankComments.length > 0 && (
                      <div>
                        <div style={{ fontWeight: '600', fontSize: '13px', color: '#667eea', marginBottom: '10px' }}>
                          Stored comments — click to insert:
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '8px' }}>
                          {qBankComments.map(c => (
                            <div
                              key={c.id}
                              onClick={() => {
                                const current = questionComments[activeQuestion.id] || '';
                                const updated = { ...questionComments, [activeQuestion.id]: current ? current + ' ' + c.text : c.text };
                                setQuestionComments(updated);
                                setFinalComment(buildCombinedFinalComment(updated, questionStructure));
                              }}
                              style={{ padding: '12px', border: '2px solid #e2e8f0', borderRadius: '8px', background: '#f8f9fa', cursor: 'pointer', fontSize: '13px', lineHeight: '1.5', transition: 'all 0.15s' }}
                              onMouseEnter={e => { e.currentTarget.style.background = '#667eea'; e.currentTarget.style.color = 'white'; e.currentTarget.style.borderColor = '#667eea'; }}
                              onMouseLeave={e => { e.currentTarget.style.background = '#f8f9fa'; e.currentTarget.style.color = '#2c3e50'; e.currentTarget.style.borderColor = '#e2e8f0'; }}
                            >
                              {c.text}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </>
                )}

                {/* Combined preview */}
                {combined.trim() && (
                  <div style={{ padding: '14px 16px', background: '#f0fdf4', border: '2px solid #1b998b', borderRadius: '8px', fontSize: '13px', lineHeight: '1.6', whiteSpace: 'pre-wrap', color: '#2c3e50' }}>
                    <div style={{ fontWeight: '600', color: '#1b998b', marginBottom: '8px', fontSize: '12px' }}>Combined output:</div>
                    {combined}
                  </div>
                )}
              </div>
            </div>
          </>
        );
      })()}

      {showFlagModal && (
        <FlagForReviewModal
          submissionId={submission?.id}
          onClose={() => { if (!flaggingSubmission) { setShowFlagModal(false); setFlagComment(''); } }}
          flagComment={flagComment}
          setFlagComment={setFlagComment}
          onFlag={handleFlagForReview}
          headMarkerName={headMarkerName}
        />
      )}

    </div>
  );
}

export default FirebaseMarker;